/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    Search,
    MapPin,
    Calendar,
    ChevronRight,
    Trophy,
    Compass,
    Heart,
    Star,
    Sparkles,
    Sliders,
    X,
    Info,
    CheckCircle2,
    Play,
    Activity,
    Hourglass,
    Share2,
    BarChart3,
    Copy,
    Check,
    RefreshCw,
    Video,
    Tv,
    ExternalLink,
    Clock,
    User
} from 'lucide-react';
import { TEAMS } from '../constants';
import { collection, query, onSnapshot, limit, setDoc, deleteDoc, doc, where, getDocs, getDoc } from 'firebase/firestore';
import { db, isFirestoreQuotaError, notifyQuotaExceeded } from '../firebase';
import { Avatar } from './Avatar';
import { LanguageCode, TRANSLATIONS } from '../translations';
import { PredictionStatsReport } from './PredictionStatsReport';

import { SHEDULE_DATA, ScheduleMatch, parseMatchDateStringToDate } from '../matchesData';

export interface VideoHighlight {
    id: string;
    matchId: string;
    title: string;
    stadium: string;
    date: string;
    score: string;
    duration: string;
    narrator: string;
    videoUrl: string;
    youtubeQuery: string;
    thumbnailGradient: string;
    homeTeam: string;
    awayTeam: string;
    summary: string;
    goals: { player: string; min: number; team: string; }[];
}

export const VIDEO_HIGHLIGHTS: VideoHighlight[] = [
    {
        id: "h_1",
        matchId: "g_1",
        title: "Mexico vs South Africa - Matchday 1 Highlights",
        stadium: "Estadio Azteca, Mexico City",
        date: "Thursday, 11 June 2026",
        score: "2 - 1",
        duration: "4:15",
        narrator: "DailyGoal Matchday Broadcast",
        videoUrl: "https://dailygoal.tv/world-cup-2026-highlights",
        youtubeQuery: "Mexico vs South Africa FIFA World Cup 2026 highlights",
        thumbnailGradient: "from-emerald-950 via-teal-900 to-slate-900",
        homeTeam: "Mexico",
        awayTeam: "South Africa",
        goals: [
            { player: "Santiago Giménez", min: 14, team: "Mexico" },
            { player: "Lyle Foster", min: 58, team: "South Africa" },
            { player: "Edson Álvarez", min: 82, team: "Mexico" }
        ],
        summary: "An electrifying opening match at the legendary Estadio Azteca! Edson Álvarez's late header secured a crucial 3 points for the host nation amidst an unbelievable atmosphere of 90,05 fans."
    },
    {
        id: "h_2",
        matchId: "g_2",
        title: "Korea Republic vs Czechia - Matchday 1 Highlights",
        stadium: "Estadio Akron, Guadalajara",
        date: "Friday, 12 June 2026",
        score: "1 - 1",
        duration: "3:40",
        narrator: "DailyGoal English Commentary",
        videoUrl: "https://dailygoal.tv/world-cup-2026-highlights",
        youtubeQuery: "Korea Republic vs Czechia FIFA World Cup 2026 highlights",
        thumbnailGradient: "from-blue-955 via-indigo-950 to-slate-900",
        homeTeam: "Korea Republic",
        awayTeam: "Czechia",
        goals: [
            { player: "Heung-min Son", min: 38, team: "Korea Republic" },
            { player: "Patrik Schick", min: 61, team: "Czechia" }
        ],
        summary: "Son Heung-min opened the account with a signature brilliant curler, but Czechia fought back courageously in the second half to secure a well-earned point in Group A."
    },
    {
        id: "h_7",
        matchId: "g_7",
        title: "Canada vs Bosnia and Herzegovina - Matchday 1 Highlights",
        stadium: "BMO Field, Toronto",
        date: "Friday, 12 June 2026",
        score: "1 - 1",
        duration: "5:02",
        narrator: "DailyGoal Arena Feed",
        videoUrl: "https://dailygoal.tv/world-cup-2026-highlights",
        youtubeQuery: "Canada vs Bosnia and Herzegovina FIFA World Cup 2026 highlights",
        thumbnailGradient: "from-red-950 via-[#cf2027]/20 to-slate-900",
        homeTeam: "Canada",
        awayTeam: "Bosnia and Herzegovina",
        goals: [
            { player: "Jonathan David", min: 29, team: "Canada" },
            { player: "Edin Džeko", min: 74, team: "Bosnia and Herzegovina" }
        ],
        summary: "Canada launched their home campaign with a high-intensity display at BMO Field. Jonathan David opened the score early on, but Bosnia and Herzegovina fought back in the second half, with the legendary Edin Džeko heading home a stunning equalizer."
    },
    {
        id: "h_8",
        matchId: "g_8",
        title: "Qatar vs Switzerland - Matchday 1 Highlights",
        stadium: "Gillette Stadium, Foxborough",
        date: "Saturday, 13 June 2026",
        score: "0 - 3",
        duration: "3:12",
        narrator: "DailyGoal Global Coverage",
        videoUrl: "https://dailygoal.tv/world-cup-2026-highlights",
        youtubeQuery: "Qatar vs Switzerland FIFA World Cup 2026 highlights",
        thumbnailGradient: "from-amber-950 via-[#d32f2f]/30 to-slate-900",
        homeTeam: "Qatar",
        awayTeam: "Switzerland",
        goals: [
            { player: "Breel Embolo", min: 11, team: "Switzerland" },
            { player: "Granit Xhaka", min: 43, team: "Switzerland" },
            { player: "Zeki Amdouni", min: 88, team: "Switzerland" }
        ],
        summary: "A flawless, masterclass performance by the Swiss squad. Embolo got them on track early, before Granit Xhaka commanded the midfield and added a gorgeous goal."
    },
    {
        id: "h_19",
        matchId: "g_19",
        title: "USA vs Paraguay - Matchday 1 Highlights",
        stadium: "Gillette Stadium, Foxborough",
        date: "Saturday, 13 June 2026",
        score: "3 - 1",
        duration: "6:10",
        narrator: "DailyGoal US Broadcast Crew",
        videoUrl: "https://dailygoal.tv/world-cup-2026-highlights",
        youtubeQuery: "USA vs Paraguay FIFA World Cup 2026 highlights",
        thumbnailGradient: "from-blue-950 via-indigo-900 to-slate-900",
        homeTeam: "USA",
        awayTeam: "Paraguay",
        goals: [
            { player: "Christian Pulisic", min: 21, team: "USA" },
            { player: "Miguel Almirón", min: 45, team: "Paraguay" },
            { player: "Folarin Balogun", min: 67, team: "USA" },
            { player: "Gio Reyna", min: 89, team: "USA" }
        ],
        summary: "The Stars and Stripes ignited their campaign! Captain Christian Pulisic inspired the victory, with Balogun and Reyna adding sensational second-half goals to withstand Paraguay's fierce press."
    },
    {
        id: "h_20",
        matchId: "g_20",
        title: "Australia vs Türkiye - Matchday 1 Highlights",
        stadium: "BC Place, Vancouver",
        date: "Sunday, 14 June 2026",
        score: "2 - 2",
        duration: "4:50",
        narrator: "DailyGoal Arena Feed",
        videoUrl: "https://dailygoal.tv/world-cup-2026-highlights",
        youtubeQuery: "Australia vs Türkiye FIFA World Cup 2026 highlights",
        thumbnailGradient: "from-green-950 via-yellow-900/30 to-slate-900",
        homeTeam: "Australia",
        awayTeam: "Türkiye",
        goals: [
            { player: "Arda Güler", min: 8, team: "Türkiye" },
            { player: "Harry Souttar", min: 34, team: "Australia" },
            { player: "Mitchell Duke", min: 72, team: "Australia" },
            { player: "Kerem Aktürkoğlu", min: 90, team: "Türkiye" }
        ],
        summary: "A dramatic 4-goal epic at BC Place! Wonderkid Arda Güler scored a jaw-dropping free-kick before Kerem Aktürkoğlu rescued Türkiye in the absolute final kick of the match."
    },
    {
        id: "h_13",
        matchId: "g_13",
        title: "Brazil vs Morocco - Matchday 1 Highlights",
        stadium: "Gillette Stadium, Foxborough",
        date: "Sunday, 14 June 2026",
        score: "4 - 2",
        duration: "5:45",
        narrator: "DailyGoal Portuguese Commentary",
        videoUrl: "https://dailygoal.tv/world-cup-2026-highlights",
        youtubeQuery: "Brazil vs Morocco FIFA World Cup 2026 highlights",
        thumbnailGradient: "from-yellow-950 via-green-900 to-slate-900",
        homeTeam: "Brazil",
        awayTeam: "Morocco",
        goals: [
            { player: "Vinícius Júnior", min: 14, team: "Brazil" },
            { player: "Hakim Ziyech", min: 28, team: "Morocco" },
            { player: "Rodrygo", min: 52, team: "Brazil" },
            { player: "Youssef En-Nesyri", min: 71, team: "Morocco" },
            { player: "Neymar Jr", min: 79, team: "Brazil" },
            { player: "Endrick", min: 88, team: "Brazil" }
        ],
        summary: "Pure samba magic on display! Brazil fought off an aggressive Moroccan counter-attack, with veteran Neymar Jr and teenager Endrick wrapping up an iconic 6-goal thriller."
    }
];


// Helper to look up team flag / render img element from flagcdn
export function TeamFlag({ name, className = "w-6 h-4" }: { name: string; className?: string }) {
    // Normalize names to match TEAMS constant keys
    const normalized = useMemo(() => {
        const lower = name.trim().toLowerCase();
        if (lower === "usa") return "united states";
        if (lower === "korea republic" || lower === "korea rep") return "south korea";
        if (lower === "ir iran") return "iran";
        if (lower === "congo dr" || lower === "congo") return "dr congo";
        if (lower === "ivory coast" || lower === "cote d'ivoire") return "côte d'ivoire";
        return lower;
    }, [name]);

    // Find team in TEAMS database
    const team = useMemo(() => {
        return TEAMS.find(t => t.name.toLowerCase() === normalized);
    }, [normalized]);

    if (team && team.flag) {
        return (
            <img
                src={`https://flagcdn.com/w40/${team.flag}.png`}
                alt={team.name}
                className={`${className} object-cover rounded shadow-sm border border-slate-700/30 flex-shrink-0`}
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
            />
        );
    }

    // Fallbacks for structural playoff labels or unmapped teams
    const isPlaceholder = name.toLowerCase().includes("winner") ||
        name.toLowerCase().includes("runner-up") ||
        name.toLowerCase().includes("group") ||
        name.toLowerCase().includes("third place");

    if (isPlaceholder) {
        return (
            <span className="inline-flex items-center justify-center bg-slate-800 text-slate-400 text-[10px] w-6 h-4 rounded shadow-sm border border-slate-700/50 font-mono select-none flex-shrink-0">
        🏆
      </span>
        );
    }

    return (
        <span className="inline-flex items-center justify-center bg-slate-800 text-slate-400 text-[10px] w-6 h-4 rounded shadow-sm border border-slate-700/50 font-mono select-none flex-shrink-0">
      🏳️
    </span>
    );
}

// Deterministic mock Match Statistics generator based on game parameters and seed
export function getMatchStats(matchId: string, actualScoreA: number, actualScoreB: number, actualCorners: number) {
    let seed = 0;
    for (let i = 0; i < matchId.length; i++) {
        seed += matchId.charCodeAt(i);
    }
    const pseudoRandom = (offset: number) => {
        const x = Math.sin(seed + offset) * 10000;
        return x - Math.floor(x);
    };

    // Possession A: 40% - 60%
    const posA = Math.floor(pseudoRandom(1) * 20) + 40;
    const posB = 100 - posA;

    // Shots on target (must be at least goals scored)
    const sTargetA = actualScoreA + Math.floor(pseudoRandom(2) * 4) + 1;
    const sTargetB = actualScoreB + Math.floor(pseudoRandom(3) * 4) + 1;

    // Total Shots
    const sTotalA = sTargetA + Math.floor(pseudoRandom(4) * 7) + 2;
    const sTotalB = sTargetB + Math.floor(pseudoRandom(5) * 7) + 2;

    // Passing Accuracy
    const passA = Math.floor(pseudoRandom(6) * 12) + 78;
    const passB = Math.floor(pseudoRandom(7) * 12) + 76;

    // Fouls
    const foulsA = Math.floor(pseudoRandom(8) * 6) + 7;
    const foulsB = Math.floor(pseudoRandom(9) * 6) + 8;

    // Yellow Cards
    const yellowA = Math.floor(pseudoRandom(10) * 3);
    const yellowB = Math.floor(pseudoRandom(11) * 3);

    // Red Cards
    const redA = pseudoRandom(12) < 0.05 ? 1 : 0;
    const redB = pseudoRandom(13) < 0.05 ? 1 : 0;

    // Corners distribution from overall corners
    const cornersA = Math.min(actualCorners, Math.floor(actualCorners * (posA / 100) + pseudoRandom(14) * 1.5));
    const cornersB = Math.max(0, actualCorners - cornersA);

    return {
        possession: [posA, posB],
        shotsTarget: [sTargetA, sTargetB],
        shotsTotal: [sTotalA, sTotalB],
        passing: [passA, passB],
        fouls: [foulsA, foulsB],
        yellow: [yellowA, yellowB],
        red: [redA, redB],
        corners: [cornersA, cornersB]
    };
}

// Safe date parsing helper to handle "Monday, 11 May 2026" cross-browser parsing correctly
export function safeParseMatchDate(dateStr: string): Date {
    if (!dateStr) return new Date();

    const parts = dateStr.split(',');
    const datePart = parts.length > 1 ? parts[1].trim() : parts[0].trim();

    const subParts = datePart.split(/\s+/);
    if (subParts.length === 3) {
        const day = parseInt(subParts[0], 10);
        const monthName = subParts[1].toLowerCase();
        const year = parseInt(subParts[2], 10);

        const months: Record<string, number> = {
            january: 0, jan: 0,
            february: 1, feb: 1,
            march: 2, mar: 2,
            april: 3, apr: 3,
            may: 4,
            june: 5, jun: 5,
            july: 6, jul: 6,
            august: 7, aug: 7,
            september: 8, sep: 8,
            october: 9, oct: 9,
            november: 10, nov: 10,
            december: 11, dec: 11
        };

        const month = months[monthName] ?? 0;
        return new Date(year, month, day, 12, 0, 0);
    }

    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
        return parsed;
    }
    return new Date();
}



/**
 * Calculates remaining minutes/seconds style countdown before kickoff.
 * If the match is starting in the next 30 minutes but hasn't started yet,
 * returns a descriptive countdown string with blinking indication.
 */
export const getCountdownInfo = (match: ScheduleMatch, now: Date): { active: boolean; label: string; mins: number } => {
    try {
        const matchDate = parseMatchDateStringToDate(match.date);
        if (isNaN(matchDate.getTime())) return { active: false, label: '', mins: 0 };

        const timeDiff = matchDate.getTime() - now.getTime();

        // If it's already started or past starting time:
        if (timeDiff <= 0) {
            return { active: false, label: '', mins: 0 };
        }

        // 30 minutes in milliseconds = 30 * 60 * 1000 = 1,800,000 ms
        if (timeDiff <= 30 * 60 * 1000) {
            const minsWord = Math.floor(timeDiff / (60 * 1000));
            const secsWord = Math.floor((timeDiff % (60 * 1000)) / 1000).toString().padStart(2, '0');
            return {
                active: true,
                label: `${minsWord}:${secsWord}`,
                mins: minsWord
            };
        }
    } catch {}
    return { active: false, label: '', mins: 0 };
};

// Deterministic match outcome generator based on matchId
export function getDeterministicMatchResult(matchId: string) {
    let seed = 0;
    for (let i = 0; i < matchId.length; i++) {
        seed += matchId.charCodeAt(i);
    }
    const pseudoRandom = (offset: number) => {
        const x = Math.sin(seed + offset) * 10000;
        return x - Math.floor(x);
    };

    // Score A: 0 - 3
    const actualScoreA = Math.floor(pseudoRandom(1) * 4);
    // Score B: 0 - 3
    const actualScoreB = Math.floor(pseudoRandom(2) * 4);
    // Corners: 4 - 11
    const actualCorners = Math.floor(pseudoRandom(3) * 8) + 4;
    const actualOverUnder = (actualScoreA + actualScoreB) > 2.5 ? 'over' : 'under';

    return {
        actualScoreA,
        actualScoreB,
        actualCorners,
        actualOverUnder
    };
}

export function maskEmail(email: string) {
    if (!email || email === 'Guest') return "Anonymous";
    const [local, domain] = email.split('@');
    if (!domain) return email;
    if (local.length <= 3) return `${local}...@${domain}`;
    return `${local.substring(0, 3)}...${local.substring(local.length - 2)}@${domain}`;
}

// Check if historical dates are in the past to lock started games
export const isMatchStarted = (match: ScheduleMatch, officialResults?: Record<string, any>): boolean => {
    try {
        // To allow predictions for all matches of all time, we only lock games if they are explicitly
        // recorded in the official settled/finished results. This permits unrestricted forecasting.
        if (officialResults && officialResults[match.id]) return true;
        return false;
    } catch {
        return false;
    }
};

export function SchedulesPage({
                                  user = null,
                                  packs = 0,
                                  setPacks = () => {},
                                  predictions = {},
                                  savePrediction = () => {},
                                  lang
                              }: {
    user?: any;
    packs?: number;
    setPacks?: React.Dispatch<React.SetStateAction<number>>;
    predictions?: Record<string, any>;
    savePrediction?: (matchId: string, pred: any) => void;
    lang?: string;
}) {
    const getLang = (): LanguageCode => {
        if (lang && lang in TRANSLATIONS) return lang as LanguageCode;
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('paganini-lang');
            if (saved && saved in TRANSLATIONS) return saved as LanguageCode;
        }
        return 'en';
    };
    const activeLang = getLang();
    const t = (key: string) => TRANSLATIONS[activeLang]?.[key] || TRANSLATIONS['en']?.[key] || key;

    const [activeTab, setActiveTab] = useState<'fixtures' | 'predictions' | 'next24' | 'scoreboard' | 'highlights'>('fixtures');
    const [filterMode, setFilterMode] = useState<'all' | 'locked' | 'resolved'>('all');
    const [stageFilter, setStageFilter] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [hostHighlights, setHostHighlights] = useState<boolean>(false);

    const [dynamicHighlights, setDynamicHighlights] = useState<any[]>([]);
    const [loadingHighlights, setLoadingHighlights] = useState<boolean>(true);
    const [checkingHighlights, setCheckingHighlights] = useState<boolean>(false);
    const [highlightsCheckMessage, setHighlightsCheckMessage] = useState<string>('');

    const [currentTime, setCurrentTime] = useState<Date>(new Date());

    const fetchDynamicHighlights = async () => {
        try {
            setLoadingHighlights(true);
            const res = await fetch("/api/football/youtube-highlights");
            if (!res.ok) throw new Error("Status " + res.status);
            const data = await res.json();
            if (data && data.success) {
                setDynamicHighlights(data.highlights || []);
            }
        } catch (err) {
            console.error("Failed to load custom YouTube highlights from server:", err);
        } finally {
            setLoadingHighlights(false);
        }
    };

    const triggerHighlightsCrawl = async () => {
        try {
            setCheckingHighlights(true);
            setHighlightsCheckMessage("AI Bot: Scraping @fifa channel feed...");
            const res = await fetch("/api/football/trigger-highlights-check", { method: "POST" });
            if (!res.ok) throw new Error("Status " + res.status);
            const data = await res.json();
            if (data && data.success) {
                setHighlightsCheckMessage(`Bot completed 1h cycle: fetched & structured matches.`);
                await fetchDynamicHighlights();
            } else {
                setHighlightsCheckMessage(`Crawl result: completed.`);
                await fetchDynamicHighlights();
            }
        } catch (err: any) {
            setHighlightsCheckMessage("Crawler pass: " + (err.message || err));
        } finally {
            setCheckingHighlights(false);
        }
    };

    useEffect(() => {
        fetchDynamicHighlights();
    }, []);
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const [scoreboardUsers, setScoreboardUsers] = useState<any[]>([]);
    const [loadingScoreboard, setLoadingScoreboard] = useState<boolean>(true);

    // Compute current level local user prediction points
    const localPredictionPoints = useMemo(() => {
        return Object.values(predictions).reduce((sum: number, pred: any) => {
            if (pred && pred.resolved) {
                return sum + (pred.pointsWon ?? pred.packsWon ?? 0);
            }
            return sum;
        }, 0);
    }, [predictions]);

    // Super user authorization checks & official results Firestore sync
    const isSuperUser = user && user.email === 'gonzos978@gmail.com';
    const [officialResultsRaw, setOfficialResultsRaw] = useState<Record<string, any>>({});
    const [gameStatisticsRaw, setGameStatisticsRaw] = useState<Record<string, any>>({});

    const officialResults = useMemo(() => {
        const merged: Record<string, any> = {};
        for (const [key, value] of Object.entries(officialResultsRaw)) {
            merged[key] = { ...merged[key], ...value as any };
        }
        for (const [key, value] of Object.entries(gameStatisticsRaw)) {
            merged[key] = { ...merged[key], ...value as any };
        }
        return merged;
    }, [officialResultsRaw, gameStatisticsRaw]);

    // Combine static and dynamic scraped/fetched highlights
    const ALL_HIGHLIGHTS = useMemo(() => {
        // Generate matches played highlights dynamically for matches that have concluded
        const playedMatchesHighlights = SHEDULE_DATA.filter(match => {
            const isPast = parseMatchDateStringToDate(match.date).getTime() < currentTime.getTime();
            const hasOfficial = officialResults && officialResults[match.id];
            const hasResolvedPred = predictions && predictions[match.id]?.resolved;
            return isPast || hasOfficial || hasResolvedPred;
        }).map(match => {
            // Find score
            let scoreStr = "0 - 0";
            if (officialResults && officialResults[match.id]) {
                scoreStr = `${officialResults[match.id].actualScoreA} - ${officialResults[match.id].actualScoreB}`;
            } else if (predictions && predictions[match.id]?.resolved && predictions[match.id]?.actualScoreA !== undefined) {
                scoreStr = `${predictions[match.id].actualScoreA} - ${predictions[match.id].actualScoreB}`;
            } else {
                const determin = getDeterministicMatchResult(match.id);
                scoreStr = `${determin.actualScoreA} - ${determin.actualScoreB}`;
            }

            // Generate a deterministic duration between 3:45 and 6:15
            let seed = 0;
            for (let i = 0; i < match.id.length; i++) {
                seed += match.id.charCodeAt(i);
            }
            const pseudoRandom = Math.sin(seed) * 1000;
            const min = Math.floor(3 + (pseudoRandom - Math.floor(pseudoRandom)) * 3);
            const sec = Math.floor((pseudoRandom * 60) % 60);
            const formatSec = sec < 10 ? `0${sec}` : `${sec}`;
            const duration = `${min}:${formatSec}`;

            // Team colors or gradients for the background thumbnail
            const fallbackGradients = [
                "from-indigo-950 via-slate-900 to-indigo-900",
                "from-emerald-950 via-zinc-900 to-teal-900",
                "from-blue-950 via-slate-900 to-sky-950",
                "from-red-950 via-zinc-900 to-rose-950",
                "from-amber-950 via-slate-900 to-yellow-950",
                "from-purple-950 via-zinc-900 to-indigo-950"
            ];
            const gradient = fallbackGradients[seed % fallbackGradients.length];

            // Build scorer list from deterministic stats or generic scorers
            const scorers: { player: string; min: number; team: string; }[] = [];
            const scoreParts = scoreStr.split(" - ");
            const goalsA = parseInt(scoreParts[0], 10) || 0;
            const goalsB = parseInt(scoreParts[1], 10) || 0;

            const teamAStarPlayers = [
                "S. Giménez", "E. Álvarez", "H. Lozano", "R. Jiménez",
                "C. Pulisic", "F. Balogun", "G. Reyna", "W. McKennie",
                "H. Son", "H. Hwang", "K. Lee", "T. Kubo", "M. Mitoma",
                "K. Mbappé", "A. Griezmann", "O. Dembélé",
                "Vinícius Jr.", "Neymar Jr", "Rodrygo", "Endrick"
            ];
            const teamBStarPlayers = [
                "P. Schick", "T. Souček", "A. Hložek",
                "L. Foster", "P. Tau",
                "E. Džeko", "M. Pjanić", "R. Krunić",
                "G. Xhaka", "B. Embolo", "Z. Amdouni",
                "M. Almirón", "A. Sanabria",
                "Arda Güler", "H. Calhanoglu", "K. Aktürkoğlu"
            ];

            for (let i = 0; i < goalsA; i++) {
                const pRandomIndex = Math.floor((seed + i * 17) % teamAStarPlayers.length);
                scorers.push({
                    player: teamAStarPlayers[pRandomIndex] || `${match.teamA} Star`,
                    min: Math.floor(10 + (25 * i) + (seed % 15)),
                    team: match.teamA
                });
            }
            for (let i = 0; i < goalsB; i++) {
                const pRandomIndex = Math.floor((seed + i * 23) % teamBStarPlayers.length);
                scorers.push({
                    player: teamBStarPlayers[pRandomIndex] || `${match.teamB} Star`,
                    min: Math.floor(15 + (22 * i) + (seed % 17)),
                    team: match.teamB
                });
            }

            const stageLabel = match.stage === 'groups' ? match.group : match.group;

            return {
                id: `hl_dyn_${match.id}`,
                matchId: match.id,
                title: `${match.teamA} vs ${match.teamB} - Matchday Highlights`,
                stadium: `${match.stadium}`,
                date: match.date,
                score: scoreStr,
                duration: duration,
                narrator: "FIFA AI Sportscaster Engine",
                videoUrl: "https://dailygoal.tv/world-cup-2026-highlights",
                youtubeQuery: `${match.teamA} vs ${match.teamB} FIFA World Cup 2026 highlights`,
                thumbnailGradient: gradient,
                homeTeam: match.teamA,
                awayTeam: match.teamB,
                goals: scorers,
                summary: `An absolute tactical showcase in this highly anticipated matchup at ${match.stadium}! Both teams displayed world-class resilience as they clashed in front of thousands of fans. A pivotal result for their ${stageLabel} standings.`
            };
        });

        const combined = [
            ...dynamicHighlights.map((dh: any) => {
                const matchingSchedule = SHEDULE_DATA.find((m) => m.id === dh.matchId);
                return {
                    id: dh.id,
                    matchId: dh.matchId || "",
                    title: dh.title,
                    stadium: matchingSchedule?.stadium || "FIFA World Cup Stadium",
                    date: dh.publishedAt
                        ? new Date(dh.publishedAt).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                        : "Recently Uploaded",
                    score: dh.matchId
                        ? (predictions[dh.matchId]?.actualScoreA !== undefined
                            ? `${predictions[dh.matchId].actualScoreA} - ${predictions[dh.matchId].actualScoreB}`
                            : "1 - 1")
                        : "Video Clip",
                    duration: "4:00",
                    narrator: "FIFA AI Bot commentary",
                    videoUrl: "https://dailygoal.tv/world-cup-2026-highlights",
                    youtubeQuery: dh.title,
                    thumbnailGradient: "from-slate-900 via-zinc-900 to-indigo-950",
                    thumbnailUrl: dh.thumbnailUrl,
                    homeTeam: matchingSchedule?.teamA || "FIFA",
                    awayTeam: matchingSchedule?.teamB || "World Cup",
                    goals: [],
                    summary: dh.summary
                };
            }),
            ...VIDEO_HIGHLIGHTS,
            ...playedMatchesHighlights
        ];

        const seenIds = new Set();
        return combined.filter(hl => {
            if (hl.matchId && seenIds.has(hl.matchId)) {
                return false;
            }
            if (seenIds.has(hl.id)) {
                return false;
            }
            if (hl.matchId) seenIds.add(hl.matchId);
            seenIds.add(hl.id);
            return true;
        });
    }, [dynamicHighlights, predictions, officialResults, currentTime]);

    // Load scoreboard from Firestore
    useEffect(() => {
        const q = query(
            collection(db, 'users'),
            limit(50)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: any[] = [];
            snapshot.forEach(doc => {
                const u = doc.data();
                let points = u.predictionPoints || 0;

                // Recalculate or secure accurate client points from latest predictions model
                if (u.predictions && Object.keys(u.predictions).length > 0) {
                    points = Object.values(u.predictions).reduce((sum: number, pred: any) => {
                        if (pred && pred.resolved) return sum + (pred.pointsWon ?? pred.packsWon ?? 0);
                        return sum;
                    }, 0);
                }

                list.push({
                    uid: u.userId,
                    displayName: u.displayName || u.email?.split('@')[0] || 'Collector',
                    photoURL: u.photoURL,
                    email: u.email,
                    predictionPoints: points,
                    streak: u.streak || 0,
                    predictionsCount: u.predictions ? Object.keys(u.predictions).length : 0,
                    resolvedCount: u.predictions ? Object.values(u.predictions).filter((p: any) => p.resolved).length : 0,
                    predictions: u.predictions || {}
                });
            });

            // Sort by prediction points desc, then predictions count desc
            list.sort((a, b) => {
                if (b.predictionPoints !== a.predictionPoints) {
                    return b.predictionPoints - a.predictionPoints;
                }
                return b.predictionsCount - a.predictionsCount;
            });

            setScoreboardUsers(list);
            setLoadingScoreboard(false);
        }, (error) => {
            console.error("Scoreboard subscription error: ", error);
            setLoadingScoreboard(false);
            if (isFirestoreQuotaError(error)) {
                notifyQuotaExceeded();
            }
        });

        return () => unsubscribe();
    }, []);

    // Active prediction form modal state
    const [predictingMatch, setPredictingMatch] = useState<ScheduleMatch | null>(null);
    const [formScoreA, setFormScoreA] = useState<number>(1);
    const [formScoreB, setFormScoreB] = useState<number>(0);
    const [formGoalsOverUnder, setFormGoalsOverUnder] = useState<'over' | 'under'>('under');
    const [formCorners, setFormCorners] = useState<number>(8);

    // Simulation state
    const [simulatingMatch, setSimulatingMatch] = useState<ScheduleMatch | null>(null);
    const [simLogs, setSimLogs] = useState<string[]>([]);
    const [simStep, setSimStep] = useState<number>(0);
    const [simOutcome, setSimOutcome] = useState<any>(null);

    // Social Share & Statistics layout state
    const [showAuthWarning, setShowAuthWarning] = useState<boolean>(false);
    const [sharingMatch, setSharingMatch] = useState<ScheduleMatch | null>(null);
    const [sharingType, setSharingType] = useState<'predict' | 'stats'>('predict');
    const [showingStatsMatch, setShowingStatsMatch] = useState<ScheduleMatch | null>(null);
    const [isCopied, setIsCopied] = useState<boolean>(false);
    const [showResetConfirm, setShowResetConfirm] = useState<boolean>(false);
    const [resetNotify, setResetNotify] = useState<string | null>(null);

    // Overlays for exploring other users' predictions
    const [viewingPlayerPredictions, setViewingPlayerPredictions] = useState<any | null>(null);
    const [viewingCommunityMatch, setViewingCommunityMatch] = useState<ScheduleMatch | null>(null);
    const [playingVideoHighlight, setPlayingVideoHighlight] = useState<any | null>(null);

    // Load all predictions from top-level Firestore collection in real-time
    const [allPredictions, setAllPredictions] = useState<any[]>([]);

    useEffect(() => {
        const q = collection(db, 'predictions');
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: any[] = [];
            snapshot.forEach(doc => {
                list.push(doc.data());
            });
            setAllPredictions(list);
        }, (error) => {
            console.error("Failed to load global predictions collection: ", error);
            if (isFirestoreQuotaError(error)) {
                notifyQuotaExceeded();
            }
        });
        return () => unsubscribe();
    }, []);


    const [adminEditingMatchId, setAdminEditingMatchId] = useState<string | null>(null);
    const [isAutofilling, setIsAutofilling] = useState<boolean>(false);
    const [autofillError, setAutofillError] = useState<string | null>(null);

    const handleAutofillFromAPI = async (teamA: string, teamB: string) => {
        setIsAutofilling(true);
        setAutofillError(null);
        try {
            const res = await fetch(`/api/football/autofill-stats?teamA=${encodeURIComponent(teamA)}&teamB=${encodeURIComponent(teamB)}`);
            const body = await res.json();
            console.log("🔌 [Football API-Sports Autofill Response]:", body);
            if (!res.ok) {
                throw new Error(body.error || "Failed to fetch stats from server");
            }
            if (body.success && body.match) {
                const m = body.match;
                setAdminScoreA(m.scoreA);
                setAdminScoreB(m.scoreB);
                setAdminCorners(m.cornersA + m.cornersB);
                setAdminPossessionA(m.possessionA);
                setAdminPossessionB(m.possessionB);
                setAdminShotsTotalA(m.shotsTotalA);
                setAdminShotsTotalB(m.shotsTotalB);
                setAdminShotsTargetA(m.shotsTargetA);
                setAdminShotsTargetB(m.shotsTargetB);
                setAdminPassingA(m.passingA);
                setAdminPassingB(m.passingB);
                setAdminCornersA(m.cornersA);
                setAdminCornersB(m.cornersB);
                setAdminFoulsA(m.foulsA);
                setAdminFoulsB(m.foulsB);
                setAdminYellowA(m.yellowA);
                setAdminYellowB(m.yellowB);
                setAdminRedA(m.redA);
                setAdminRedB(m.redB);

                alert("🔌 API Autobest! Official scores and detailed statistics parsed successfully from API-Sports.");
            } else {
                throw new Error("No match data returned from API-Sports");
            }
        } catch (err: any) {
            console.error("Autofill API error: ", err);
            setAutofillError(err.message || "Unknown error");
            alert("❌ Autofill API Error: " + (err.message || "Could not fetch match data."));
        } finally {
            setIsAutofilling(false);
        }
    };

    const [adminScoreA, setAdminScoreA] = useState<number>(0);
    const [adminScoreB, setAdminScoreB] = useState<number>(0);
    const [adminCorners, setAdminCorners] = useState<number>(8);
    const [adminPossessionA, setAdminPossessionA] = useState<number>(50);
    const [adminPossessionB, setAdminPossessionB] = useState<number>(50);
    const [adminShotsTotalA, setAdminShotsTotalA] = useState<number>(10);
    const [adminShotsTotalB, setAdminShotsTotalB] = useState<number>(10);
    const [adminShotsTargetA, setAdminShotsTargetA] = useState<number>(4);
    const [adminShotsTargetB, setAdminShotsTargetB] = useState<number>(4);
    const [adminPassingA, setAdminPassingA] = useState<number>(80);
    const [adminPassingB, setAdminPassingB] = useState<number>(80);
    const [adminCornersA, setAdminCornersA] = useState<number>(4);
    const [adminCornersB, setAdminCornersB] = useState<number>(4);
    const [adminFoulsA, setAdminFoulsA] = useState<number>(10);
    const [adminFoulsB, setAdminFoulsB] = useState<number>(10);
    const [adminYellowA, setAdminYellowA] = useState<number>(1);
    const [adminYellowB, setAdminYellowB] = useState<number>(1);
    const [adminRedA, setAdminRedA] = useState<number>(0);
    const [adminRedB, setAdminRedB] = useState<number>(0);

    useEffect(() => {
        const q1 = collection(db, 'official_results');
        const q2 = collection(db, 'gamestatistics');

        const unsub1 = onSnapshot(q1, (snapshot) => {
            const results: Record<string, any> = {};
            snapshot.forEach((doc) => {
                results[doc.id] = doc.data();
            });
            setOfficialResultsRaw(results);
        }, (error) => {
            console.error("Failed to sync global official results: ", error);
            if (isFirestoreQuotaError(error)) {
                notifyQuotaExceeded();
            }
        });

        const unsub2 = onSnapshot(q2, (snapshot) => {
            const stats: Record<string, any> = {};
            snapshot.forEach((doc) => {
                stats[doc.id] = doc.data();
            });
            setGameStatisticsRaw(stats);
        }, (error) => {
            console.error("Failed to sync global gamestatistics: ", error);
            if (isFirestoreQuotaError(error)) {
                notifyQuotaExceeded();
            }
        });

        return () => {
            unsub1();
            unsub2();
        };
    }, []);

    const saveOfficialResult = async (matchId: string, scoreA: number, scoreB: number, corners: number) => {
        try {
            const docRef = doc(db, 'official_results', matchId);
            const statsDocRef = doc(db, 'gamestatistics', matchId);
            const goalsOverUnder = (scoreA + scoreB) > 2.5 ? 'over' : 'under';
            const payload = {
                matchId,
                actualScoreA: scoreA,
                actualScoreB: scoreB,
                actualCorners: corners,
                actualGoalsOverUnder: goalsOverUnder,
                resolved: true,
                locked: true,
                updatedBy: user?.email || 'gonzos978@gmail.com',
                updatedAt: new Date().toISOString(),
                actualPossession: [adminPossessionA, adminPossessionB],
                actualShotsTotal: [adminShotsTotalA, adminShotsTotalB],
                actualShotsTarget: [adminShotsTargetA, adminShotsTargetB],
                actualPassing: [adminPassingA, adminPassingB],
                actualCornersA: adminCornersA,
                actualCornersB: adminCornersB,
                actualFouls: [adminFoulsA, adminFoulsB],
                actualYellow: [adminYellowA, adminYellowB],
                actualRed: [adminRedA, adminRedB]
            };

            await setDoc(docRef, payload, { merge: true });
            await setDoc(statsDocRef, payload, { merge: true });

            // Load all predictions for this match across all users from high-level collection
            const predQuery = query(collection(db, 'predictions'), where('matchId', '==', matchId));
            const predSnap = await getDocs(predQuery);

            for (const predDoc of predSnap.docs) {
                const pred = predDoc.data();
                const predUserId = pred.userId;
                if (!predUserId) continue;

                // Scoring rules: exact scoreline 5 points, goals O/U 2 points, corners 3 points
                const isScoreCorrect = (Number(pred.scoreA) === scoreA && Number(pred.scoreB) === scoreB);
                const isGoalsCorrect = (pred.goalsOverUnder === goalsOverUnder);
                const isCornersCorrect = Math.abs(Number(pred.corners || 0) - corners) <= 1;

                let pointsWon = 0;
                if (isScoreCorrect) pointsWon += 5;
                if (isGoalsCorrect) pointsWon += 2;
                if (isCornersCorrect) pointsWon += 3;

                let packsWon = 0;
                if (isScoreCorrect) packsWon += 3;
                if (isCornersCorrect) packsWon += 2;
                if (isGoalsCorrect) packsWon += 1;

                // 1. Update individual prediction document inpredictions collection
                await setDoc(doc(db, 'predictions', predDoc.id), {
                    resolved: true,
                    status: 'finished',
                    actualScoreA: scoreA,
                    actualScoreB: scoreB,
                    actualCorners: corners,
                    pointsWon: pointsWon,
                    packsWon: packsWon,
                    updatedAt: new Date().toISOString()
                }, { merge: true });

                // 2. Load and update the user's document object (predictions map, predictionPoints scoreboard, packs wallet)
                const userDocRef = doc(db, 'users', predUserId);
                const userSnap = await getDoc(userDocRef);
                if (userSnap.exists()) {
                    const userData = userSnap.data();
                    const userPredictions = userData.predictions || {};

                    let oldPacks = 0;
                    if (userPredictions[matchId] && userPredictions[matchId].resolved) {
                        oldPacks = Number(userPredictions[matchId].packsWon || 0);
                    }

                    // Merge prediction into map
                    userPredictions[matchId] = {
                        ...(userPredictions[matchId] || pred),
                        resolved: true,
                        status: 'finished',
                        actualScoreA: scoreA,
                        actualScoreB: scoreB,
                        actualCorners: corners,
                        pointsWon: pointsWon,
                        packsWon: packsWon,
                        updatedAt: new Date().toISOString()
                    };

                    // Re-calculate the sum of all resolved predictions' points
                    const totalPointsCalculated = Object.values(userPredictions).reduce((sum: number, p: any) => {
                        if (p && p.resolved) {
                            return sum + Number(p.pointsWon ?? p.packsWon ?? 0);
                        }
                        return sum;
                    }, 0);

                    // Update packs wallet
                    const deltaPacks = packsWon - oldPacks;
                    const userPacks = Number(userData.packs || 0);
                    const nextPacks = Math.max(0, userPacks + deltaPacks);

                    await setDoc(userDocRef, {
                        predictions: userPredictions,
                        predictionPoints: totalPointsCalculated,
                        packs: nextPacks,
                        updatedAt: new Date().toISOString()
                    }, { merge: true });
                }
            }

            alert("Official score verified and locked. All user predictions have been resolved: Correct Score line (+5 Points, +3 Sticker Packs), Goals Over/Under (+2 Points, +1 Sticker Pack), and Corners (+3 Points, +2 Sticker Packs) awarded successfully!");
        } catch (err) {
            console.error("Failed to save official match result: ", err);
            alert("Failed to save official match result: " + (err as Error).message);
        }
    };

    const deleteOfficialResult = async (matchId: string) => {
        try {
            const docRef = doc(db, 'official_results', matchId);
            const statsDocRef = doc(db, 'gamestatistics', matchId);
            await deleteDoc(docRef);
            await deleteDoc(statsDocRef);

            // Revert all related user prediction documents back to active/unresolved
            const predQuery = query(collection(db, 'predictions'), where('matchId', '==', matchId));
            const predSnap = await getDocs(predQuery);

            for (const predDoc of predSnap.docs) {
                const pred = predDoc.data();
                const predUserId = pred.userId;
                if (!predUserId) continue;

                // Reset the prediction document in 'predictions' collection
                await setDoc(doc(db, 'predictions', predDoc.id), {
                    resolved: false,
                    status: 'active',
                    actualScoreA: null,
                    actualScoreB: null,
                    actualCorners: null,
                    pointsWon: 0,
                    packsWon: 0,
                    updatedAt: new Date().toISOString()
                }, { merge: true });

                // Update the user profile document (remove awarded points, packages)
                const userDocRef = doc(db, 'users', predUserId);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    const userPredictions = userData.predictions || {};

                    let oldPacks = 0;
                    if (userPredictions[matchId] && userPredictions[matchId].resolved) {
                        oldPacks = Number(userPredictions[matchId].packsWon || 0);
                    }

                    // Un-resolve prediction mapping
                    userPredictions[matchId] = {
                        ...(userPredictions[matchId] || pred),
                        resolved: false,
                        status: 'active',
                        actualScoreA: null,
                        actualScoreB: null,
                        actualCorners: null,
                        pointsWon: 0,
                        packsWon: 0,
                        updatedAt: new Date().toISOString()
                    };

                    // Re-calculate the sum of all resolved predictions
                    const totalPointsCalculated = Object.values(userPredictions).reduce((sum: number, p: any) => {
                        if (p && p.resolved) {
                            return sum + Number(p.pointsWon ?? p.packsWon ?? 0);
                        }
                        return sum;
                    }, 0);

                    // Deduct previously rewarded pack counts
                    const userPacks = Number(userData.packs || 0);
                    const nextPacks = Math.max(0, userPacks - oldPacks);

                    await setDoc(userDocRef, {
                        predictions: userPredictions,
                        predictionPoints: totalPointsCalculated,
                        packs: nextPacks,
                        updatedAt: new Date().toISOString()
                    }, { merge: true });
                }
            }

            alert("Definitive score cleared. All player predictions resolved back to active/pending status.");
        } catch (err) {
            console.error("Failed to delete official match result: ", err);
            alert("Failed to delete official match result: " + (err as Error).message);
        }
    };

    const handleOpenAdminEdit = (match: ScheduleMatch) => {
        const existing = officialResults[match.id];
        setAdminEditingMatchId(match.id);
        setAdminScoreA(existing ? existing.actualScoreA : 0);
        setAdminScoreB(existing ? existing.actualScoreB : 0);
        setAdminCorners(existing ? (existing.actualCorners !== undefined ? existing.actualCorners : 8) : 8);
        setAdminPossessionA(existing?.actualPossession ? existing.actualPossession[0] : 50);
        setAdminPossessionB(existing?.actualPossession ? existing.actualPossession[1] : 50);
        setAdminShotsTotalA(existing?.actualShotsTotal ? existing.actualShotsTotal[0] : 10);
        setAdminShotsTotalB(existing?.actualShotsTotal ? existing.actualShotsTotal[1] : 10);
        setAdminShotsTargetA(existing?.actualShotsTarget ? existing.actualShotsTarget[0] : 4);
        setAdminShotsTargetB(existing?.actualShotsTarget ? existing.actualShotsTarget[1] : 4);
        setAdminPassingA(existing?.actualPassing ? existing.actualPassing[0] : 80);
        setAdminPassingB(existing?.actualPassing ? existing.actualPassing[1] : 80);
        setAdminCornersA(existing?.actualCornersA !== undefined ? existing.actualCornersA : (existing?.actualCorners ? Math.floor(existing.actualCorners / 2) : 4));
        setAdminCornersB(existing?.actualCornersB !== undefined ? existing.actualCornersB : (existing?.actualCorners ? Math.ceil(existing.actualCorners / 2) : 4));
        setAdminFoulsA(existing?.actualFouls ? existing.actualFouls[0] : 10);
        setAdminFoulsB(existing?.actualFouls ? existing.actualFouls[1] : 10);
        setAdminYellowA(existing?.actualYellow ? existing.actualYellow[0] : 1);
        setAdminYellowB(existing?.actualYellow ? existing.actualYellow[1] : 1);
        setAdminRedA(existing?.actualRed ? existing.actualRed[0] : 0);
        setAdminRedB(existing?.actualRed ? existing.actualRed[1] : 0);
    };

    // Compute community forecast statistics for a given match
    const getCommunityPredictions = (matchId: string) => {
        let teamAWins = 0;
        let draws = 0;
        let teamBWins = 0;
        const userPredictions: { userId: string; displayName: string; photoURL: string; email: string; scoreA: number; scoreB: number; corners: number; goalsOverUnder: string }[] = [];

        // Filter prediction documents by matchId from the real-time stream
        const matchPreds = [...allPredictions.filter(p => p.matchId === matchId)];

        // Merge in any other players from scoreboardUsers that have predictions for this match but aren't in the predictions collection yet
        const existingUserIds = new Set(matchPreds.map(p => p.userId));
        scoreboardUsers.forEach(u => {
            if (u.uid && !existingUserIds.has(u.uid)) {
                const p = u.predictions?.[matchId];
                if (p && p.submitted) {
                    matchPreds.push({
                        id: `${u.uid}_${matchId}`,
                        userId: u.uid,
                        matchId: matchId,
                        scoreA: p.scoreA,
                        scoreB: p.scoreB,
                        goalsOverUnder: p.goalsOverUnder,
                        corners: p.corners || 0,
                        submitted: p.submitted,
                        resolved: p.resolved,
                        status: p.status || 'active',
                        displayName: u.displayName || 'Collector',
                        photoURL: u.photoURL || 'wc_ball',
                        email: u.email || '',
                        updatedAt: p.updatedAt || new Date().toISOString()
                    });
                }
            }
        });

        matchPreds.forEach(p => {
            if (p.submitted) {
                if (p.scoreA > p.scoreB) {
                    teamAWins++;
                } else if (p.scoreA < p.scoreB) {
                    teamBWins++;
                } else {
                    draws++;
                }
                userPredictions.push({
                    userId: p.userId || '',
                    displayName: p.displayName || 'Collector',
                    photoURL: p.photoURL || 'wc_ball',
                    email: p.email || '',
                    scoreA: Number(p.scoreA),
                    scoreB: Number(p.scoreB),
                    corners: Number(p.corners || 0),
                    goalsOverUnder: p.goalsOverUnder || 'under'
                });
            }
        });

        // Merge in current user's local prediction if they have one and it's not yet synced to Firestore
        const localPred = predictions[matchId];
        if (localPred && localPred.submitted && !userPredictions.some(up => up.userId === user?.uid)) {
            if (localPred.scoreA > localPred.scoreB) {
                teamAWins++;
            } else if (localPred.scoreA < localPred.scoreB) {
                teamBWins++;
            } else {
                draws++;
            }
            userPredictions.push({
                userId: user?.uid || 'guest_local_id',
                displayName: user?.displayName || user?.email?.split('@')[0] || 'You',
                photoURL: user?.photoURL || 'wc_ball',
                email: user?.email || '',
                scoreA: Number(localPred.scoreA),
                scoreB: Number(localPred.scoreB),
                corners: Number(localPred.corners || 0),
                goalsOverUnder: localPred.goalsOverUnder || 'under'
            });
        }

        const total = teamAWins + draws + teamBWins;
        return { teamAWins, draws, teamBWins, total, list: userPredictions };
    };

    // Auto detect goalsOverUnder whenever scores change
    useEffect(() => {
        if (formScoreA + formScoreB > 2) {
            setFormGoalsOverUnder('over');
        } else {
            setFormGoalsOverUnder('under');
        }
    }, [formScoreA, formScoreB]);

    const hasCheckedRef = React.useRef(false);

    // All matches are manually predictable and simulated by the user. Auto-resolve is disabled to preserve user predictions.

    // Group options
    const stages = [
        { key: 'all', label: 'All Stages' },
        { key: 'groups', label: 'Group Stage' },
        { key: 'round_of_32', label: 'Round of 32' },
        { key: 'round_of_16', label: 'Round of 16' },
        { key: 'quarters', label: 'Quarter-Finals' },
        { key: 'semis', label: 'Semi-Finals' },
        { key: 'finals', label: 'Finals' },
    ];

    const filteredMatches = useMemo(() => {
        return SHEDULE_DATA.filter(match => {
            // Stage matching
            if (stageFilter !== 'all') {
                if (stageFilter === 'finals') {
                    if (match.stage !== 'final' && match.stage !== 'bronze') return false;
                } else if (match.stage !== stageFilter) {
                    return false;
                }
            }

            // Host country filter (USA, Mexico, Canada)
            if (hostHighlights) {
                const teams = [match.teamA.toLowerCase(), match.teamB.toLowerCase()];
                const isHost = teams.some(t => t.includes('mexico') || t.includes('usa') || t.includes('canada') || t.includes('united states'));
                if (!isHost) return false;
            }

            // Search query filter
            if (searchQuery.trim() !== '') {
                const query = searchQuery.toLowerCase();
                const scoreMatch =
                    match.teamA.toLowerCase().includes(query) ||
                    match.teamB.toLowerCase().includes(query) ||
                    match.group.toLowerCase().includes(query) ||
                    match.stadium.toLowerCase().includes(query) ||
                    match.date.toLowerCase().includes(query) ||
                    (match.matchNumber && match.matchNumber.toLowerCase().includes(query));
                if (!scoreMatch) return false;
            }

            return true;
        });
    }, [stageFilter, searchQuery, hostHighlights]);

    // Group filtered results by Date
    const groupedMatches = useMemo(() => {
        const groups: { [date: string]: ScheduleMatch[] } = {};
        filteredMatches.forEach(m => {
            if (!groups[m.date]) {
                groups[m.date] = [];
            }
            groups[m.date].push(m);
        });
        return groups;
    }, [filteredMatches]);

    // Handle open predict screen
    const handleOpenPredict = (match: ScheduleMatch) => {
        if (!user) {
            setShowAuthWarning(true);
            return;
        }
        if (isMatchStarted(match, officialResults)) return;
        const isFinished = predictions[match.id]?.resolved === true || officialResults[match.id]?.resolved === true;
        if (isFinished) return;
        const existing = predictions[match.id];
        setPredictingMatch(match);
        if (existing) {
            setFormScoreA(existing.scoreA);
            setFormScoreB(existing.scoreB);
            setFormGoalsOverUnder(existing.goalsOverUnder);
            setFormCorners(existing.corners);
        } else {
            setFormScoreA(1);
            setFormScoreB(1);
            setFormGoalsOverUnder('under');
            setFormCorners(8);
        }
    };

    // Submit prediction
    const handleLockPrediction = () => {
        if (!predictingMatch) return;
        const isFinished = predictions[predictingMatch.id]?.resolved === true || officialResults[predictingMatch.id]?.resolved === true;
        if (isFinished || isMatchStarted(predictingMatch, officialResults)) {
            setPredictingMatch(null);
            return;
        }
        savePrediction(predictingMatch.id, {
            matchId: predictingMatch.id,
            scoreA: formScoreA,
            scoreB: formScoreB,
            goalsOverUnder: formGoalsOverUnder,
            corners: formCorners,
            submitted: true,
            resolved: false,
            status: 'active'
        });
        setPredictingMatch(null);
    };

    // Count active / unresolved predictions dynamically (to allow reset) - only matches that have NOT started yet!
    const unstartedPredictionsCount = useMemo(() => {
        return Object.entries(predictions).filter(([matchId, p]: [string, any]) => {
            if (!p || p.resolved) return false;
            const match = SHEDULE_DATA.find(m => m.id === matchId);
            if (!match) return false;
            return !isMatchStarted(match, officialResults);
        }).length;
    }, [predictions]);

    // Bulk reset active predictions that have NOT started yet
    const handleResetUnstarted = () => {
        if (!savePrediction) return;
        let count = 0;
        Object.entries(predictions).forEach(([matchId, p]: [string, any]) => {
            if (p && !p.resolved) {
                const match = SHEDULE_DATA.find(m => m.id === matchId);
                // Only allow resetting if the match has not started yet
                if (match && !isMatchStarted(match, officialResults)) {
                    savePrediction(matchId, null);
                    count++;
                }
            }
        });
        setResetNotify(`Successfully reset ${count} active predictions!`);
        setShowResetConfirm(false);
        setTimeout(() => {
            setResetNotify(null);
        }, 4500);
    };

    // Run the premium matchday simulator
    const handleSimulateMatch = (match: ScheduleMatch) => {
        const originalPred = predictions[match.id];
        if (!originalPred) return;

        setSimulatingMatch(match);
        setSimLogs(["🏟️ Teams are lining up in the tunnel... Fans are creating a legendary atmosphere!"]);
        setSimStep(0);
        setSimOutcome(null);

        // Realistic tournament score generator
        const getRandomGoals = () => {
            const r = Math.random();
            if (r < 0.28) return 0;
            if (r < 0.60) return 1;
            if (r < 0.85) return 2;
            if (r < 0.95) return 3;
            return 4;
        };

        const actualA = getRandomGoals();
        const actualB = getRandomGoals();
        const actualCorners = Math.floor(Math.random() * 8) + 4; // 4 to 11 corners
        const actualOverUnder = (actualA + actualB) > 2.5 ? 'over' : 'under';

        // Calculate score metrics
        const isScoreCorrect = (actualA === originalPred.scoreA && actualB === originalPred.scoreB);
        const isGoalsCorrect = (actualOverUnder === originalPred.goalsOverUnder);
        const isCornersCorrect = Math.abs(originalPred.corners - actualCorners) <= 1; // within 1

        let pointsWon = 0;
        if (isScoreCorrect) pointsWon += 5;
        if (isGoalsCorrect) pointsWon += 2;
        if (isCornersCorrect) pointsWon += 3;

        let packsWon = 0;
        if (isScoreCorrect) packsWon += 3;
        if (isCornersCorrect) packsWon += 2;
        if (isGoalsCorrect) packsWon += 1;

        const timelineEvents = [
            `⏱️ [Kickoff] The referee blows the whistle! ${match.teamA} vs ${match.teamB} is underway!`,
            actualCorners > 5
                ? `🚩 [22'] End-to-end action! A fierce deflection forces a critical corner kick.`
                : `⚙️ [22'] Tactically intense start. Both defensive blocks are holding tight.`,
            actualA > 0
                ? `⚽ [38'] GOOOL! ${match.teamA} scores a magnificent volley into the top corner! [${match.teamA} 1 - 0 ${match.teamB}]`
                : `🧤 [38'] Extraordinary save! The goalkeeper stretches to tip a curving shot over the bar.`,
            `⏱️ [Halftime] The players head to the changing rooms to debrief. Strategical changes incoming!`,
            actualB > 0
                ? `⚽ [68'] GOOOL! ${match.teamB} strikes back with an unstoppable rocket! [Score: ${actualA > 0 ? actualA : 0} - 1]`
                : `🛡️ [68'] Staggering tackle inside the penalty area snuffs out a lethal fast break!`,
            actualCorners > 8
                ? `🚩 [81'] Spectacular pressure causing consecutive corner kicks. Total corners rising to ${actualCorners}!`
                : `⏱️ [81'] Time is running down. Players are leaving everything on the pitch.`,
            actualA > 1 || actualB > 1
                ? `🔥 [88'] DRAMA! A late goal changes everything! The crowd goes absolutely wild!`
                : `🧤 [88'] What a dramatic block by the goalkeeper to preserve the scoreline!`,
            `🏁 [90' Fulltime] The match concludes with final score: ${match.teamA} ${actualA} - ${actualB} ${match.teamB}. Total Corners: ${actualCorners}.`
        ];

        let current = 0;
        const interval = setInterval(() => {
            if (current < timelineEvents.length) {
                setSimLogs(prev => [...prev, timelineEvents[current]]);
                setSimStep(current + 1);
                current++;
            } else {
                clearInterval(interval);
                const resolvedOutcome = {
                    scoreA: actualA,
                    scoreB: actualB,
                    corners: actualCorners,
                    goalsOverUnder: actualOverUnder,
                    pointsWon,
                    packsWon,
                    isScoreCorrect,
                    isGoalsCorrect,
                    isCornersCorrect
                };
                setSimOutcome(resolvedOutcome);

                // Update database and states!
                savePrediction(match.id, {
                    ...originalPred,
                    resolved: true,
                    status: 'finished',
                    actualScoreA: actualA,
                    actualScoreB: actualB,
                    actualCorners,
                    pointsWon,
                    packsWon
                });

                if (packsWon > 0) {
                    setPacks(p => p + packsWon);
                }
            }
        }, 900);
    };

    // Check if match supports prediction (matches that have not started yet are predictable)
    const isPredictable = (match: ScheduleMatch) => {
        return !isMatchStarted(match, officialResults);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 text-slate-300">

            {/* Visual Navigation Subheader */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2.5">
                        <Trophy className="text-amber-400" size={24} /> {t('fixturesTitle')}
                    </h2>
                    <p className="text-xs text-slate-400 font-mono mt-1">
                        {t('predictionsDashboard')}
                    </p>
                </div>

                {/* Prediction Stats Cards */}
                <div className="grid grid-cols-2 lg:flex lg:flex-wrap gap-3 w-full md:w-auto">
                    <div className="bg-[#131d31] border border-slate-800/80 px-4 py-2 rounded-xl flex items-center gap-3 text-xs shadow-md">
                        <Sparkles size={15} className="text-yellow-400 animate-bounce shrink-0" />
                        <div className="flex flex-col font-mono min-w-0">
                            <span className="text-white font-bold text-[10px] sm:text-xs leading-tight truncate">{t('earnedPacks')}</span>
                            <span className="text-[9px] text-slate-400 uppercase leading-none mt-0.5 truncate">
                {t('earnedPacks')}: <strong className="text-yellow-400 font-mono font-black">{packs}</strong>
              </span>
                        </div>
                    </div>
                    <div className="bg-[#131d31] border border-slate-800/80 px-4 py-2 rounded-xl flex items-center gap-3 text-xs shadow-md">
                        <Trophy size={15} className="text-amber-400 shrink-0" />
                        <div className="flex flex-col font-mono min-w-0">
                            <span className="text-white font-bold text-[10px] sm:text-xs leading-tight truncate">{t('myPredictionPoints')}</span>
                            <span className="text-[9px] text-slate-400 uppercase leading-none mt-0.5 truncate">
                {t('pointsTitle')}: <strong className="text-amber-400 font-mono font-black">{localPredictionPoints} PTS</strong>
              </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Tab Switcher */}
            <div className="relative">
                {/* Subtle mobile gradient swipe cues */}
                <div className="absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l from-slate-950 to-transparent pointer-events-none z-10 sm:hidden" />
                <div className="absolute left-0 top-0 bottom-4 w-6 bg-gradient-to-r from-slate-950 to-transparent pointer-events-none z-10 sm:hidden" />

                <div className="flex gap-2 sm:gap-3 mb-6 border-b border-slate-800/80 pb-4 overflow-x-auto -mx-4 px-4 custom-scrollbar">
                    <button
                        onClick={() => setActiveTab('fixtures')}
                        className={`flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl border transition-all cursor-pointer shrink-0 ${
                            activeTab === 'fixtures'
                                ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/10'
                                : 'bg-[#131d31] border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white'
                        }`}
                    >
                        <Calendar size={14} /> {t('fixturesTitle')}
                    </button>
                    <button
                        onClick={() => setActiveTab('predictions')}
                        className={`flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl border transition-all cursor-pointer relative shrink-0 ${
                            activeTab === 'predictions'
                                ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/10'
                                : 'bg-[#131d31] border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white'
                        }`}
                    >
                        <Sparkles size={14} className="text-yellow-400 fill-yellow-400/20" />
                        {t('predictionsDashboard')}
                        {Object.values(predictions).filter((p: any) => !p.resolved).length > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white font-mono text-[9px] w-4.5 h-4.5 rounded-full flex items-center justify-center font-black animate-pulse">
                {Object.values(predictions).filter((p: any) => !p.resolved).length}
              </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('next24')}
                        className={`flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl border transition-all cursor-pointer relative shrink-0 ${
                            activeTab === 'next24'
                                ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/10'
                                : 'bg-[#131d31] border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white'
                        }`}
                    >
                        <Activity size={14} className="text-emerald-400 animate-pulse" />
                        {lang === 'es' ? 'Próximas 24 Horas' : 'Next 24 Hours'}
                        {(() => {
                            const now = new Date();
                            const isMatchInNext24Hours = (m: ScheduleMatch): boolean => {
                                const mDate = parseMatchDateStringToDate(m.date);
                                if (isNaN(mDate.getTime())) return false;
                                const timeDiff = mDate.getTime() - now.getTime();
                                return timeDiff >= -2 * 60 * 60 * 1000 && timeDiff <= 24 * 60 * 60 * 1000;
                            };
                            let next24Count = SHEDULE_DATA.filter(isMatchInNext24Hours).length;
                            if (next24Count === 0) {
                                next24Count = SHEDULE_DATA.filter(m => m.date.includes("11 June 2026") || m.date.includes("12 June 2026") || m.date.includes("13 June 2026")).length;
                            }
                            if (next24Count > 0) {
                                return (
                                    <span className="bg-emerald-500 text-[#090d16] font-mono text-[9px] px-1.5 py-0.5 rounded-full flex items-center justify-center font-black">
                    {next24Count}
                  </span>
                                );
                            }
                            return null;
                        })()}
                    </button>
                    <button
                        onClick={() => setActiveTab('scoreboard')}
                        className={`flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl border transition-all cursor-pointer shrink-0 ${
                            activeTab === 'scoreboard'
                                ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/10'
                                : 'bg-[#131d31] border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white'
                        }`}
                    >
                        <Trophy size={14} className="text-amber-400" />
                        {t('globalScoreboard')}
                    </button>
                    <button
                        onClick={() => setActiveTab('highlights')}
                        className={`flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl border transition-all cursor-pointer shrink-0 ${
                            activeTab === 'highlights'
                                ? 'bg-[#ef4444] border-red-500 text-white shadow-lg shadow-red-500/10'
                                : 'bg-[#131d31] border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white'
                        }`}
                    >
                        <Video size={14} className="text-red-500 animate-pulse" />
                        📺 {lang === 'es' ? 'Vídeos de Resúmenes' : 'Video Highlights'}
                    </button>
                </div>
            </div>

            {/* VIEW: SCHEDULES & FIXTURES */}
            {activeTab === 'fixtures' && (
                <div className="space-y-6">
                    {/* Quick Filters */}
                    <div className="bg-[#131d31] border border-slate-800 rounded-2xl p-4 shadow-xl">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                            {/* Search */}
                            <div className="lg:col-span-4 relative">
                                <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none text-slate-500">
                                    <Search size={16} />
                                </div>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={t('searchMatchesPlaceholder')}
                                    className="w-full bg-[#090d16] border border-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-xl py-2.5 pl-10 pr-4 text-xs font-medium text-white placeholder-slate-500 focus:outline-none transition-all"
                                />
                            </div>

                            {/* Grid buttons */}
                            <div className="lg:col-span-8 flex lg:flex-wrap items-center gap-1.5 overflow-x-auto lg:overflow-x-visible pb-1.5 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0 scrollbar-none [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
                                {stages.map(stage => (
                                    <button
                                        key={stage.key}
                                        onClick={() => setStageFilter(stage.key)}
                                        className={`text-xs px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-xl border font-bold transition-all cursor-pointer shrink-0 ${
                                            stageFilter === stage.key
                                                ? 'bg-blue-600 border-blue-500 text-white'
                                                : 'bg-[#090d16] border-slate-800 hover:border-slate-700 text-slate-400'
                                        }`}
                                    >
                                        {lang === 'es' && stage.key === 'groups' ? 'Grupos' : stage.label}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setHostHighlights(!hostHighlights)}
                                    className={`text-xs px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-xl border font-bold flex items-center gap-1.5 transition-all cursor-pointer shrink-0 ${
                                        hostHighlights
                                            ? 'bg-rose-500/20 border-rose-500/40 text-rose-300'
                                            : 'bg-[#090d16] border-slate-800 hover:border-slate-700 text-slate-400'
                                    }`}
                                >
                                    <Heart size={14} className={hostHighlights ? 'fill-rose-400 text-rose-400' : ''} />
                                    {lang === 'es' ? 'Anfitriones' : 'Hosts'} (🇨🇦/🇲🇽/🇺🇸)
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Fixtures List Container */}
                    <div className="space-y-8">
                        {Object.keys(groupedMatches).length === 0 ? (
                            <div className="text-center py-16 bg-[#131d31] border border-slate-800 rounded-2xl">
                                <Compass size={40} className="text-slate-600 mx-auto mb-3" />
                                <p className="text-slate-400 font-medium">{t('noActiveTrades')}</p>
                                <p className="text-slate-500 text-xs mt-1">{lang === 'es' ? 'Intente ajustar sus criterios de búsqueda.' : 'Try adjusting your search criteria above.'}</p>
                            </div>
                        ) : (
                            (Object.entries(groupedMatches) as [string, ScheduleMatch[]][]).map(([date, matches], idx) => (
                                <div key={date} className="space-y-3">
                                    {/* Date Header */}
                                    <div className="flex items-center gap-2 pb-1">
                                        <Calendar size={15} className="text-blue-500" />
                                        <h3 className="text-xs font-black font-mono tracking-wider text-slate-400 uppercase">
                                            {date}
                                        </h3>
                                        <div className="h-[1px] bg-slate-800 flex-grow ml-2 animate-pulse" />
                                        <span className="text-[10px] font-mono text-slate-500 px-2 py-0.5 bg-[#131d31] rounded-full">
                      {matches.length} {matches.length === 1 ? 'match' : 'matches'}
                    </span>
                                    </div>

                                    {/* Cards Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {matches.map(match => {
                                            const isHostA = ['canada', 'mexico', 'usa', 'united states'].some(t => match.teamA.toLowerCase().includes(t));
                                            const isHostB = ['canada', 'mexico', 'usa', 'united states'].some(t => match.teamB.toLowerCase().includes(t));
                                            const pred = predictions[match.id];
                                            const isFinished = pred?.resolved === true || officialResults[match.id]?.resolved === true;
                                            const isGroupStage = match.stage === 'groups';
                                            const predictable = isPredictable(match);

                                            return (
                                                <motion.div
                                                    key={match.id}
                                                    initial={{ opacity: 0, y: 8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="bg-[#131d31] border border-slate-800 hover:border-slate-700/80 rounded-xl p-4 transition-all shadow-md relative overflow-hidden"
                                                >
                                                    {/* Top Border Stripe for Host Team Matches */}
                                                    {(isHostA || isHostB) && (
                                                        <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-rose-500/40 via-blue-500/40 to-emerald-500/40" />
                                                    )}

                                                    {/* Card Header Info */}
                                                    <div className="flex items-center justify-between mb-3.5 text-[11px] font-mono">
                            <span className="text-blue-400 font-bold tracking-wide">
                              {match.group}
                            </span>
                                                        {match.matchNumber && (
                                                            <span className="text-rose-400 font-black px-2 py-0.5 bg-rose-500/10 rounded border border-rose-500/20 uppercase">
                                {match.matchNumber}
                              </span>
                                                        )}
                                                    </div>

                                                    {/* Countdown block with blinking animation */}
                                                    {(() => {
                                                        const countdown = getCountdownInfo(match, currentTime);
                                                        if (!countdown.active) return null;
                                                        return (
                                                            <div className="mb-3 px-3 py-1.5 bg-rose-500/10 border border-rose-500/30 text-rose-400 rounded-lg flex items-center justify-between font-mono text-[10px] animate-pulse">
                                <span className="flex items-center gap-1.5 font-bold uppercase tracking-wider">
                                  <span className="h-2 w-2 rounded-full bg-rose-500 animate-ping inline-block" />
                                  ⚡ {lang === 'es' ? 'El juego comienza en' : 'Game starts in'}
                                </span>
                                                                <span className="font-extrabold tracking-widest bg-rose-500/25 px-2 py-0.5 rounded text-white text-xs">
                                  {countdown.label}
                                </span>
                                                            </div>
                                                        );
                                                    })()}

                                                    {/* Competitors Layout */}
                                                    <div className="flex items-center justify-between gap-1.5 sm:gap-4 py-1.5 matches-teams-list">

                                                        {/* Team A */}
                                                        <div className="flex items-center gap-1.5 sm:gap-2.5 w-[40%] min-w-0">
                                                            <TeamFlag name={match.teamA} className="w-5 h-3.5 sm:w-6 sm:h-4 shrink-0" />
                                                            <span className={`text-xs sm:text-sm font-semibold truncate ${
                                                                isHostA ? 'text-amber-200 font-black' : 'text-white'
                                                            }`}>
                                {match.teamA}
                              </span>
                                                        </div>

                                                        {/* Versus Badge / Active Pred Quick Display */}
                                                        <div className="w-[20%] text-center flex-shrink-0">
                                                            {officialResults[match.id] ? (
                                                                <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[9px] font-black px-1.5 py-0.5 rounded animate-fade-in" title="Definitive Official Result">
                                                                    {officialResults[match.id].actualScoreA} - {officialResults[match.id].actualScoreB}
                                                                </div>
                                                            ) : isFinished ? (
                                                                <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[9px] font-black px-1.5 py-0.5 rounded" title="Actual finished result">
                                                                    {pred?.actualScoreA ?? getDeterministicMatchResult(match.id).actualScoreA} - {pred?.actualScoreB ?? getDeterministicMatchResult(match.id).actualScoreB}
                                                                </div>
                                                            ) : pred ? (
                                                                <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 font-mono text-[9px] font-black px-1.5 py-0.5 rounded">
                                                                    {pred.scoreA} - {pred.scoreB}
                                                                </div>
                                                            ) : (
                                                                <span className="bg-[#090d16] text-[9px] font-bold font-mono text-slate-500 px-2 py-1 rounded border border-slate-800">
                                  VS
                                </span>
                                                            )}
                                                        </div>

                                                        {/* Team B */}
                                                        <div className="flex items-center justify-end gap-1.5 sm:gap-2.5 w-[40%] min-w-0 text-right">
                              <span className={`text-xs sm:text-sm font-semibold truncate ${
                                  isHostB ? 'text-amber-200 font-black' : 'text-white'
                              }`}>
                                {match.teamB}
                              </span>
                                                            <TeamFlag name={match.teamB} className="w-5 h-3.5 sm:w-6 sm:h-4 shrink-0" />
                                                        </div>

                                                    </div>

                                                    {/* Community Forecasts Info Bar */}
                                                    {(() => {
                                                        const stats = getCommunityPredictions(match.id);
                                                        if (stats.total === 0) return null;
                                                        return (
                                                            <div
                                                                onClick={() => setViewingCommunityMatch(match)}
                                                                className="mt-2.5 p-2 bg-[#090d16]/70 hover:bg-[#090d16] border border-slate-800/80 rounded-lg text-[9px] font-mono flex items-center justify-between gap-2 text-slate-400 cursor-pointer transition-all hover:border-slate-700 hover:text-white"
                                                                title="Click to view exactly who predicted what for this match"
                                                            >
                                                                <span className="text-[10px] text-indigo-400 font-bold flex items-center gap-1 shrink-0">👥 Community ({stats.total}):</span>
                                                                <div className="flex items-center gap-2 font-bold font-mono overflow-x-auto scrollbar-none">
                                                                    <span className="text-indigo-300 whitespace-nowrap">{match.teamA}: {stats.teamAWins}</span>
                                                                    <span className="text-slate-600 shrink-0">•</span>
                                                                    <span className="text-yellow-500 whitespace-nowrap">Draw: {stats.draws}</span>
                                                                    <span className="text-slate-600 shrink-0">•</span>
                                                                    <span className="text-rose-400 whitespace-nowrap">{match.teamB}: {stats.teamBWins}</span>
                                                                    <span className="text-emerald-400 border border-emerald-400/20 bg-emerald-500/5 px-1 py-0.5 rounded text-[8px] shrink-0">View</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}

                                                    {/* Footer Arena Info & Interactive Predict Button */}
                                                    <div className="mt-4 pt-3.5 border-t border-slate-800/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-[10px] font-mono text-slate-500">
                                                        <div className="flex items-center gap-1 min-w-0 pr-2">
                                                            <MapPin size={11} className="text-slate-400 flex-shrink-0" />
                                                            <span className="truncate">{match.stadium}</span>
                                                        </div>

                                                        {predictable ? (
                                                            <div className="flex flex-wrap items-center gap-1.5 justify-end">
                                                                {isFinished ? (
                                                                    <>
                                                                        {(() => {
                                                                            const hasHighlight = VIDEO_HIGHLIGHTS.find(h => h.matchId === match.id);
                                                                            if (hasHighlight) {
                                                                                return (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => setPlayingVideoHighlight(hasHighlight)}
                                                                                        className="px-2 py-1 text-[9px] font-black rounded border border-red-500/30 bg-red-650/10 text-red-500 hover:bg-red-600 hover:text-white transition-all flex items-center gap-1 cursor-pointer uppercase tracking-tight"
                                                                                        title="Watch video highlights"
                                                                                    >
                                                                                        🎥 Highlights
                                                                                    </button>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })()}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setShowingStatsMatch(match)}
                                                                            className="px-2.5 py-1 text-[9px] font-black rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-slate-950 transition-all flex items-center gap-1 cursor-pointer uppercase tracking-tight"
                                                                        >
                                                                            <BarChart3 size={11} /> Stats
                                                                        </button>
                                                                        {pred && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setSharingMatch(match);
                                                                                    setSharingType('stats');
                                                                                }}
                                                                                className="px-2 py-1 text-[9px] font-black rounded border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-705 hover:text-white transition-all flex items-center gap-1 cursor-pointer uppercase tracking-tight"
                                                                                title="Share statistics on social networks"
                                                                            >
                                                                                <Share2 size={11} /> Share
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                ) : pred ? (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleOpenPredict(match)}
                                                                            className="px-2.5 py-1 text-[9px] font-black rounded border border-yellow-500/30 bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20 transition-all flex items-center gap-1 cursor-pointer uppercase tracking-tight"
                                                                        >
                                                                            🔮 Edit Predict
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setSharingMatch(match);
                                                                                setSharingType('predict');
                                                                            }}
                                                                            className="px-2 py-1 text-[9px] font-black rounded border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-705 hover:text-white transition-all flex items-center gap-1 cursor-pointer uppercase tracking-tight"
                                                                            title="Share prediction on social networks"
                                                                        >
                                                                            <Share2 size={11} /> Share
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleOpenPredict(match)}
                                                                        className="px-2.5 py-1 text-[9px] font-black rounded border border-blue-500/40 bg-blue-600/20 text-blue-300 hover:bg-blue-500 hover:text-white hover:border-transparent transition-all flex items-center gap-1 cursor-pointer uppercase tracking-tight"
                                                                    >
                                                                        🔮 Predict
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-wrap items-center gap-1.5 justify-end font-bold">
                                                                <span className="text-rose-400 border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 rounded text-[8px] font-black uppercase">Started</span>
                                                                {pred && (
                                                                    <span className="text-slate-300 border border-slate-700 bg-slate-800 px-2 py-0.5 rounded text-[8px] font-black tracking-tight" title="Your locked prediction">
                                    🔮 {pred.scoreA} - {pred.scoreB}
                                  </span>
                                                                )}
                                                                {(isFinished || officialResults[match.id]) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowingStatsMatch(match)}
                                                                        className="px-2 py-0.5 text-[8px] font-black rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-slate-950 transition-all flex items-center gap-0.5 cursor-pointer uppercase tracking-tight animate-fade-in"
                                                                    >
                                                                        Stats
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Admin Options Panel inside the card */}
                                                    {isSuperUser && (
                                                        <div className="mt-3.5 pt-3 border-t border-slate-800/40 flex flex-col gap-2">
                                                            {adminEditingMatchId === match.id ? (
                                                                <div className="bg-slate-900 border border-indigo-500/20 rounded-xl p-3 space-y-3 font-mono text-left animate-fade-in text-[11px]" id={`admin-edit-panel-${match.id}`}>
                                                                    <div className="flex items-center justify-between border-b border-slate-800 pb-1.5 mb-1.55">
                                                                        <span className="text-[10px] font-black text-indigo-400 uppercase tracking-wider flex items-center gap-1">🔒 Definitive Official Score</span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setAdminEditingMatchId(null)}
                                                                            className="text-slate-500 hover:text-white cursor-pointer"
                                                                        >
                                                                            <X size={12} />
                                                                        </button>
                                                                    </div>

                                                                    <div className="mb-3 bg-[#0a0f1d] p-2.5 rounded-lg border border-indigo-500/10 flex flex-col gap-1.5">
                                                                        <div className="flex items-center justify-between gap-2">
                                                                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Avoid manual entry:</span>
                                                                            <button
                                                                                type="button"
                                                                                disabled={isAutofilling}
                                                                                onClick={() => handleAutofillFromAPI(match.teamA, match.teamB)}
                                                                                className="px-2 py-1 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-900 disabled:text-indigo-400 text-[9px] font-black tracking-wider uppercase text-white rounded transition-all cursor-pointer flex items-center gap-1.5 shadow-sm border border-indigo-400/20"
                                                                            >
                                                                                {isAutofilling ? (
                                                                                    <>
                                                                                        <RefreshCw size={10} className="animate-spin" />
                                                                                        Syncing live...
                                                                                    </>
                                                                                ) : (
                                                                                    "🔌 API-Sports Autofill"
                                                                                )}
                                                                            </button>
                                                                        </div>
                                                                        {autofillError && (
                                                                            <p className="text-[8px] text-rose-400 font-bold font-mono">{autofillError}</p>
                                                                        )}
                                                                    </div>
                                                                    <div className="grid grid-cols-2 gap-2">
                                                                        <div>
                                                                            <label className="block text-[8px] text-slate-500 uppercase font-bold mb-1 truncate">{match.teamA} Score</label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                value={adminScoreA}
                                                                                onChange={(e) => setAdminScoreA(Math.max(0, parseInt(e.target.value) || 0))}
                                                                                className="w-full bg-[#090d16] border border-slate-850 rounded p-1 text-center text-xs text-yellow-400 font-bold"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-[8px] text-slate-500 uppercase font-bold mb-1 truncate">{match.teamB} Score</label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                value={adminScoreB}
                                                                                onChange={(e) => setAdminScoreB(Math.max(0, parseInt(e.target.value) || 0))}
                                                                                className="w-full bg-[#090d16] border border-slate-850 rounded p-1 text-center text-xs text-yellow-400 font-bold"
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="border-t border-slate-800/60 pt-2 mt-2 space-y-2">
                                                                        <div className="text-[9px] font-black text-slate-400 font-mono uppercase tracking-wider mb-2 flex items-center gap-1">
                                                                            📊 Customize Detailed Matchday Statistics
                                                                        </div>

                                                                        {/* Stat Row Header */}
                                                                        <div className="grid grid-cols-3 gap-2 text-center text-[8px] text-slate-500 uppercase font-black font-mono">
                                                                            <div className="truncate">{match.teamA}</div>
                                                                            <div className="text-indigo-400">STATISTIC</div>
                                                                            <div className="truncate">{match.teamB}</div>
                                                                        </div>

                                                                        {/* Stat inputs */}
                                                                        {[
                                                                            { label: 'Total Shots', keyA: adminShotsTotalA, setA: setAdminShotsTotalA, keyB: adminShotsTotalB, setB: setAdminShotsTotalB },
                                                                            { label: 'Shots on Target', keyA: adminShotsTargetA, setA: setAdminShotsTargetA, keyB: adminShotsTargetB, setB: setAdminShotsTargetB },
                                                                            { label: 'Passing Accuracy %', keyA: adminPassingA, setA: setAdminPassingA, keyB: adminPassingB, setB: setAdminPassingB },
                                                                            { label: 'Corners', keyA: adminCornersA, setA: setAdminCornersA, keyB: adminCornersB, setB: setAdminCornersB, isCorners: true },
                                                                            { label: 'Fouls Committed', keyA: adminFoulsA, setA: setAdminFoulsA, keyB: adminFoulsB, setB: setAdminFoulsB },
                                                                            { label: 'Yellow Cards', keyA: adminYellowA, setA: setAdminYellowA, keyB: adminYellowB, setB: setAdminYellowB },
                                                                            { label: 'Red Cards', keyA: adminRedA, setA: setAdminRedA, keyB: adminRedB, setB: setAdminRedB },
                                                                            { label: 'Possession %', keyA: adminPossessionA, setA: setAdminPossessionA, keyB: adminPossessionB, setB: setAdminPossessionB, isPoss: true },
                                                                        ].map((row, idx) => (
                                                                            <div key={idx} className="grid grid-cols-3 gap-2 items-center">
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    max={row.isPoss ? 100 : undefined}
                                                                                    value={row.keyA}
                                                                                    onChange={(e) => {
                                                                                        const val = Math.max(0, parseInt(e.target.value) || 0);
                                                                                        if (row.isPoss) {
                                                                                            const safeA = Math.min(100, val);
                                                                                            row.setA(safeA);
                                                                                            row.setB(100 - safeA);
                                                                                        } else if (row.isCorners) {
                                                                                            row.setA(val);
                                                                                            setAdminCorners(val + adminCornersB);
                                                                                        } else {
                                                                                            row.setA(val);
                                                                                        }
                                                                                    }}
                                                                                    className="w-full bg-[#090d16] border border-slate-850 rounded p-1 text-[10px] text-white font-bold text-center"
                                                                                />
                                                                                <div className="text-[9px] text-slate-400 text-center uppercase tracking-tight py-0.5 font-bold truncate">
                                                                                    {row.label}
                                                                                </div>
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    max={row.isPoss ? 100 : undefined}
                                                                                    value={row.keyB}
                                                                                    onChange={(e) => {
                                                                                        const val = Math.max(0, parseInt(e.target.value) || 0);
                                                                                        if (row.isPoss) {
                                                                                            const safeB = Math.min(100, val);
                                                                                            row.setB(safeB);
                                                                                            row.setA(100 - safeB);
                                                                                        } else if (row.isCorners) {
                                                                                            row.setB(val);
                                                                                            setAdminCorners(adminCornersA + val);
                                                                                        } else {
                                                                                            row.setB(val);
                                                                                        }
                                                                                    }}
                                                                                    className="w-full bg-[#090d16] border border-slate-850 rounded p-1 text-[10px] text-white font-bold text-center"
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    <div className="flex gap-2 justify-end pt-1">
                                                                        {officialResults[match.id] && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={async () => {
                                                                                    if (confirm("Clear official result for this match?")) {
                                                                                        await deleteOfficialResult(match.id);
                                                                                        setAdminEditingMatchId(null);
                                                                                    }
                                                                                }}
                                                                                className="px-2 py-1 bg-red-600/20 text-red-400 border border-red-500/30 text-[9px] font-black rounded uppercase hover:bg-red-600 hover:text-white transition-all cursor-pointer font-mono"
                                                                            >
                                                                                Clear
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            onClick={async () => {
                                                                                await saveOfficialResult(match.id, adminScoreA, adminScoreB, adminCorners);
                                                                                setAdminEditingMatchId(null);
                                                                            }}
                                                                            className="px-3 py-1 bg-indigo-600 text-white text-[9px] font-black rounded uppercase hover:bg-indigo-500 transition-all cursor-pointer font-mono"
                                                                        >
                                                                            Save & Lock
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-[9px] text-indigo-400 font-bold font-mono">⚡ Super User Admin Action</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleOpenAdminEdit(match)}
                                                                        className="px-2 py-1 text-[9px] font-black rounded-lg border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500 hover:text-slate-950 transition-all cursor-pointer uppercase font-mono"
                                                                    >
                                                                        {officialResults[match.id] ? "✏️ Edit Final Score" : "🔒 Set Final Score"}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* VIEW: CONTESTS AND PREDICTIONS HUB */}
            {activeTab === 'predictions' && (
                <div className="space-y-6">
                    {/* Rules and reward breakdown bento block */}
                    <div className="bg-[#1e1b4b]/35 border border-blue-900/40 rounded-2xl p-5 relative overflow-hidden shadow-xl grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
                        <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none translate-x-12 translate-y-12">
                            <Trophy size={190} className="text-blue-500" />
                        </div>

                        <div className="md:col-span-12 lg:col-span-6 space-y-1.5 z-10">
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-[9px] font-bold font-mono tracking-wider rounded-full uppercase mb-1">
                <Sparkles size={10} className="fill-indigo-300/20" /> Sticker Rewards Program
              </span>
                            <h3 className="text-sm font-black text-white uppercase tracking-tight">How predictions earn Sticker Packs</h3>
                            <p className="text-xs text-slate-400">
                                Put your football expertise to the test. Correctly analyze game scorelines, total matches goals, or critical corner counts to raise sticker pack collections!
                            </p>
                        </div>

                        <div className="md:col-span-12 lg:col-span-6 grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 z-10 font-mono text-[11px]">
                            <div className="bg-[#131d31]/80 rounded-xl p-3 border border-slate-800 text-center flex items-center justify-between sm:flex-col sm:justify-between sm:h-24">
                                <span className="text-slate-500 text-[9px] uppercase tracking-tighter sm:text-center block text-left">🎯 Correct score</span>
                                <span className="text-white text-md sm:text-lg font-black my-1 sm:my-1 font-mono">+3 PACKS</span>
                                <span className="text-[8px] text-slate-500 sm:text-center hidden sm:block">Exact score</span>
                            </div>
                            <div className="bg-[#131d31]/80 rounded-xl p-2.5 sm:p-3 border border-slate-800 text-center flex items-center justify-between sm:flex-col sm:justify-between sm:h-24">
                                <span className="text-slate-500 text-[9px] uppercase tracking-tighter sm:text-center block text-left">⚽ Goals O/U 2.5</span>
                                <span className="text-white text-md sm:text-lg font-black my-1 sm:my-1 font-mono">+1 PACK</span>
                                <span className="text-[8px] text-slate-500 sm:text-center hidden sm:block">O/U 2.5</span>
                            </div>
                            <div className="bg-[#131d31]/80 rounded-xl p-2.5 sm:p-3 border border-slate-800 text-center flex items-center justify-between sm:flex-col sm:justify-between sm:h-24">
                                <span className="text-slate-500 text-[9px] uppercase tracking-tighter sm:text-center block text-left">🚩 Total corners</span>
                                <span className="text-white text-md sm:text-lg font-black my-1 sm:my-1 font-mono">+1 PACK</span>
                                <span className="text-[8px] text-slate-500 sm:text-center hidden sm:block">Within 1 corner</span>
                            </div>
                        </div>
                    </div>

                    {/* User Prediction Summary Analytics Report */}
                    <PredictionStatsReport predictions={predictions || {}} lang={activeLang} />

                    {/* Predict state filters */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setFilterMode('all')}
                                className={`text-xs px-3.5 py-2 font-bold rounded-xl border cursor-pointer transition-all ${
                                    filterMode === 'all'
                                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg'
                                        : 'bg-[#131d31] border-slate-800 hover:border-slate-700 text-slate-400'
                                }`}
                            >
                                All Predictions ({Object.keys(predictions).length})
                            </button>
                            <button
                                onClick={() => setFilterMode('locked')}
                                className={`text-xs px-3.5 py-2 font-bold rounded-xl border cursor-pointer transition-all ${
                                    filterMode === 'locked'
                                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg'
                                        : 'bg-[#131d31] border-slate-800 hover:border-slate-700 text-slate-400'
                                }`}
                            >
                                Active / Unresolved ({Object.values(predictions).filter((e: any) => !e.resolved).length})
                            </button>
                            <button
                                onClick={() => setFilterMode('resolved')}
                                className={`text-xs px-3.5 py-2 font-bold rounded-xl border cursor-pointer transition-all ${
                                    filterMode === 'resolved'
                                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg'
                                        : 'bg-[#131d31] border-slate-800 hover:border-slate-700 text-slate-400'
                                }`}
                            >
                                History & Settled ({Object.values(predictions).filter((e: any) => e.resolved).length})
                            </button>
                        </div>

                        {unstartedPredictionsCount > 0 && (
                            <button
                                onClick={() => setShowResetConfirm(true)}
                                className="text-xs px-3.5 py-2 font-bold rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white cursor-pointer transition-all flex items-center justify-center gap-1.5 shrink-0"
                                title="Reset all active predictions that have not been settled yet"
                            >
                                <span>🗑️ Reset Active ({unstartedPredictionsCount})</span>
                            </button>
                        )}
                    </div>

                    {/* Predictions lists */}
                    {Object.keys(predictions).length === 0 ? (
                        <div className="text-center py-16 bg-[#131d31] border border-slate-800 rounded-2xl">
                            <Sparkles size={36} className="text-indigo-400 mx-auto mb-3 animate-pulse" />
                            <p className="text-slate-400 font-medium">No predictions submitted yet.</p>
                            <p className="text-slate-500 text-xs mt-1 max-w-sm mx-auto">
                                Head over to the "Schedules & Fixtures" tab and select matches with 🔮 predict badges to enter forecasts!
                            </p>
                            <button
                                onClick={() => setActiveTab('fixtures')}
                                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white font-bold text-xs rounded-xl hover:bg-blue-500 transition-all cursor-pointer"
                            >
                                Go to Schedules <ChevronRight size={14} />
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {SHEDULE_DATA.filter((m) => {
                                const p = predictions[m.id];
                                if (!p) return false;
                                if (filterMode === 'locked') return !p.resolved;
                                if (filterMode === 'resolved') return p.resolved;
                                return true;
                            }).map((match) => {
                                const pred = predictions[match.id];

                                return (
                                    <div key={match.id} className="bg-[#131d31] border border-slate-800 rounded-2xl p-5 relative overflow-hidden flex flex-col justify-between shadow-md">
                                        {/* Background details with Active/Finished Status Pill */}
                                        <div className="absolute right-4 top-3.5 flex items-center gap-1.5 text-[9px] font-mono uppercase font-black">
                                            <span className="text-indigo-400 opacity-60 mr-1">{match.group}</span>
                                            {pred.status === 'finished' || pred.resolved ? (
                                                <span className="px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-md">Finished</span>
                                            ) : (
                                                <span className="px-1.5 py-0.5 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-md animate-pulse">Active</span>
                                            )}
                                        </div>

                                        {/* Countdown block with blinking animation */}
                                        {(() => {
                                            const countdown = getCountdownInfo(match, currentTime);
                                            if (!countdown.active) return null;
                                            return (
                                                <div className="mb-3.5 mt-8 px-3 py-1.5 bg-rose-500/10 border border-rose-500/30 text-rose-400 rounded-lg flex items-center justify-between font-mono text-[10px] animate-pulse">
                          <span className="flex items-center gap-1.5 font-bold uppercase tracking-wider">
                            <span className="h-2 w-2 rounded-full bg-rose-500 animate-ping inline-block" />
                            ⚡ {lang === 'es' ? 'El juego comienza en' : 'Game starts in'}
                          </span>
                                                    <span className="font-extrabold tracking-widest bg-rose-500/25 px-2 py-0.5 rounded text-white text-xs">
                            {countdown.label}
                          </span>
                                                </div>
                                            );
                                        })()}

                                        {/* Flags row */}
                                        <div className="flex items-center justify-between gap-1.5 py-1 mb-4">
                                            <div className="flex items-center gap-1.5 min-w-0 w-[42%]">
                                                <TeamFlag name={match.teamA} className="w-7 h-4.5 sm:w-8 sm:h-5.5 shrink-0" />
                                                <span className="text-xs font-black text-white truncate">{match.teamA}</span>
                                            </div>
                                            <span className="text-slate-600 font-mono text-[8.5px] uppercase px-1.5 py-0.5 bg-slate-900 rounded shrink-0">VS</span>
                                            <div className="flex items-center justify-end gap-1.5 min-w-0 w-[42%] text-right">
                                                <span className="text-xs font-black text-white truncate">{match.teamB}</span>
                                                <TeamFlag name={match.teamB} className="w-7 h-4.5 sm:w-8 sm:h-5.5 shrink-0" />
                                            </div>
                                        </div>

                                        {/* Predictions Grid */}
                                        <div className="grid grid-cols-3 gap-2 py-3 border-y border-slate-800/80 mb-4 bg-slate-900/40 rounded-xl px-2.5 text-center font-mono">
                                            <div>
                                                <span className="text-[8px] text-slate-500 uppercase font-bold">Predicted score</span>
                                                <div className="text-sm font-black text-yellow-400 tracking-widest mt-0.5">
                                                    {pred.scoreA} - {pred.scoreB}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-[8px] text-slate-500 uppercase font-bold">Goals (O/U 2.5)</span>
                                                <div className="text-xs font-bold text-indigo-300 mt-1 uppercase">
                                                    {pred.goalsOverUnder === 'over' ? 'OVER 2.5' : 'UNDER 2.5'}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-[8px] text-slate-500 uppercase font-bold"> corners</span>
                                                <div className="text-xs font-black text-yellow-500 mt-1 uppercase">
                                                    {pred.corners} 🚩
                                                </div>
                                            </div>
                                        </div>

                                        {/* Settle Action / Finished state results */}
                                        <div className="flex items-center justify-between mt-1 text-[11px]">
                                            {pred.resolved ? (
                                                <div className="w-full space-y-2">
                                                    <div className="flex items-center justify-between text-emerald-400 bg-emerald-500/5 px-2.5 py-1.5 rounded-lg border border-emerald-500/10 mb-2">
                                                        <span className="font-bold flex items-center gap-1"><CheckCircle2 size={12} /> Settled results</span>
                                                        <span className="font-mono font-black text-[10px]">+{pred.pointsWon ?? pred.packsWon ?? 0} PTS & +{pred.packsWon ?? 0} packs won!</span>
                                                    </div>

                                                    <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-mono text-slate-400 py-1 bg-slate-900/20 rounded-md">
                                                        <div>
                                                            <span>Actual Score</span>
                                                            <div className="font-black text-white mt-0.5">
                                                                {pred.actualScoreA} - {pred.actualScoreB}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span>Actual Goals</span>
                                                            <div className="font-black text-white mt-0.5">
                                                                {(pred.actualScoreA + pred.actualScoreB) > 2.5 ? 'Over 2.5' : 'Under 2.5'}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span>Actual Corners</span>
                                                            <div className="font-black text-white mt-0.5">
                                                                {pred.actualCorners} 🚩
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-800/40 mt-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowingStatsMatch(match)}
                                                            className="px-2.5 py-1 text-[9px] font-black rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-slate-950 transition-all flex items-center gap-1 cursor-pointer uppercase tracking-tight"
                                                        >
                                                            <BarChart3 size={11} /> Game Stats
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSharingMatch(match);
                                                                setSharingType('stats');
                                                            }}
                                                            className="px-2.5 py-1 text-[9px] font-black rounded-lg border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-705 hover:text-white transition-all flex items-center gap-1 cursor-pointer uppercase tracking-tight"
                                                        >
                                                            <Share2 size={11} /> Share Stats
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (officialResults && officialResults[match.id]) ? (
                                                <div className="w-full space-y-2 mt-2 font-mono text-left">
                                                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 rounded-xl relative overflow-hidden">
                                                        <h4 className="text-[10px] font-black uppercase tracking-wider mb-2 flex items-center gap-1 text-white">
                                                            🏆 Official Match Result Declared
                                                        </h4>
                                                        <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-mono py-1.5 bg-slate-900/40 rounded-lg border border-slate-850">
                                                            <div>
                                                                <span className="text-slate-505 text-[8px] uppercase font-bold">Goals</span>
                                                                <div className="font-black text-yellow-400 mt-0.5">
                                                                    {officialResults[match.id].actualScoreA} - {officialResults[match.id].actualScoreB}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <span className="text-slate-505 text-[8px] uppercase font-bold">Goals O/U</span>
                                                                <div className="font-black text-white mt-0.5 uppercase text-[9px]">
                                                                    {officialResults[match.id].actualGoalsOverUnder === 'over' ? 'Over 2.5' : 'Under 2.5'}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <span className="text-slate-505 text-[8px] uppercase font-bold">Corners</span>
                                                                <div className="font-black text-yellow-500 mt-0.5">
                                                                    {officialResults[match.id].actualCorners} 🚩
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Compute how many packs won */}
                                                        {(() => {
                                                            const oRes = officialResults[match.id];
                                                            const isScoreCorrect = (oRes.actualScoreA === pred.scoreA && oRes.actualScoreB === pred.scoreB);
                                                            const isGoalsCorrect = (oRes.actualGoalsOverUnder === pred.goalsOverUnder);
                                                            const isCornersCorrect = Math.abs(pred.corners - oRes.actualCorners) <= 1;

                                                            let pointsWon = 0;
                                                            if (isScoreCorrect) pointsWon += 5;
                                                            if (isGoalsCorrect) pointsWon += 2;
                                                            if (isCornersCorrect) pointsWon += 3;

                                                            let packsWon = 0;
                                                            if (isScoreCorrect) packsWon += 3;
                                                            if (isCornersCorrect) packsWon += 2;
                                                            if (isGoalsCorrect) packsWon += 1;

                                                            return (
                                                                <div className="mt-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                                                    <div className="text-[9px] font-mono text-slate-400 flex flex-col gap-0.5">
                                    <span className="font-semibold text-slate-300">
                                      {isScoreCorrect ? '🎯 Score exact (+5)' : '❌ Score incorrect'}
                                    </span>
                                                                        <span className="font-semibold text-slate-300">
                                      {isGoalsCorrect ? '⚽ Goals O/U correct (+2)' : '❌ Goals incorrect'}
                                    </span>
                                                                        <span className="font-semibold text-slate-300">
                                      {isCornersCorrect ? '🚩 Corners correct (+3)' : '❌ Corners incorrect'}
                                    </span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            // Save prediction as resolved
                                                                            savePrediction(match.id, {
                                                                                ...pred,
                                                                                resolved: true,
                                                                                status: 'finished',
                                                                                actualScoreA: oRes.actualScoreA,
                                                                                actualScoreB: oRes.actualScoreB,
                                                                                actualCorners: oRes.actualCorners,
                                                                                pointsWon: pointsWon,
                                                                                packsWon: packsWon
                                                                            });
                                                                            if (packsWon > 0) {
                                                                                setPacks(p => p + packsWon);
                                                                            }
                                                                        }}
                                                                        className="px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-slate-950 font-black text-[10px] rounded-lg shadow-lg transition-all animate-pulse uppercase cursor-pointer shrink-0"
                                                                    >
                                                                        Claim {packsWon} Packs 🎁
                                                                    </button>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between w-full">
                                                    <div className="flex items-center gap-1.5 text-slate-400">
                                                        <Hourglass size={14} className="text-blue-400 animate-spin" />
                                                        <span className="font-mono font-bold uppercase text-[9px]">Match day active</span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSharingMatch(match);
                                                                setSharingType('predict');
                                                            }}
                                                            className="bg-slate-800 hover:bg-slate-705 border border-slate-700 text-slate-305 px-3 py-1.5 text-[9px] font-black rounded-xl flex items-center gap-1 cursor-pointer transition-all uppercase tracking-tight"
                                                        >
                                                            <Share2 size={11} /> Share Pred
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* VIEW: NEXT 24 HOURS PREDICTIONS */}
            {activeTab === 'next24' && (
                <div className="space-y-6">
                    {/* Header Bento block */}
                    <div className="bg-[#090d16]/80 border border-slate-800 rounded-2xl p-5 relative overflow-hidden shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 animate-fade-in">
                        <div className="space-y-1.5 z-10">
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[9px] font-bold font-mono tracking-wider rounded-full uppercase">
                <Hourglass size={11} className="animate-spin text-emerald-400" /> Kicking off next 24 Hours
              </span>
                            <h3 className="text-sm font-black text-white uppercase tracking-tight">Community Match Forecasts</h3>
                            <p className="text-xs text-slate-400 max-w-xl">
                                Compare and inspect exactly what everyone predicted for all the upcoming battles schedules in the next 24 hours. Analyze community winning ratios in real-time!
                            </p>
                        </div>

                        <div className="bg-[#131d31] border border-slate-800 rounded-xl p-3 flex items-center gap-2.5 font-mono text-[10px] text-slate-400">
                            <span className="text-emerald-400 animate-ping h-2 w-2 rounded-full bg-emerald-400 inline-block shrink-0" />
                            <span>Current Clock: <strong>11 June 2026</strong></span>
                        </div>
                    </div>

                    {/* List of matches with predictions */}
                    {(() => {
                        const now = new Date();
                        const isMatchInNext24Hours = (m: ScheduleMatch): boolean => {
                            const mDate = parseMatchDateStringToDate(m.date);
                            if (isNaN(mDate.getTime())) return false;
                            const timeDiff = mDate.getTime() - now.getTime();
                            return timeDiff >= -2 * 60 * 60 * 1000 && timeDiff <= 24 * 60 * 60 * 1000;
                        };

                        let matches = SHEDULE_DATA.filter(isMatchInNext24Hours);
                        const isStrict24HourEmpty = matches.length === 0;

                        if (isStrict24HourEmpty) {
                            // Fallback to June 11, 12 and 13, 2026 matches to always represent predictions nicely
                            matches = SHEDULE_DATA.filter(m => m.date.includes("11 June 2026") || m.date.includes("12 June 2026") || m.date.includes("13 June 2026"));
                        }

                        if (matches.length === 0) {
                            return (
                                <div className="text-center py-16 bg-[#131d31] border border-slate-800 rounded-2xl">
                                    <Calendar size={36} className="text-slate-655 mx-auto mb-3 animate-pulse" />
                                    <p className="text-slate-400 font-medium">No matches scheduled in this block.</p>
                                    <p className="text-slate-500 text-xs mt-1">Please explore the full match schedule of the tournament.</p>
                                </div>
                            );
                        }

                        return (
                            <div className="space-y-8">
                                {isStrict24HourEmpty && (
                                    <div className="text-[11px] font-mono bg-amber-500/5 text-amber-300 border border-amber-500/20 px-3.5 py-2.5 rounded-xl flex items-center gap-2">
                                        <Info size={14} className="shrink-0 text-amber-400" />
                                        <span>No matches strictly within the nearest 24 hours of your local clock. Displaying the closest scheduled matches for the 2026 tournament!</span>
                                    </div>
                                )}

                                {matches.map(match => {
                                    const stats = getCommunityPredictions(match.id);
                                    const pred = predictions[match.id];
                                    const matchStarted = isMatchStarted(match, officialResults);

                                    // Calculate percentages for the bar chart
                                    const totalCount = stats.total || 0;
                                    const winAPct = totalCount > 0 ? Math.round((stats.teamAWins / totalCount) * 100) : 0;
                                    const drawPct = totalCount > 0 ? Math.round((stats.draws / totalCount) * 100) : 0;
                                    const winBPct = totalCount > 0 ? Math.round((stats.teamBWins / totalCount) * 100) : 0;

                                    return (
                                        <div key={match.id} className="bg-[#131d31] border border-slate-800 rounded-2xl overflow-hidden shadow-xl p-6 space-y-5">

                                            {/* Match Header Bar */}
                                            <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4 pb-4 border-b border-slate-800/60">
                                                <div className="min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-1.5">
                                                        <span>{match.group}</span>
                                                        <span className="text-slate-650">•</span>
                                                        <span className="truncate">{match.stadium}</span>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                            <TeamFlag name={match.teamA} className="w-6 h-4 sm:w-7 sm:h-5 shrink-0" />
                                                            <span className="text-sm font-black text-white uppercase tracking-tight truncate">{match.teamA}</span>
                                                        </div>
                                                        <span className="text-slate-500 font-normal px-1 shrink-0">vs</span>
                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                            <span className="text-sm font-black text-white uppercase tracking-tight truncate">{match.teamB}</span>
                                                            <TeamFlag name={match.teamB} className="w-6 h-4 sm:w-7 sm:h-5 shrink-0" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3.5 font-mono text-xs">
                                                    {/* Kickoff label */}
                                                    <div className="flex flex-col items-end">
                                                        <span className="text-[9px] text-slate-500 uppercase">Kickoff time</span>
                                                        <span className="text-slate-300 font-bold text-[11px] mt-0.5">{match.date}</span>
                                                    </div>

                                                    {/* Quick user interaction status */}
                                                    {pred ? (
                                                        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 font-mono text-[9px] font-black px-2.5 py-1 rounded">
                                                            YOUR PRED: {pred.scoreA} - {pred.scoreB}
                                                        </div>
                                                    ) : !matchStarted ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenPredict(match)}
                                                            className="px-3 py-1.5 text-[9px] font-black rounded-lg border border-blue-500/40 bg-blue-600/20 text-blue-300 hover:bg-blue-500 hover:text-white hover:border-transparent transition-all cursor-pointer uppercase tracking-tight"
                                                        >
                                                            🔮 Predict
                                                        </button>
                                                    ) : (
                                                        <span className="text-rose-400 border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 rounded text-[8px] font-black uppercase">Lock</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Countdown block with blinking animation */}
                                            {(() => {
                                                const countdown = getCountdownInfo(match, currentTime);
                                                if (!countdown.active) return null;
                                                return (
                                                    <div className="px-3 py-1.5 bg-rose-500/10 border border-rose-500/30 text-rose-400 rounded-lg flex items-center justify-between font-mono text-[10px] animate-pulse">
                            <span className="flex items-center gap-1.5 font-bold uppercase tracking-wider">
                              <span className="h-2 w-2 rounded-full bg-rose-500 animate-ping inline-block" />
                              ⚡ {lang === 'es' ? 'El juego comienza en' : 'Game starts in'}
                            </span>
                                                        <span className="font-extrabold tracking-widest bg-rose-500/25 px-2 py-0.5 rounded text-white text-xs">
                              {countdown.label}
                            </span>
                                                    </div>
                                                );
                                            })()}

                                            {/* Community Forecast Aggregated opinion bar chart */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center text-[10px] font-mono text-slate-400">
                                                    <span className="font-bold flex items-center gap-1">📊 Sentiment ({totalCount} Forecasts)</span>
                                                    <div className="flex gap-3 text-[9px]">
                                                        <span className="text-indigo-400">{match.teamA}: {winAPct}%</span>
                                                        <span className="text-yellow-500">Draw: {drawPct}%</span>
                                                        <span className="text-rose-400">{match.teamB}: {winBPct}%</span>
                                                    </div>
                                                </div>

                                                {totalCount === 0 ? (
                                                    <div className="bg-slate-900/30 text-slate-500 font-mono text-[10px] text-center py-2 rounded-lg border border-slate-800/40">
                                                        Be the first user to submit a prediction!
                                                    </div>
                                                ) : (
                                                    <div className="h-2 rounded-full overflow-hidden flex bg-slate-900 border border-slate-800">
                                                        {winAPct > 0 && <div style={{ width: `${winAPct}%` }} className="bg-gradient-to-r from-blue-600 to-indigo-500 transition-all" title={`${match.teamA} wins`} />}
                                                        {drawPct > 0 && <div style={{ width: `${drawPct}%` }} className="bg-yellow-500 transition-all" title="Draw" />}
                                                        {winBPct > 0 && <div style={{ width: `${winBPct}%` }} className="bg-gradient-to-r from-rose-500 to-red-500 transition-all" title={`${match.teamB} wins`} />}
                                                    </div>
                                                )}
                                            </div>

                                            {/* User Feed for this match */}
                                            <div className="space-y-3 pt-2">
                        <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest block font-bold">
                          All submitted forecasts ({stats.list.length})
                        </span>

                                                {stats.list.length === 0 ? (
                                                    <div className="text-center py-6 text-slate-600 font-mono text-xs">
                                                        No collector predictions have been loaded for this matchup.
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                        {stats.list.map((item, index) => {
                                                            const isMe = item.userId === user?.uid;
                                                            return (
                                                                <div key={item.userId + '_' + index} className="bg-[#090d16]/40 border border-slate-800/80 rounded-xl p-3 flex justify-between items-center gap-3">
                                                                    {/* Profile */}
                                                                    <div className="flex items-center gap-2 min-w-0">
                                                                        <Avatar photoURL={item.photoURL} email={item.email} size="sm" />
                                                                        <div className="flex flex-col min-w-0">
                                      <span className={`font-bold text-[10px] truncate ${isMe ? 'text-amber-400 font-extrabold' : 'text-slate-100'}`}>
                                        {item.displayName}
                                      </span>
                                                                            {item.email && item.email !== 'Guest' && (
                                                                                <span className="text-[8px] font-mono text-slate-500 truncate">{maskEmail(item.email)}</span>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* Prediction details */}
                                                                    <div className="flex items-center gap-2.5 font-mono text-center">
                                                                        <div className="flex flex-col">
                                                                            <span className="text-[6px] text-slate-500 uppercase">Score</span>
                                                                            <span className="text-[11px] font-extrabold text-yellow-500">{item.scoreA} - {item.scoreB}</span>
                                                                        </div>
                                                                        <div className="text-slate-700">|</div>
                                                                        <div className="flex flex-col">
                                                                            <span className="text-[6px] text-slate-500 uppercase">Corners</span>
                                                                            <span className="text-[11px] font-bold text-indigo-400">{item.corners} 🚩</span>
                                                                        </div>
                                                                        <div className="text-slate-700">|</div>
                                                                        <div className="flex flex-col">
                                                                            <span className="text-[6px] text-slate-500 uppercase">Goals</span>
                                                                            <span className="text-[11px] font-bold text-slate-300 uppercase">{item.goalsOverUnder === 'over' ? 'O2.5' : 'U2.5'}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Admin Options Panel inside the card */}
                                            {isSuperUser && (
                                                <div className="mt-4 pt-3.5 border-t border-slate-800/40 flex flex-col gap-2">
                                                    {adminEditingMatchId === match.id ? (
                                                        <div className="bg-slate-900 border border-indigo-500/20 rounded-xl p-3.5 space-y-3 font-mono text-left animate-fade-in text-[11px]" id={`admin-edit-panel-next24-${match.id}`}>
                                                            <div className="flex items-center justify-between border-b border-slate-800 pb-1.5 mb-1.55">
                                                                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-wider flex items-center gap-1">🔒 Definitive Official Score</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setAdminEditingMatchId(null)}
                                                                    className="text-slate-500 hover:text-white cursor-pointer"
                                                                >
                                                                    <X size={12} />
                                                                </button>
                                                            </div>

                                                            <div className="mb-3 bg-[#0a0f1d] p-2.5 rounded-lg border border-indigo-500/10 flex flex-col gap-1.5">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Avoid manual entry:</span>
                                                                    <button
                                                                        type="button"
                                                                        disabled={isAutofilling}
                                                                        onClick={() => handleAutofillFromAPI(match.teamA, match.teamB)}
                                                                        className="px-2 py-1 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-900 disabled:text-indigo-400 text-[9px] font-black tracking-wider uppercase text-white rounded transition-all cursor-pointer flex items-center gap-1.5 shadow-sm border border-indigo-400/20"
                                                                    >
                                                                        {isAutofilling ? (
                                                                            <>
                                                                                <RefreshCw size={10} className="animate-spin" />
                                                                                Syncing live...
                                                                            </>
                                                                        ) : (
                                                                            "🔌 API-Sports Autofill"
                                                                        )}
                                                                    </button>
                                                                </div>
                                                                {autofillError && (
                                                                    <p className="text-[8px] text-rose-400 font-bold font-mono">{autofillError}</p>
                                                                )}
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                <div>
                                                                    <label className="block text-[8px] text-slate-500 uppercase font-bold mb-1 truncate">{match.teamA} Score</label>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        value={adminScoreA}
                                                                        onChange={(e) => setAdminScoreA(Math.max(0, parseInt(e.target.value) || 0))}
                                                                        className="w-full bg-[#090d16] border border-slate-850 rounded p-1 text-center text-xs text-yellow-400 font-bold"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="block text-[8px] text-slate-500 uppercase font-bold mb-1 truncate">{match.teamB} Score</label>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        value={adminScoreB}
                                                                        onChange={(e) => setAdminScoreB(Math.max(0, parseInt(e.target.value) || 0))}
                                                                        className="w-full bg-[#090d16] border border-slate-850 rounded p-1 text-center text-xs text-yellow-400 font-bold"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="border-t border-slate-800/60 pt-2 mt-2 space-y-2">
                                                                <div className="text-[9px] font-black text-slate-400 font-mono uppercase tracking-wider mb-2 flex items-center gap-1">
                                                                    📊 Customize Detailed Matchday Statistics
                                                                </div>

                                                                {/* Stat Row Header */}
                                                                <div className="grid grid-cols-3 gap-2 text-center text-[8px] text-slate-500 uppercase font-black font-mono">
                                                                    <div className="truncate">{match.teamA}</div>
                                                                    <div className="text-indigo-400">STATISTIC</div>
                                                                    <div className="truncate">{match.teamB}</div>
                                                                </div>

                                                                {/* Stat inputs */}
                                                                {[
                                                                    { label: 'Total Shots', keyA: adminShotsTotalA, setA: setAdminShotsTotalA, keyB: adminShotsTotalB, setB: setAdminShotsTotalB },
                                                                    { label: 'Shots on Target', keyA: adminShotsTargetA, setA: setAdminShotsTargetA, keyB: adminShotsTargetB, setB: setAdminShotsTargetB },
                                                                    { label: 'Passing Accuracy %', keyA: adminPassingA, setA: setAdminPassingA, keyB: adminPassingB, setB: setAdminPassingB },
                                                                    { label: 'Corners', keyA: adminCornersA, setA: setAdminCornersA, keyB: adminCornersB, setB: setAdminCornersB, isCorners: true },
                                                                    { label: 'Fouls Committed', keyA: adminFoulsA, setA: setAdminFoulsA, keyB: adminFoulsB, setB: setAdminFoulsB },
                                                                    { label: 'Yellow Cards', keyA: adminYellowA, setA: setAdminYellowA, keyB: adminYellowB, setB: setAdminYellowB },
                                                                    { label: 'Red Cards', keyA: adminRedA, setA: setAdminRedA, keyB: adminRedB, setB: setAdminRedB },
                                                                    { label: 'Possession %', keyA: adminPossessionA, setA: setAdminPossessionA, keyB: adminPossessionB, setB: setAdminPossessionB, isPoss: true },
                                                                ].map((row, idx) => (
                                                                    <div key={idx} className="grid grid-cols-3 gap-2 items-center">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            max={row.isPoss ? 100 : undefined}
                                                                            value={row.keyA}
                                                                            onChange={(e) => {
                                                                                const val = Math.max(0, parseInt(e.target.value) || 0);
                                                                                if (row.isPoss) {
                                                                                    const safeA = Math.min(100, val);
                                                                                    row.setA(safeA);
                                                                                    row.setB(100 - safeA);
                                                                                } else if (row.isCorners) {
                                                                                    row.setA(val);
                                                                                    setAdminCorners(val + adminCornersB);
                                                                                } else {
                                                                                    row.setA(val);
                                                                                }
                                                                            }}
                                                                            className="w-full bg-[#090d16] border border-slate-850 rounded p-1 text-[10px] text-white font-bold text-center"
                                                                        />
                                                                        <div className="text-[9px] text-slate-400 text-center uppercase tracking-tight py-0.5 font-bold truncate">
                                                                            {row.label}
                                                                        </div>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            max={row.isPoss ? 100 : undefined}
                                                                            value={row.keyB}
                                                                            onChange={(e) => {
                                                                                const val = Math.max(0, parseInt(e.target.value) || 0);
                                                                                if (row.isPoss) {
                                                                                    const safeB = Math.min(100, val);
                                                                                    row.setB(safeB);
                                                                                    row.setA(100 - safeB);
                                                                                } else if (row.isCorners) {
                                                                                    row.setB(val);
                                                                                    setAdminCorners(adminCornersA + val);
                                                                                } else {
                                                                                    row.setB(val);
                                                                                }
                                                                            }}
                                                                            className="w-full bg-[#090d16] border border-slate-850 rounded p-1 text-[10px] text-white font-bold text-center"
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className="flex gap-2 justify-end pt-1">
                                                                {officialResults[match.id] && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={async () => {
                                                                            if (confirm("Clear official result for this match?")) {
                                                                                await deleteOfficialResult(match.id);
                                                                                setAdminEditingMatchId(null);
                                                                            }
                                                                        }}
                                                                        className="px-2 py-1 bg-red-600/20 text-red-400 border border-red-500/30 text-[9px] font-black rounded uppercase hover:bg-red-600 hover:text-white transition-all cursor-pointer font-mono"
                                                                    >
                                                                        Clear
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={async () => {
                                                                        await saveOfficialResult(match.id, adminScoreA, adminScoreB, adminCorners);
                                                                        setAdminEditingMatchId(null);
                                                                    }}
                                                                    className="px-3 py-1 bg-indigo-600 text-white text-[9px] font-black rounded uppercase hover:bg-indigo-500 transition-all cursor-pointer font-mono"
                                                                >
                                                                    Save & Lock
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-[9px] text-indigo-400 font-bold font-mono">⚡ Super User Admin Action</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleOpenAdminEdit(match)}
                                                                className="px-2 py-1 text-[9px] font-black rounded-lg border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500 hover:text-slate-950 transition-all cursor-pointer uppercase font-mono"
                                                            >
                                                                {officialResults[match.id] ? "✏️ Edit Final Score" : "🔒 Set Final Score"}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* VIEW: GLOBAL SCOREBOARD */}
            {activeTab === 'scoreboard' && (
                <div className="space-y-6">
                    {/* Rules and reward scoreboard header card */}
                    <div className="bg-gradient-to-r from-blue-950/40 to-indigo-950/40 border border-indigo-900/40 rounded-2xl p-5 relative overflow-hidden shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="space-y-1 z-10">
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[9px] font-bold font-mono tracking-wider rounded-full uppercase">
                🏆 Global Leaderboard
              </span>
                            <h3 className="text-sm font-black text-white uppercase tracking-tight">Real-time Predictors Competition</h3>
                            <p className="text-xs text-slate-400 max-w-xl">
                                Compete with collector enthusiasts around the world! Settle and resolve match forecasts to rise high in the standings and earn ultimate prestige.
                            </p>
                        </div>

                        {/* Current user's summary badge */}
                        <div className="bg-[#131d31] border border-slate-800 rounded-xl p-4 flex items-center gap-3 z-10 shrink-0 select-none">
                            <Avatar photoURL={user?.photoURL || 'wc_ball'} email={user?.email || 'Guest'} size="sm" />
                            <div className="flex flex-col font-mono">
                                <span className="text-slate-400 text-[9px] uppercase leading-tight font-black">YOUR CURRENT SCORE</span>
                                <span className="text-amber-400 text-sm font-black mt-0.5">{localPredictionPoints} POINTS</span>
                                <span className="text-[8px] text-slate-500 uppercase">
                  {Object.values(predictions).filter((p: any) => p.resolved).length} settled forecasts
                </span>
                            </div>
                        </div>
                    </div>

                    {loadingScoreboard ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-[#131d31] border border-slate-800 rounded-2xl text-center space-y-4">
                            <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                            <p className="text-xs font-mono text-slate-500 uppercase tracking-widest animate-pulse">
                                loading worldwide standings...
                            </p>
                        </div>
                    ) : (
                        <div className="bg-[#131d31] border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                            {/* Scoreboard stats header */}
                            <div className="px-6 py-4 bg-slate-900/40 border-b border-slate-800/80 flex items-center justify-between">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-slate-300">
                  Collectors ({scoreboardUsers.some(u => u.uid === user?.uid) ? scoreboardUsers.length : scoreboardUsers.length + (user ? 0 : 1)})
                </span>
                                <span className="text-[10px] font-mono text-slate-500 uppercase">
                  Updated in real-time ⚡
                </span>
                            </div>

                            {/* Leaderboard list container */}
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse min-w-[600px] text-xs">
                                    <thead>
                                    <tr className="border-b border-slate-800/60 font-mono text-[9px] text-slate-500 uppercase tracking-wider bg-slate-900/10">
                                        <th className="py-3 px-6 text-center w-16">Rank</th>
                                        <th className="py-3 px-4">Collector</th>
                                        <th className="py-3 px-4 text-center">Forecasts Submitted</th>
                                        <th className="py-3 px-4 text-center">Settled & Solved</th>
                                        <th className="py-3 px-4 text-center">Active Streak</th>
                                        <th className="py-3 px-6 text-right w-36">Total Points</th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800/40">
                                    {(() => {
                                        // Prepare final list for rendering ensuring the current user is visible even if they're offline/guest or not registered in the leaderboard
                                        let finalStandings = [...scoreboardUsers];
                                        const currentUserId = user?.uid;
                                        const hasMe = currentUserId && finalStandings.some(u => u.uid === currentUserId);

                                        if (currentUserId && !hasMe) {
                                            // User is registered but not fetched OR user is logged in but hasn't updated Firestore yet
                                            finalStandings.push({
                                                uid: currentUserId,
                                                displayName: user.displayName || user.email?.split('@')[0] || 'You',
                                                photoURL: user.photoURL,
                                                email: user.email,
                                                predictionPoints: localPredictionPoints,
                                                streak: user.streak || 0,
                                                predictionsCount: Object.keys(predictions).length,
                                                resolvedCount: Object.values(predictions).filter((p: any) => p.resolved).length,
                                                isOfflineLocal: true
                                            });
                                            // Re-sort
                                            finalStandings.sort((a, b) => b.predictionPoints - a.predictionPoints);
                                        } else if (!currentUserId) {
                                            // Guest mode - inject guest account
                                            finalStandings.push({
                                                uid: 'guest_local_id',
                                                displayName: 'You (Guest Collector)',
                                                photoURL: 'wc_ball',
                                                email: 'Guest',
                                                predictionPoints: localPredictionPoints,
                                                streak: 0,
                                                predictionsCount: Object.keys(predictions).length,
                                                resolvedCount: Object.values(predictions).filter((p: any) => p.resolved).length,
                                                isOfflineLocal: true
                                            });
                                            // Re-sort
                                            finalStandings.sort((a, b) => b.predictionPoints - a.predictionPoints);
                                        }

                                        return finalStandings.map((player, index) => {
                                            const rank = index + 1;
                                            const isMe = player.uid === currentUserId || (player.uid === 'guest_local_id' && !currentUserId);

                                            // Mask email or mask name
                                            const displayEmail = player.email === 'Guest' ? '' : (player.email ? maskEmail(player.email) : '');

                                            return (
                                                <motion.tr
                                                    key={player.uid + '_' + index}
                                                    className={`transition-colors border-l-2 ${
                                                        isMe
                                                            ? 'bg-indigo-505/10 border-amber-500/80 hover:bg-indigo-505/15'
                                                            : 'hover:bg-slate-900/35 border-transparent'
                                                    }`}
                                                >
                                                    {/* Rank Column */}
                                                    <td className="py-4 px-6 text-center font-mono font-black text-xs">
                                                        {rank === 1 ? (
                                                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 border border-yellow-500/30 text-xs shadow-sm shadow-yellow-500/10 font-bold">
                                  1st
                                </span>
                                                        ) : rank === 2 ? (
                                                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-300/20 text-slate-300 border border-slate-300/30 text-xs text-slate-200 font-bold">
                                  2nd
                                </span>
                                                        ) : rank === 3 ? (
                                                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-700/20 text-amber-600 border border-amber-805/30 text-xs font-bold">
                                  3rd
                                </span>
                                                        ) : (
                                                            <span className="text-slate-500 font-mono">#{rank}</span>
                                                        )}
                                                    </td>

                                                    {/* Profile details */}
                                                    <td className="py-4 px-4">
                                                        <div className="flex items-center gap-3">
                                                            <Avatar photoURL={player.photoURL || 'wc_ball'} email={player.email || 'Guest'} size="sm" />
                                                            <div className="flex flex-col min-w-0">
                                                                <div className="flex items-center gap-1.5 min-w-0">
                                    <span className={`font-bold truncate max-w-[150px] ${isMe ? 'text-amber-400 font-extrabold' : 'text-slate-100'}`}>
                                      {player.displayName}
                                    </span>
                                                                    {isMe && (
                                                                        <span className="text-[7px] font-mono font-black uppercase tracking-widest bg-amber-400 text-slate-950 px-1 py-0.5 rounded leading-none">
                                        YOU
                                      </span>
                                                                    )}
                                                                    {player.isOfflineLocal && (
                                                                        <span className="text-[7px] font-mono px-1 py-0.5 rounded bg-slate-800 text-slate-400 leading-none" title="This is a locally computed score currently synced only to your device storage. Log in to claim your leaderboard standing!">
                                        LOCAL
                                      </span>
                                                                    )}
                                                                </div>
                                                                {displayEmail && (
                                                                    <span className="text-[9px] font-mono text-slate-500 truncate leading-tight mt-0.5">
                                      {displayEmail}
                                    </span>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setViewingPlayerPredictions(player)}
                                                                    className="mt-1 inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/25 text-indigo-400 hover:bg-indigo-500 hover:text-slate-950 transition-all font-mono font-black cursor-pointer uppercase tracking-tight w-fit"
                                                                >
                                                                    🔮 View predictions
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Predictions count */}
                                                    <td className="py-4 px-4 text-center font-mono text-slate-300">
                                                        {player.predictionsCount}
                                                    </td>

                                                    {/* Resolved & Solved count */}
                                                    <td className="py-4 px-4 text-center font-mono">
                                                        <span className="text-indigo-400 font-bold">{player.resolvedCount}</span>
                                                        <span className="text-slate-600"> / {player.predictionsCount}</span>
                                                    </td>

                                                    {/* Streak */}
                                                    <td className="py-4 px-4 text-center font-mono">
                                                        {player.streak > 0 ? (
                                                            <span className="text-amber-500 font-black flex items-center justify-center gap-0.5">
                                  🔥 {player.streak}
                                </span>
                                                        ) : (
                                                            <span className="text-slate-600 font-bold">-</span>
                                                        )}
                                                    </td>

                                                    {/* Prediction score / points */}
                                                    <td className="py-4 px-6 text-right font-mono font-black text-slate-100">
                                                        <div className="flex items-center justify-end gap-1.5 font-bold">
                                <span className={`text-sm ${isMe ? 'text-amber-400 font-black text-base' : 'text-white'}`}>
                                  {player.predictionPoints}
                                </span>
                                                            <span className="text-[8px] font-mono text-slate-500 uppercase tracking-tight font-normal">PTS</span>
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            );
                                        });
                                    })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* VIEW: VIDEO HIGHLIGHTS PORTAL */}
            {activeTab === 'highlights' && (
                <div className="space-y-6">
                    {/* Hero Highlights Jumbotron */}
                    <div className="bg-gradient-to-r from-red-950/40 via-purple-950/35 to-slate-900/40 border border-red-900/30 rounded-2xl p-6 relative overflow-hidden shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                        <div className="space-y-2 z-10">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-red-500/10 text-red-400 border border-red-500/30 text-[9px] font-bold font-mono tracking-wider rounded-full uppercase">
                🔴 DailyGoal.tv Real-Time Matches
              </span>
                            <h3 className="text-base font-black text-white uppercase tracking-tight">Official Video Highlights Portal</h3>
                            <p className="text-xs text-slate-350 max-w-xl leading-relaxed">
                                Relive the most magnificent, heart-stopping goals, dramatic penalty shootouts, and intense match highlights directly synchronized from DailyGoal's 2026 World Cup broadcasting feed.
                            </p>
                            <div className="pt-1.5 flex flex-wrap gap-2.5">
                                <a
                                    href="https://dailygoal.tv/world-cup-2026-highlights"
                                    target="_blank"
                                    referrerPolicy="no-referrer"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-650 hover:bg-red-505 text-white font-mono text-[10px] font-bold rounded-lg transition-all shadow-md cursor-pointer uppercase"
                                >
                                    <ExternalLink size={11} /> Open DailyGoal Highlights Hub
                                </a>
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-800/80 border border-slate-700/60 text-slate-400 font-mono text-[9px] rounded-lg">
                  <RefreshCw size={10} className="text-emerald-400 animate-spin" /> Stream Sync: Live
                </span>
                            </div>
                        </div>
                        <div className="hidden lg:flex items-center justify-center p-4 bg-slate-950/50 rounded-2xl border border-slate-800 shrink-0 w-28 h-20 font-mono text-center flex-col select-none">
                            <span className="text-2xl">🏆</span>
                            <span className="text-[9px] text-slate-500 font-bold uppercase mt-1">2026 Edition</span>
                        </div>
                    </div>

                    {/* Quick Hub Statistics and Guide */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono">
                        <div className="bg-[#131d31] p-4 rounded-xl border border-slate-800/85">
                            <span className="text-[9px] text-slate-400 lowercase uppercase">Highlight Coverage</span>
                            <div className="text-base font-black text-white mt-0.5">100% Matchday Coverage</div>
                            <div className="text-[9px] text-slate-500 mt-0.5">All 104 matches of tournament</div>
                        </div>
                        <div className="bg-[#131d31] p-4 rounded-xl border border-slate-800/85">
                            <span className="text-[9px] text-slate-400 lowercase">Available Hosts</span>
                            <div className="text-base font-black text-rose-400 mt-0.5">3 Countries (MEX/CAN/USA)</div>
                            <div className="text-[9px] text-slate-500 mt-0.5">Dual English/Spanish commentary</div>
                        </div>
                        <div className="bg-[#131d31] p-4 rounded-xl border border-slate-800/85">
                            <span className="text-[9px] text-slate-400 lowercase">Streaming Quality</span>
                            <div className="text-base font-black text-emerald-400 mt-0.5">Ultra HD 4K Support</div>
                            <div className="text-[9px] text-slate-500 mt-0.5">Powered by DailyGoal.tv Media Hub</div>
                        </div>
                    </div>

                    {/* AI Crawler Bot Control Center */}
                    <div className="bg-[#101424] border border-indigo-900/40 p-5 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-lg shadow-indigo-950/10">
                        <div className="space-y-1">
              <span className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[9px] font-black tracking-widest px-2 py-0.5 rounded-full uppercase inline-flex items-center gap-1.5 font-mono">
                🤖 AI CRAWLER DAEMON
              </span>
                            <h4 className="text-sm font-black text-white uppercase tracking-tight font-mono">FIFA (@fifa) YouTube Hourly Highlights Checker</h4>
                            <p className="text-[11px] text-slate-400 max-w-xl leading-relaxed">
                                The server bot scans the official FIFA YouTube RSS stream hourly. When a match highlight video is localized, our integrated Gemini model structures its details and drafts a comprehensive analysis report automatically.
                            </p>
                            {highlightsCheckMessage && (
                                <div className="text-[10px] bg-indigo-950/50 border border-indigo-800 text-indigo-350 font-mono py-1 px-2.5 rounded-lg inline-block">
                                    Status: {highlightsCheckMessage}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={triggerHighlightsCrawl}
                            disabled={checkingHighlights}
                            className={`px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-mono text-xs font-black rounded-xl select-none flex items-center gap-2 cursor-pointer transition-all shrink-0 ${checkingHighlights ? 'opacity-50 cursor-wait' : ''}`}
                        >
                            <RefreshCw size={12} className={checkingHighlights ? 'animate-spin' : ''} />
                            {checkingHighlights ? "Scanning Channel..." : "Trigger Manual Scan"}
                        </button>
                    </div>

                    {/* Matches Highlights Catalog */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h4 className="font-mono text-xs font-black text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                                <Video size={13} className="text-red-500" /> Catalog of Played Matches ({ALL_HIGHLIGHTS.length})
                            </h4>
                            <span className="text-[10px] font-mono text-slate-500">
                Sorted by most recent matchday
              </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {ALL_HIGHLIGHTS.map((hl) => {
                                const matchObj = SHEDULE_DATA.find(m => m.id === hl.matchId);
                                return (
                                    <motion.div
                                        key={hl.id}
                                        whileHover={{ scale: 1.006, y: -2 }}
                                        className="bg-[#131d31] border border-slate-800 rounded-2xl p-4 overflow-hidden shadow-xl flex flex-col justify-between hover:border-slate-700 transition-all group"
                                    >
                                        <div>
                                            {/* Thumbnail Container */}
                                            <div className="w-full h-36 rounded-xl relative overflow-hidden flex flex-col justify-between p-3 border border-slate-800 group-hover:border-slate-705">
                                                {hl.thumbnailUrl ? (
                                                    <img
                                                        src={hl.thumbnailUrl}
                                                        alt={hl.title}
                                                        className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-75 transition-opacity"
                                                        referrerPolicy="no-referrer"
                                                    />
                                                ) : (
                                                    <div className={`absolute inset-0 bg-gradient-to-br ${hl.thumbnailGradient}`} />
                                                )}

                                                <div className="flex items-center justify-between z-10 font-mono">
                          <span className="bg-slate-950/80 border border-slate-800 text-[8px] font-bold tracking-wider px-2 py-0.5 rounded text-slate-350 uppercase">
                            {hl.thumbnailUrl ? "YOUTUBE BROADCAST" : "DAILYGOAL.TV OFFICIAL HIGHLIGHT"}
                          </span>
                                                    <span className="bg-red-650 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full flex items-center gap-1 uppercase select-none animate-pulse">
                            🔴 HD 4K
                          </span>
                                                </div>

                                                {/* Centered Play Button Overlay */}
                                                <button
                                                    onClick={() => setPlayingVideoHighlight(hl)}
                                                    className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-red-600 hover:bg-red-500 text-white flex items-center justify-center shadow-lg shadow-red-500/20 group-hover:scale-110 transition-all cursor-pointer border border-white/20 select-none animate-bounce"
                                                    title="Click of highlight card to play match commentary video"
                                                >
                                                    <Play size={18} fill="currentColor" className="ml-0.5" />
                                                </button>

                                                <div className="flex items-end justify-between font-mono z-10">
                          <span className="bg-slate-950/90 text-[10px] font-bold text-white px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock size={10} className="text-red-400" /> {hl.duration || "4:00"} MIN
                          </span>
                                                    <span className="bg-slate-950/90 text-[8px] font-black text-emerald-400 px-2 py-0.5 rounded tracking-tight">
                            {hl.score}
                          </span>
                                                </div>
                                            </div>

                                            {/* Info block */}
                                            <div className="mt-3.5 space-y-1">
                                                <div className="flex items-center justify-between text-[10px] font-mono text-slate-500">
                                                    <span className="font-bold text-indigo-400">{hl.date}</span>
                                                    <span className="truncate max-w-[50%]">• {hl.stadium}</span>
                                                </div>
                                                <h5 className="text-xs font-black text-slate-100 uppercase group-hover:text-white transition-colors">
                                                    {hl.title}
                                                </h5>
                                                <p className="text-[11px] text-slate-400 font-mono mt-2 leading-relaxed line-clamp-3">
                                                    {hl.summary}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Bottom controls */}
                                        <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between gap-2.5">
                                            {/* Scorer Pills */}
                                            <div className="flex flex-wrap gap-1 pr-2 max-w-[60%] font-mono text-[8.5px] leading-tight select-none">
                                                {hl.goals && hl.goals.slice(0, 2).map((goal, idx) => (
                                                    <span key={idx} className="bg-slate-950/40 border border-slate-800 px-1.5 py-0.5 rounded text-slate-400">
                            ⚽  {goal.player} ({goal.min}')
                          </span>
                                                ))}
                                                {hl.goals && hl.goals.length > 2 && (
                                                    <span className="text-[8px] text-slate-500">+{hl.goals.length - 2} more</span>
                                                )}
                                                {(!hl.goals || hl.goals.length === 0) && (
                                                    <span className="text-slate-500 italic">No lineups synced</span>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-1.5 shrink-0 select-none">
                                                <button
                                                    onClick={() => setPlayingVideoHighlight(hl)}
                                                    className="px-2.5 py-1 text-[9px] font-black bg-indigo-600/10 border border-indigo-500/20 text-indigo-400 hover:bg-indigo-600 hover:text-white transition-all rounded font-mono uppercase tracking-tight cursor-pointer"
                                                >
                                                    Details
                                                </button>
                                                <a
                                                    href={hl.videoUrl}
                                                    target="_blank"
                                                    referrerPolicy="no-referrer"
                                                    rel="noopener noreferrer"
                                                    className="px-2 py-1 text-[9px] font-black bg-red-650 text-white hover:bg-red-500 transition-all rounded font-mono uppercase tracking-tight cursor-pointer flex items-center gap-0.5"
                                                    title="Search on YouTube or watch highlight video"
                                                >
                                                    PLAY 📺
                                                </a>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* OVERLAY: PREDICT MATCH MODAL */}
            <AnimatePresence>
                {predictingMatch && (
                    <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-[#131d31] border border-slate-800 rounded-2xl w-full max-w-md p-6 relative overflow-hidden shadow-2xl"
                        >
                            {/* Close Button */}
                            <button
                                onClick={() => setPredictingMatch(null)}
                                className="absolute right-4 top-4 hover:bg-slate-800 p-1 rounded-full text-slate-400 hover:text-white transition-all cursor-pointer"
                            >
                                <X size={18} />
                            </button>

                            <span className="text-[10px] font-mono tracking-wider text-indigo-400 uppercase font-black">
                {predictingMatch.group} • Forecast Contest
              </span>
                            <h3 className="text-md font-extrabold text-white uppercase mt-1 mb-6">
                                Predict Matchday Score
                            </h3>

                            {/* Scoreline controls */}
                            <div className="bg-[#090d16] border border-slate-800 rounded-xl p-4 flex items-center justify-between gap-4 mb-4 select-none">
                                {/* Team A */}
                                <div className="flex flex-col items-center gap-2 w-[40%]">
                                    <TeamFlag name={predictingMatch.teamA} className="w-12 h-8" />
                                    <span className="text-xs font-bold text-white text-center truncate w-full">{predictingMatch.teamA}</span>

                                    <div className="flex items-center gap-2 mt-1">
                                        <button
                                            onClick={() => setFormScoreA(Math.max(0, formScoreA - 1))}
                                            className="bg-slate-800 hover:bg-slate-700 text-white w-6 h-6 rounded flex items-center justify-center text-xs font-bold font-mono cursor-pointer"
                                        >
                                            -
                                        </button>
                                        <span className="font-mono font-black text-base text-yellow-400 w-4 text-center">{formScoreA}</span>
                                        <button
                                            onClick={() => setFormScoreA(formScoreA + 1)}
                                            className="bg-slate-800 hover:bg-slate-700 text-white w-6 h-6 rounded flex items-center justify-center text-xs font-bold font-mono cursor-pointer"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>

                                <div className="w-[10%] text-center text-slate-500 font-mono font-black text-sm">
                                    -
                                </div>

                                {/* Team B */}
                                <div className="flex flex-col items-center gap-2 w-[40%] text-right font-mono">
                                    <TeamFlag name={predictingMatch.teamB} className="w-12 h-8" />
                                    <span className="text-xs font-bold text-white text-center truncate w-full">{predictingMatch.teamB}</span>

                                    <div className="flex items-center gap-2 mt-1">
                                        <button
                                            onClick={() => setFormScoreB(Math.max(0, formScoreB - 1))}
                                            className="bg-slate-800 hover:bg-slate-700 text-white w-6 h-6 rounded flex items-center justify-center text-xs font-bold font-mono cursor-pointer"
                                        >
                                            -
                                        </button>
                                        <span className="font-mono font-black text-base text-yellow-400 w-4 text-center">{formScoreB}</span>
                                        <button
                                            onClick={() => setFormScoreB(formScoreB + 1)}
                                            className="bg-slate-800 hover:bg-slate-700 text-white w-6 h-6 rounded flex items-center justify-center text-xs font-bold font-mono cursor-pointer"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Extra prediction params */}
                            <div className="space-y-4 mb-6">
                                {/* Total goals over/under */}
                                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-slate-400 flex items-center gap-1">
                    <Info size={12} className="text-teal-400" /> Over/Under 2.5 goals
                  </span>

                                    <div className="flex gap-1.5 font-bold uppercase text-[9px] tracking-tight">
                    <span className={`px-2.5 py-1.5 rounded transition-all select-none ${
                        formGoalsOverUnder === 'under'
                            ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-black'
                            : 'bg-slate-900 text-slate-600'
                    }`}>
                      Under 2.5
                    </span>
                                        <span className={`px-2.5 py-1.5 rounded transition-all select-none ${
                                            formGoalsOverUnder === 'over'
                                                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-black'
                                                : 'bg-slate-900 text-slate-600'
                                        }`}>
                      Over 2.5
                    </span>
                                    </div>
                                </div>

                                {/* Corners quantity selector */}
                                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-slate-400 flex items-center gap-1">
                    <Info size={12} className="text-yellow-400" /> Total corners
                  </span>

                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setFormCorners(Math.max(0, formCorners - 1))}
                                            className="bg-slate-800 hover:bg-slate-700 text-white w-5 h-5 rounded flex items-center justify-center text-xs font-bold font-mono cursor-pointer"
                                        >
                                            -
                                        </button>
                                        <span className="font-mono font-black text-center w-6 text-white text-[12px]">
                      {formCorners} 🚩
                    </span>
                                        <button
                                            type="button"
                                            onClick={() => setFormCorners(formCorners + 1)}
                                            className="bg-slate-800 hover:bg-slate-700 text-white w-5 h-5 rounded flex items-center justify-center text-xs font-bold font-mono cursor-pointer"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Prediction Action */}
                            <button
                                onClick={handleLockPrediction}
                                className="w-full bg-blue-600 hover:bg-blue-400 text-white font-black py-3 rounded-xl text-xs flex items-center justify-center gap-2 cursor-pointer uppercase tracking-wider transition-all shadow-lg shadow-blue-500/10 mt-2"
                            >
                                <Sparkles size={14} className="fill-white/20 animate-pulse" /> Submit prediction
                            </button>

                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* OVERLAY: ACTIVE MATCHDAY LIVE TICKER SIMULATION */}
            <AnimatePresence>
                {simulatingMatch && (
                    <div className="fixed inset-0 bg-[#020617]/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-[#131d31] border border-slate-800 rounded-2xl w-full max-w-lg p-6 relative overflow-hidden shadow-2xl"
                        >
                            {/* Top simulation indicators */}
                            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800/80">
                                <div className="flex items-center gap-2">
                                    <Activity size={16} className="text-indigo-400 animate-pulse" />
                                    <span className="font-mono font-black text-xs text-white uppercase tracking-wider text-left">Live match simulator</span>
                                </div>
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-full font-mono text-[9px] font-black animate-pulse uppercase">
                                    ● LIVE
                                </div>
                            </div>

                            {/* Stadium Detail Jumbotron */}
                            <div className="bg-[#090d16] border border-slate-800 rounded-xl p-4 text-center font-mono relative overflow-hidden pr-3 mb-4">
                                <span className="text-[9px] text-slate-500 uppercase font-black">{simulatingMatch.group} • STADIUM INTERACTIVE SIMULATOR</span>
                                <div className="text-[12px] text-white font-extrabold mt-0.5 uppercase tracking-wide">{simulatingMatch.stadium}</div>

                                {/* Score display */}
                                <div className="flex items-center justify-center gap-6 mt-4">
                                    <div className="flex flex-col items-center gap-1.5 w-[33%]">
                                        <TeamFlag name={simulatingMatch.teamA} className="w-10 h-6.5 shadow-md" />
                                        <span className="text-[11px] font-black tracking-tight text-white truncate w-full text-center">{simulatingMatch.teamA}</span>
                                    </div>
                                    <div className="text-2xl font-black text-yellow-400 flex items-center gap-3">
                                        <span>{simOutcome ? simOutcome.scoreA : (simStep > 2 ? Math.floor(Math.random() * 2) + 1 : 0)}</span>
                                        <span className="text-slate-600 font-normal text-sm">:</span>
                                        <span>{simOutcome ? simOutcome.scoreB : (simStep > 4 ? Math.floor(Math.random() * 1) + 1 : 0)}</span>
                                    </div>
                                    <div className="flex flex-col items-center gap-1.5 w-[33%]">
                                        <TeamFlag name={simulatingMatch.teamB} className="w-10 h-6.5 shadow-md" />
                                        <span className="text-[11px] font-black tracking-tight text-white truncate w-full text-center">{simulatingMatch.teamB}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Log Commentary Box */}
                            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 h-48 overflow-y-auto font-mono text-[11px] text-slate-400 space-y-2 flex flex-col-reverse shadow-inner">
                                <div className="space-y-2">
                                    {simLogs.map((log, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: -5 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className={`pb-1 border-b border-slate-800/30 ${
                                                log && log.startsWith('⚽')
                                                    ? 'text-yellow-400 font-extrabold'
                                                    : log && log.startsWith('🏁')
                                                        ? 'text-white font-extrabold pt-2 border-slate-800'
                                                        : log && log.startsWith('⏱️')
                                                            ? 'text-slate-200'
                                                            : 'text-slate-400'
                                            }`}
                                        >
                                            {log}
                                        </motion.div>
                                    ))}
                                </div>
                            </div>

                            {/* simulation results settlement */}
                            {simOutcome && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-5 space-y-4 pt-3.5 border-t border-slate-800/80"
                                >
                                    <div className="bg-[#1e1b4b]/50 border border-indigo-500/20 rounded-xl p-4 relative overflow-hidden shadow-inner">
                                        <span className="text-[9px] font-mono font-black uppercase text-indigo-400 tracking-wider">YOUR REWARDS CALCULATOR</span>

                                        {simOutcome.packsWon > 0 ? (
                                            <div className="flex flex-col gap-1 mt-1 z-10 relative text-left">
                                                <h4 className="text-xs font-black text-emerald-400 flex items-center gap-1.5 uppercase">
                                                    🏆 Congratulations! You won
                                                    <span className="text-yellow-400 font-mono font-black">{simOutcome.packsWon} Sticker {simOutcome.packsWon === 1 ? 'Pack' : 'Packs'}!</span>
                                                </h4>
                                                <p className="text-[10px] text-slate-300">
                                                    These packs have been added directly to your collection pool and can be opened in the Album!
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col gap-1 mt-1 z-10 relative text-left">
                                                <h4 className="text-xs font-black text-rose-300 uppercase">Close but No Cigar!</h4>
                                                <p className="text-[10px] text-slate-400 font-medium font-mono">
                                                    Your values didn't land in the award limits this time. Make more forecasts to try again!
                                                </p>
                                            </div>
                                        )}

                                        {/* Breakdown grids */}
                                        <div className="grid grid-cols-3 gap-2 text-[10px] text-center font-mono mt-3 text-slate-400">
                                            <div className={`p-1.5 rounded-lg border ${simOutcome.isScoreCorrect ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-slate-900/30 border-transparent text-slate-500'}`}>
                                                <span>Scoreline</span>
                                                <div className="font-extrabold mt-0.5">{simOutcome.isScoreCorrect ? '✔ MATCH' : '❌ MISS'}</div>
                                            </div>
                                            <div className={`p-1.5 rounded-lg border ${simOutcome.isGoalsCorrect ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-slate-900/30 border-transparent text-slate-500'}`}>
                                                <span>Goals O/U</span>
                                                <div className="font-extrabold mt-0.5">{simOutcome.isGoalsCorrect ? '✔ MATCH' : '❌ MISS'}</div>
                                            </div>
                                            <div className={`p-1.5 rounded-lg border ${simOutcome.isCornersCorrect ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-slate-900/30 border-transparent text-slate-500'}`}>
                                                <span>Corners O/U</span>
                                                <div className="font-extrabold mt-0.5">{simOutcome.isCornersCorrect ? '✔ MATCH' : '❌ MISS'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => {
                                            setSimulatingMatch(null);
                                            setSimOutcome(null);
                                        }}
                                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-heavy uppercase font-mono tracking-wider py-3 rounded-xl text-xs flex items-center justify-center gap-1 cursor-pointer transition-all shadow-md"
                                    >
                                        Claim rewards & close
                                    </button>
                                </motion.div>
                            )}

                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* OVERLAY: GAME STATISTICS MODAL */}
            <AnimatePresence>
                {showingStatsMatch && (() => {
                    const pred = predictions[showingStatsMatch.id];
                    const official = officialResults[showingStatsMatch.id];
                    const actualScoreA = official ? official.actualScoreA : ((pred && pred.resolved && pred.actualScoreA !== undefined) ? pred.actualScoreA : getDeterministicMatchResult(showingStatsMatch.id).actualScoreA);
                    const actualScoreB = official ? official.actualScoreB : ((pred && pred.resolved && pred.actualScoreB !== undefined) ? pred.actualScoreB : getDeterministicMatchResult(showingStatsMatch.id).actualScoreB);
                    const actualCorners = official ? (official.actualCorners !== undefined ? official.actualCorners : (official.actualCornersA !== undefined && official.actualCornersB !== undefined ? (official.actualCornersA + official.actualCornersB) : 8)) : ((pred && pred.resolved && pred.actualCorners !== undefined) ? pred.actualCorners : getDeterministicMatchResult(showingStatsMatch.id).actualCorners);

                    const stats = (official && official.actualPassing) ? {
                        possession: official.actualPossession || [50, 50],
                        shotsTotal: official.actualShotsTotal || [10, 10],
                        shotsTarget: official.actualShotsTarget || [4, 4],
                        passing: official.actualPassing || [80, 80],
                        corners: [
                            official.actualCornersA !== undefined ? official.actualCornersA : Math.floor((official.actualCorners || 8) / 2),
                            official.actualCornersB !== undefined ? official.actualCornersB : Math.ceil((official.actualCorners || 8) / 2)
                        ],
                        fouls: official.actualFouls || [10, 10],
                        yellow: official.actualYellow || [1, 1],
                        red: official.actualRed || [0, 0]
                    } : getMatchStats(
                        showingStatsMatch.id,
                        actualScoreA,
                        actualScoreB,
                        actualCorners
                    );

                    return (
                        <div className="fixed inset-0 bg-[#020617]/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="bg-[#131d31] border border-slate-800 rounded-2xl w-full max-w-lg p-6 relative overflow-hidden shadow-2xl"
                            >
                                {/* Close Button */}
                                <button
                                    type="button"
                                    onClick={() => setShowingStatsMatch(null)}
                                    className="absolute right-4 top-4 hover:bg-slate-800 p-1.5 rounded-full text-slate-400 hover:text-white transition-all cursor-pointer"
                                >
                                    <X size={18} />
                                </button>

                                <div className="flex items-center gap-2 mb-2">
                                    <BarChart3 className="text-emerald-400" size={18} />
                                    <span className="font-mono font-black text-xs text-white uppercase tracking-wider">Official Match Statistics</span>
                                </div>
                                <p className="text-[10px] text-slate-400 font-mono mb-4">
                                    Dynamic performance parameters computed for {showingStatsMatch.stadium} matchday.
                                </p>

                                {/* Score Header */}
                                <div className="bg-[#090d16] border border-slate-800/80 rounded-xl p-4 text-center font-mono mb-6">
                                    <div className="flex items-center justify-center gap-4">
                                        <div className="flex flex-col items-center gap-1 w-[38%]">
                                            <TeamFlag name={showingStatsMatch.teamA} className="w-10 h-6.5 shadow" />
                                            <span className="text-[11px] font-bold text-white truncate w-full mt-1 text-center">{showingStatsMatch.teamA}</span>
                                        </div>
                                        <div className="text-xl font-black text-yellow-500 bg-slate-900 border border-slate-800 px-3.5 py-1.5 rounded-lg font-mono">
                                            {actualScoreA} : {actualScoreB}
                                        </div>
                                        <div className="flex flex-col items-center gap-1 w-[38%]">
                                            <TeamFlag name={showingStatsMatch.teamB} className="w-10 h-6.5 shadow" />
                                            <span className="text-[11px] font-bold text-white truncate w-full mt-1 text-center">{showingStatsMatch.teamB}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Stats Table */}
                                <div className="space-y-3.5 font-mono text-[11px]">
                                    {/* Possession */}
                                    <div>
                                        <div className="flex justify-between text-[10px] text-slate-400 mb-1 font-bold">
                                            <span>{stats.possession[0]}% Possession</span>
                                            <span>Possession</span>
                                            <span>{stats.possession[1]}% Possession</span>
                                        </div>
                                        <div className="h-2 w-full bg-slate-800 rounded-full flex overflow-hidden">
                                            <div className="bg-blue-500 h-full transition-all" style={{ width: `${stats.possession[0]}%` }} />
                                            <div className="bg-rose-500 h-full transition-all" style={{ width: `${stats.possession[1]}%` }} />
                                        </div>
                                    </div>

                                    {/* Divider line */}
                                    <div className="h-px bg-slate-800/40 my-2" />

                                    {/* Regular stat lines */}
                                    {[
                                        { label: 'Total Shots', valA: stats.shotsTotal[0], valB: stats.shotsTotal[1] },
                                        { label: 'Shots on Target', valA: stats.shotsTarget[0], valB: stats.shotsTarget[1] },
                                        { label: 'Passing Accuracy', valA: `${stats.passing[0]}%`, valB: `${stats.passing[1]}%` },
                                        { label: 'Corners', valA: stats.corners[0], valB: stats.corners[1] },
                                        { label: 'Fouls Committed', valA: stats.fouls[0], valB: stats.fouls[1] },
                                        { label: 'Yellow Cards', valA: stats.yellow[0], valB: stats.yellow[1] },
                                        { label: 'Red Cards', valA: stats.red[0], valB: stats.red[1] },
                                    ].map((stat, idx) => (
                                        <div key={idx} className="flex justify-between items-center py-0.5">
                                            <span className="text-white font-bold w-12 text-left">{stat.valA}</span>
                                            <span className="text-slate-500 text-[10px] uppercase font-medium">{stat.label}</span>
                                            <span className="text-white font-bold w-12 text-right">{stat.valB}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex gap-2.5 mt-6 pt-4 border-t border-slate-800/80">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowingStatsMatch(null);
                                            setSharingMatch(showingStatsMatch);
                                            setSharingType('stats');
                                        }}
                                        className="flex-1 bg-emerald-605 hover:bg-emerald-500 text-[#090d16] font-black py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer uppercase tracking-tight shadow-md transition-all font-mono"
                                    >
                                        <Share2 size={13} /> Share Stats
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowingStatsMatch(null)}
                                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 rounded-xl text-xs flex items-center justify-center cursor-pointer uppercase tracking-tight transition-all font-mono"
                                    >
                                        Close
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    );
                })()}
            </AnimatePresence>

            {/* OVERLAY: SOCIAL SHARE MODAL */}
            <AnimatePresence>
                {sharingMatch && (() => {
                    const pred = predictions[sharingMatch.id];
                    const official = officialResults[sharingMatch.id];
                    const actualScoreA = official ? official.actualScoreA : ((pred && pred.resolved && pred.actualScoreA !== undefined) ? pred.actualScoreA : getDeterministicMatchResult(sharingMatch.id).actualScoreA);
                    const actualScoreB = official ? official.actualScoreB : ((pred && pred.resolved && pred.actualScoreB !== undefined) ? pred.actualScoreB : getDeterministicMatchResult(sharingMatch.id).actualScoreB);
                    const actualCorners = official ? (official.actualCorners !== undefined ? official.actualCorners : (official.actualCornersA !== undefined && official.actualCornersB !== undefined ? (official.actualCornersA + official.actualCornersB) : 8)) : ((pred && pred.resolved && pred.actualCorners !== undefined) ? pred.actualCorners : getDeterministicMatchResult(sharingMatch.id).actualCorners);

                    const shareText = (sharingType === 'predict' && pred)
                        ? `🔮 My FIFA 2026 Prediction:\n⚽ ${sharingMatch.teamA} ${pred.scoreA} - ${pred.scoreB} ${sharingMatch.teamB}\n🚩 Expected Corners: ${pred.corners}\n📈 Total Goals: ${pred.goalsOverUnder === 'over' ? 'Over 2.5' : 'Under 2.5'} goals\n\nJoin the World Cup predictor & collect stickers! 🌍🏆 https://paganini2026.com/ #FifaWorldCup`
                        : `🏁 World Cup Stats!\n⚽ ${sharingMatch.teamA} ${actualScoreA} - ${actualScoreB} ${sharingMatch.teamB}\n🚩 Total Corners: ${actualCorners}\n📊 Who forecasted this correctly? Play and win custom sticker packs! https://paganini2026.com/ 🏆⚽ #FifaWorldCup`;

                    const encodedText = encodeURIComponent(shareText);
                    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
                    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodedText}`;
                    const telegramUrl = `https://t.me/share/url?url=https://paganini2026.com/&text=${encodedText}`;

                    const handleCopyText = async () => {
                        try {
                            await navigator.clipboard.writeText(shareText);
                            setIsCopied(true);
                            setTimeout(() => {
                                setIsCopied(false);
                            }, 2000);
                        } catch (err) {
                            console.error(err);
                        }
                    };

                    return (
                        <div className="fixed inset-0 bg-[#020617]/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="bg-[#131d31] border border-slate-800 rounded-2xl w-full max-w-sm p-6 relative overflow-hidden shadow-2xl"
                            >
                                {/* Close Button */}
                                <button
                                    type="button"
                                    onClick={() => setSharingMatch(null)}
                                    className="absolute right-4 top-4 hover:bg-slate-800 p-1.5 rounded-full text-slate-400 hover:text-white transition-all cursor-pointer"
                                >
                                    <X size={18} />
                                </button>

                                <div className="flex items-center gap-2 mb-2">
                                    <Share2 className="text-yellow-405 animate-pulse" size={18} />
                                    <span className="font-mono font-black text-xs text-white uppercase tracking-wider">
                    {sharingType === 'predict' ? 'Share Prediction' : 'Share Results'}
                  </span>
                                </div>
                                <p className="text-[10px] text-slate-400 font-mono mb-4">
                                    Select a network below to copy or post your tournament stats directly.
                                </p>

                                {/* Preview text */}
                                <div className="bg-[#090d16] border border-slate-800/60 rounded-xl p-3 mb-4 text-xs font-mono text-slate-300 relative select-all break-words max-h-36 overflow-y-auto whitespace-pre-line leading-relaxed scrollbar-none scrollbar-thin">
                                    {shareText}
                                </div>

                                {/* Buttons Grid */}
                                <div className="grid grid-cols-3 gap-2.5 mb-5 font-mono text-[11px]">
                                    {/* Twitter / X */}
                                    <a
                                        href={twitterUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-black hover:bg-zinc-900 text-white rounded-xl p-3 border border-zinc-800 text-center flex flex-col items-center justify-center gap-2 transition-all cursor-pointer shadow-sm group"
                                    >
                                        <span className="text-white text-md font-bold group-hover:scale-110 transition-transform">𝕏</span>
                                        <span className="text-[9px] text-slate-400 uppercase tracking-tight font-black">Twitter</span>
                                    </a>

                                    {/* WhatsApp */}
                                    <a
                                        href={whatsappUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] rounded-xl p-3 border border-[#25D366]/20 text-center flex flex-col items-center justify-center gap-2 transition-all cursor-pointer shadow-sm group"
                                    >
                                        <span className="text-[#25D366] text-sm font-bold group-hover:scale-110 transition-transform">🟢</span>
                                        <span className="text-[9px] text-slate-400 uppercase tracking-tight font-black">WhatsApp</span>
                                    </a>

                                    {/* Telegram */}
                                    <a
                                        href={telegramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-[#0088cc]/10 hover:bg-[#0088cc]/20 text-[#0088cc] rounded-xl p-3 border border-[#0088cc]/20 text-center flex flex-col items-center justify-center gap-2 transition-all cursor-pointer shadow-sm group"
                                    >
                                        <span className="text-[#0088cc] text-sm font-bold group-hover:scale-110 transition-transform">🔵</span>
                                        <span className="text-[9px] text-slate-400 uppercase tracking-tight font-black">Telegram</span>
                                    </a>
                                </div>

                                <div className="flex gap-2.5">
                                    <button
                                        type="button"
                                        onClick={handleCopyText}
                                        className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-black py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer uppercase tracking-wide shadow-md transition-all font-mono"
                                    >
                                        {isCopied ? (
                                            <>
                                                <Check size={13} className="text-green-300" /> Copied!
                                            </>
                                        ) : (
                                            <>
                                                <Copy size={13} /> Copy Post
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSharingMatch(null)}
                                        className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-4 py-2.5 rounded-xl text-xs flex items-center justify-center cursor-pointer uppercase tracking-wide transition-all font-mono"
                                    >
                                        Close
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    );
                })()}
            </AnimatePresence>

            {/* OVERLAY: RESET UNSTARTED PREDICTIONS CONFIRMATION MODAL */}
            <AnimatePresence>
                {showResetConfirm && (
                    <div className="fixed inset-0 bg-[#020617]/85 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-[#131d31] border border-red-500/25 rounded-2xl w-full max-w-sm p-6 relative overflow-hidden shadow-2xl space-y-4"
                            id="reset-predictions-modal"
                        >
                            <div className="w-12 h-12 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center text-red-500 mx-auto text-xl">
                                ⚠️
                            </div>
                            <div className="text-center space-y-1.5">
                                <h3 className="text-sm font-black text-white uppercase tracking-tight">Reset Active Predictions?</h3>
                                <p className="text-xs text-slate-400">
                                    Are you sure you want to reset all <strong>{unstartedPredictionsCount} active</strong> predictions? Settled matches will not be touched.
                                </p>
                            </div>
                            <div className="flex gap-2.5 pt-2">
                                <button
                                    type="button"
                                    id="btn-confirm-prediction-reset"
                                    onClick={handleResetUnstarted}
                                    className="flex-1 bg-red-600 hover:bg-red-500 text-white font-black py-2.5 rounded-xl text-xs flex items-center justify-center cursor-pointer uppercase tracking-wider transition-all shadow-md font-mono"
                                >
                                    Yes, Reset
                                </button>
                                <button
                                    type="button"
                                    id="btn-cancel-prediction-reset"
                                    onClick={() => setShowResetConfirm(false)}
                                    className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 rounded-xl text-xs flex items-center justify-center cursor-pointer uppercase tracking-wider transition-all font-mono"
                                >
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* FLOATING NOTIFICATION BANNER */}
            <AnimatePresence>
                {resetNotify && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="fixed bottom-6 right-6 z-[70] bg-emerald-950 border border-emerald-500/30 rounded-xl px-4 py-3.5 shadow-xl flex items-center gap-3 font-mono text-xs text-emerald-300"
                        id="reset-toast-banner"
                    >
                        <span className="text-emerald-400">✔</span>
                        <span>{resetNotify}</span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* OVERLAY: VIEW OTHER PLAYER PREDICTIONS */}
            <AnimatePresence>
                {viewingPlayerPredictions && (
                    <div className="fixed inset-0 bg-[#020617]/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-[#131d31] border border-slate-800 rounded-2xl w-full max-w-xl p-6 relative overflow-hidden shadow-2xl flex flex-col max-h-[85vh]"
                        >
                            {/* Close Button */}
                            <button
                                onClick={() => setViewingPlayerPredictions(null)}
                                className="absolute right-4 top-4 hover:bg-slate-800 p-1.5 rounded-full text-slate-400 hover:text-white transition-all cursor-pointer"
                            >
                                <X size={18} />
                            </button>

                            <div className="flex items-center gap-3.5 mb-6 pb-4 border-b border-slate-800">
                                <Avatar photoURL={viewingPlayerPredictions.photoURL || 'wc_ball'} email={viewingPlayerPredictions.email || 'Guest'} size="md" />
                                <div>
                  <span className="text-[9px] font-mono tracking-wider text-indigo-400 uppercase font-black">
                    Collector Profile Forecasts
                  </span>
                                    <h3 className="text-md font-extrabold text-white uppercase mt-0.5">
                                        {viewingPlayerPredictions.displayName}
                                    </h3>
                                    <p className="text-[10px] text-slate-400">
                                        Total Score: <span className="text-amber-400 font-bold">{viewingPlayerPredictions.predictionPoints} PTS</span> • {viewingPlayerPredictions.predictionsCount} predictions
                                    </p>
                                </div>
                            </div>

                            {/* Predictions List inside the Modal */}
                            <div className="overflow-y-auto space-y-3.5 flex-grow pr-1">
                                {(() => {
                                    const playerPreds = viewingPlayerPredictions.predictions || {};
                                    const matchesWithPred = SHEDULE_DATA.filter(m => playerPreds[m.id]?.submitted);

                                    if (matchesWithPred.length === 0) {
                                        return (
                                            <div className="text-center py-10 text-slate-500 font-mono text-xs">
                                                This user hasn't submitted any forecasts yet.
                                            </div>
                                        );
                                    }

                                    return matchesWithPred.map(match => {
                                        const p = playerPreds[match.id];
                                        const matchStarted = isMatchStarted(match, officialResults);
                                        const isFinished = p.resolved === true || officialResults[match.id]?.resolved === true;
                                        const isMe = viewingPlayerPredictions.uid === user?.uid;

                                        // Disable masking so all forecasts are always viewable by the community
                                        const maskPrediction = false;

                                        return (
                                            <div key={match.id} className="bg-[#090d16]/50 border border-slate-800/80 rounded-xl p-3 flex flex-col md:flex-row justify-between items-center gap-3">
                                                {/* Match details */}
                                                <div className="flex items-center gap-2 md:w-1/2 min-w-0 pr-2">
                                                    <TeamFlag name={match.teamA} className="w-5 h-3.5" />
                                                    <span className="text-white font-bold text-[11px] truncate">{match.teamA}</span>
                                                    <span className="text-slate-500 text-[9px] font-mono">vs</span>
                                                    <span className="text-white font-bold text-[11px] truncate">{match.teamB}</span>
                                                    <TeamFlag name={match.teamB} className="w-5 h-3.5" />
                                                </div>

                                                {/* Prediction outcome */}
                                                <div className="flex items-center gap-3 md:w-1/2 justify-end">
                                                    {maskPrediction ? (
                                                        <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 text-slate-500 font-mono text-[9px] px-2 py-1 rounded">
                                                            <span>🔒 Locked until kickoff</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-3">
                                                            <div className="flex flex-col text-center font-mono">
                                                                <span className="text-[7px] text-slate-500 uppercase">Forecast</span>
                                                                <span className="text-xs font-black text-yellow-500">{p.scoreA} - {p.scoreB}</span>
                                                            </div>
                                                            <div className="flex flex-col text-center font-mono">
                                                                <span className="text-[7px] text-slate-500 uppercase">Corners</span>
                                                                <span className="text-xs font-black text-indigo-400">{p.corners} 🚩</span>
                                                            </div>
                                                            <div className="flex flex-col text-center font-mono">
                                                                <span className="text-[7px] text-slate-500 uppercase">Goals</span>
                                                                <span className="text-xs font-bold text-slate-300 uppercase">{p.goalsOverUnder} 2.5</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Settle/Points details */}
                                                    {isFinished ? (
                                                        <span className="text-emerald-400 font-mono text-[9px] font-black uppercase bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded">
                              +{p.pointsWon ?? p.packsWon ?? 0} PTS
                            </span>
                                                    ) : matchStarted ? (
                                                        <span className="text-indigo-400 font-mono text-[9px] font-black uppercase bg-indigo-500/10 border border-indigo-500/20 px-2 py-1 rounded">
                              Live results
                            </span>
                                                    ) : (
                                                        <span className="text-slate-500 font-mono text-[9px] font-black uppercase bg-slate-800 border border-slate-700 px-2 py-1 rounded">
                              Active
                            </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>

                            <div className="mt-5 pt-3.5 border-t border-slate-800 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setViewingPlayerPredictions(null)}
                                    className="px-4 py-2 bg-slate-850 hover:bg-slate-800 border border-slate-800 text-slate-200 text-xs font-bold rounded-xl cursor-pointer"
                                >
                                    Close Standings Drawer
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* OVERLAY: VIEW WHO PREDICTED WHAT FOR MATCH */}
            <AnimatePresence>
                {viewingCommunityMatch && (
                    <div className="fixed inset-0 bg-[#020617]/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-[#131d31] border border-slate-800 rounded-2xl w-full max-w-xl p-6 relative overflow-hidden shadow-2xl flex flex-col max-h-[85vh]"
                        >
                            {/* Close Button */}
                            <button
                                onClick={() => setViewingCommunityMatch(null)}
                                className="absolute right-4 top-4 hover:bg-slate-800 p-1.5 rounded-full text-slate-400 hover:text-white transition-all cursor-pointer"
                            >
                                <X size={18} />
                            </button>

                            <div className="mb-6 pb-4 border-b border-slate-800">
                <span className="text-[9px] font-mono tracking-wider text-indigo-400 uppercase font-black">
                  {viewingCommunityMatch.group} • Community Breakdown
                </span>
                                <h3 className="text-md font-extrabold text-white uppercase mt-0.5 flex items-center gap-2">
                                    <TeamFlag name={viewingCommunityMatch.teamA} className="w-5 h-3.5" />
                                    {viewingCommunityMatch.teamA} vs {viewingCommunityMatch.teamB}
                                    <TeamFlag name={viewingCommunityMatch.teamB} className="w-5 h-3.5" />
                                </h3>
                                <p className="text-[10px] text-slate-400 mt-1">
                                    Showing exactly how other collectors predicted this matchup.
                                </p>
                            </div>

                            {/* Predictors lists inside the modal */}
                            <div className="overflow-y-auto space-y-3.5 flex-grow pr-1">
                                {(() => {
                                    const stats = getCommunityPredictions(viewingCommunityMatch.id);

                                    if (stats.list.length === 0) {
                                        return (
                                            <div className="text-center py-10 text-slate-500 font-mono text-xs">
                                                No predictions submitted for this match yet.
                                            </div>
                                        );
                                    }

                                    const matchStarted = isMatchStarted(viewingCommunityMatch, officialResults);

                                    return stats.list.map((item, index) => {
                                        const isMe = item.userId === user?.uid;
                                        // Disable masking so all forecasts are always viewable by the community
                                        const maskPrediction = false;

                                        return (
                                            <div key={item.userId + '_' + index} className="bg-[#090d16]/50 border border-slate-800/80 rounded-xl p-3 flex justify-between items-center gap-3">
                                                {/* Profile detail */}
                                                <div className="flex items-center gap-2.5">
                                                    <Avatar photoURL={item.photoURL} email={item.email} size="sm" />
                                                    <div className="flex flex-col">
                            <span className={`font-bold text-[11px] ${isMe ? 'text-amber-400 font-extrabold' : 'text-slate-100'}`}>
                              {item.displayName} {isMe && '(You)'}
                            </span>
                                                        {item.email && item.email !== 'Guest' && (
                                                            <span className="text-[8px] font-mono text-slate-500">{maskEmail(item.email)}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Forecast detail */}
                                                <div>
                                                    {maskPrediction ? (
                                                        <span className="text-[9px] font-mono text-slate-500 bg-slate-900 border border-slate-800/80 px-2.5 py-1 rounded">
                              🔒 Locked until kickoff
                            </span>
                                                    ) : (
                                                        <div className="flex items-center gap-3 font-mono">
                                                            <div className="flex flex-col text-center">
                                                                <span className="text-[7px] text-slate-500 uppercase">Score</span>
                                                                <span className="text-xs font-black text-yellow-500">{item.scoreA} - {item.scoreB}</span>
                                                            </div>
                                                            <div className="flex flex-col text-center">
                                                                <span className="text-[7px] text-slate-500 uppercase">Corners</span>
                                                                <span className="text-xs font-bold text-indigo-400">{item.corners} 🚩</span>
                                                            </div>
                                                            <div className="flex flex-col text-center">
                                                                <span className="text-[7px] text-slate-500 uppercase">Goals</span>
                                                                <span className="text-xs font-bold text-slate-300 uppercase">{item.goalsOverUnder} 2.5</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>

                            <div className="mt-5 pt-3.5 border-t border-slate-800 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setViewingCommunityMatch(null)}
                                    className="px-4 py-2 bg-slate-850 hover:bg-slate-800 border border-slate-800 text-slate-205 text-xs font-bold rounded-xl cursor-pointer"
                                >
                                    Close Breakdown Drawer
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* OVERLAY: ANONYMOUS PREDICTION WARNING POPUP */}
            <AnimatePresence>
                {showAuthWarning && (
                    <div className="fixed inset-0 bg-[#020617]/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-[#131d31] border border-red-500/30 rounded-2xl w-full max-w-sm p-6 relative overflow-hidden shadow-2xl text-center"
                        >
                            <div className="absolute right-4 top-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAuthWarning(false)}
                                    className="hover:bg-slate-850 p-1.5 rounded-full text-slate-400 hover:text-white transition-all cursor-pointer"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Icon Container */}
                            <div className="mx-auto w-12 h-12 bg-red-500/10 border border-red-500/20 text-red-500 rounded-full flex items-center justify-center mb-4">
                                <Info size={24} />
                            </div>

                            {/* Title */}
                            <h3 className="text-base font-extrabold text-white uppercase tracking-tight mb-2">
                                Authentication Required
                            </h3>

                            {/* Message */}
                            <p className="text-xs text-slate-300 leading-relaxed max-w-xs mx-auto mb-5">
                                Only logged-in collectors can forecast matchday outcomes, participate on the community leaderboard, and get free sticker packs.
                            </p>

                            {/* Info Accent Card */}
                            <div className="bg-[#090d16]/60 border border-slate-800/80 rounded-xl p-3.5 mb-5 text-left flex items-start gap-2.5">
                                <div className="p-1 bg-yellow-500/10 text-yellow-500 rounded font-bold text-[10px]">🎁</div>
                                <div className="text-[10px] text-slate-400 leading-relaxed font-mono">
                                    Sign in or create a free profile using the <span className="text-indigo-400 font-bold">Collector Account</span> panel located in the sidebar manager or the main header!
                                </div>
                            </div>

                            {/* Button */}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowAuthWarning(false)}
                                    className="w-full py-2.5 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-slate-950 text-xs font-black rounded-xl cursor-pointer shadow-lg uppercase transition-all"
                                >
                                    Understood
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {playingVideoHighlight && (
                <HighlightsPlayerModal
                    highlight={playingVideoHighlight}
                    onClose={() => setPlayingVideoHighlight(null)}
                />
            )}

        </div>
    );
}

interface HighlightsPlayerModalProps {
    highlight: VideoHighlight;
    onClose: () => void;
}

class StadiumAudioEngine {
    private ctx: AudioContext | null = null;
    private noiseSource: AudioBufferSourceNode | null = null;
    private crowdFilter: BiquadFilterNode | null = null;
    private crowdGain: GainNode | null = null;

    constructor() {}

    init() {
        if (this.ctx) return;
        try {
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
            if (!AudioContextClass) return;
            this.ctx = new AudioContextClass();

            // Create White Noise Buffer for ambient stadium crowd noise
            const bufferSize = 2 * this.ctx.sampleRate;
            const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
            const output = noiseBuffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) {
                output[i] = Math.random() * 2 - 1;
            }

            this.noiseSource = this.ctx.createBufferSource();
            this.noiseSource.buffer = noiseBuffer;
            this.noiseSource.loop = true;

            // Filter to shape into standard distant stadium low hum
            this.crowdFilter = this.ctx.createBiquadFilter();
            this.crowdFilter.type = 'lowpass';
            this.crowdFilter.frequency.setValueAtTime(320, this.ctx.currentTime);
            this.crowdFilter.Q.setValueAtTime(1.0, this.ctx.currentTime);

            this.crowdGain = this.ctx.createGain();
            this.crowdGain.gain.setValueAtTime(0.015, this.ctx.currentTime);

            this.noiseSource.connect(this.crowdFilter);
            this.crowdFilter.connect(this.crowdGain);
            this.crowdGain.connect(this.ctx.destination);
            this.noiseSource.start();
        } catch (e) {
            console.warn("Web Audio Context not initialized", e);
        }
    }

    playWhistle() {
        if (!this.ctx) return;
        try {
            if (this.ctx.state === 'suspended') {
                this.ctx.resume();
            }
            const now = this.ctx.currentTime;
            const osc1 = this.ctx.createOscillator();
            const osc2 = this.ctx.createOscillator();
            const gainNode = this.ctx.createGain();

            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(1400, now);
            osc1.frequency.exponentialRampToValueAtTime(1150, now + 0.15);

            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(1430, now);
            osc2.frequency.exponentialRampToValueAtTime(1180, now + 0.15);

            gainNode.gain.setValueAtTime(0, now);
            gainNode.gain.linearRampToValueAtTime(0.08, now + 0.02);
            gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

            osc1.connect(gainNode);
            osc2.connect(gainNode);
            gainNode.connect(this.ctx.destination);

            osc1.start(now);
            osc2.start(now);
            osc1.stop(now + 0.16);
            osc2.stop(now + 0.16);
        } catch (e) {
            console.warn("Failed to synthesize whistle", e);
        }
    }

    setVolume(volume: number) {
        if (this.crowdGain && this.ctx) {
            this.crowdGain.gain.linearRampToValueAtTime(volume * 0.05, this.ctx.currentTime + 0.1);
        }
    }

    stopAmbient() {
        if (this.crowdGain && this.ctx) {
            this.crowdGain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.2);
        }
    }

    goalSwell() {
        if (!this.ctx || !this.crowdGain || !this.crowdFilter) return;
        try {
            if (this.ctx.state === 'suspended') {
                this.ctx.resume();
            }
            const now = this.ctx.currentTime;

            // Blow standard whistle twice for scored goal celebration
            this.playWhistle();
            setTimeout(() => this.playWhistle(), 150);

            // Swell filter bandwidth and raise the volume dynamically
            this.crowdFilter.frequency.setValueAtTime(320, now);
            this.crowdFilter.frequency.exponentialRampToValueAtTime(1300, now + 0.5);
            this.crowdFilter.frequency.exponentialRampToValueAtTime(320, now + 4.5);

            this.crowdGain.gain.setValueAtTime(this.crowdGain.gain.value, now);
            this.crowdGain.gain.linearRampToValueAtTime(0.18, now + 0.3);
            this.crowdGain.gain.exponentialRampToValueAtTime(0.015, now + 4.5);
        } catch (e) {
            console.warn("Failed to generate goal swell", e);
        }
    }

    destroy() {
        if (this.ctx) {
            try {
                if (this.noiseSource) this.noiseSource.disconnect();
                if (this.crowdFilter) this.crowdFilter.disconnect();
                if (this.crowdGain) this.crowdGain.disconnect();
                this.ctx.close();
            } catch (e) {}
            this.ctx = null;
        }
    }
}

export function HighlightsPlayerModal({ highlight, onClose }: HighlightsPlayerModalProps) {
    const [isPlaying, setIsPlaying] = useState<boolean>(true);
    const [currentTimeSec, setCurrentTimeSec] = useState<number>(0);
    const [audioVolume, setAudioVolume] = useState<number>(80);
    const [isMuted, setIsMuted] = useState<boolean>(false);
    const [selectedFeed, setSelectedFeed] = useState<'main' | 'tactical' | 'stadium'>('main');

    const audioEngineRef = React.useRef<StadiumAudioEngine | null>(null);
    const lastTriggeredGoalMin = React.useRef<number | null>(null);

    // Convert duration to seconds
    const totalSeconds = useMemo(() => {
        const parts = highlight.duration.split(':');
        if (parts.length === 2) {
            return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
        }
        return 200;
    }, [highlight.duration]);

    // Lazy initialize AudioContext on mount / first play
    useEffect(() => {
        const engine = new StadiumAudioEngine();
        audioEngineRef.current = engine;

        if (isPlaying) {
            engine.init();
            engine.setVolume(isMuted ? 0 : audioVolume / 100);
            engine.playWhistle();
        }

        return () => {
            engine.destroy();
        };
    }, []);

    // Sync state variables with Audio Engine
    useEffect(() => {
        if (audioEngineRef.current) {
            audioEngineRef.current.setVolume(isMuted ? 0 : audioVolume / 100);
        }
    }, [audioVolume, isMuted]);

    useEffect(() => {
        if (audioEngineRef.current) {
            if (isPlaying) {
                audioEngineRef.current.init();
                audioEngineRef.current.setVolume(isMuted ? 0 : audioVolume / 100);
            } else {
                audioEngineRef.current.stopAmbient();
            }
        }
    }, [isPlaying]);

    // Handle automatic clock
    useEffect(() => {
        if (!isPlaying) return;
        const interval = setInterval(() => {
            setCurrentTimeSec((prev) => {
                if (prev >= totalSeconds) {
                    setIsPlaying(false);
                    return totalSeconds;
                }
                return prev + 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [isPlaying, totalSeconds]);

    // Monitor goals timeline triggers for real audio goal cheers
    useEffect(() => {
        const currentRatioMin = Math.round((currentTimeSec / totalSeconds) * 90);
        const activeGoal = highlight.goals.find(g => Math.abs(currentRatioMin - g.min) <= 1);

        if (activeGoal && lastTriggeredGoalMin.current !== activeGoal.min) {
            lastTriggeredGoalMin.current = activeGoal.min;
            if (audioEngineRef.current && isPlaying) {
                audioEngineRef.current.goalSwell();
            }
        }
    }, [currentTimeSec, totalSeconds, highlight.goals, isPlaying]);

    // Calculate coordinates for animated soccer pitch radar (Feed 1: main)
    const pitchSimulation = useMemo(() => {
        const currentRatio = currentTimeSec / totalSeconds;
        const currentMin = Math.round(currentRatio * 90);

        const leftPlayers = [
            { id: 1, name: highlight.homeTeam.substring(0, 3).toUpperCase() + " 10", base: { x: 100, y: 70 } },
            { id: 2, name: highlight.homeTeam.substring(0, 3).toUpperCase() + " 9", base: { x: 180, y: 120 } },
            { id: 3, name: highlight.homeTeam.substring(0, 3).toUpperCase() + " 7", base: { x: 120, y: 170 } },
        ];

        const rightPlayers = [
            { id: 1, name: highlight.awayTeam.substring(0, 3).toUpperCase() + " 11", base: { x: 300, y: 80 } },
            { id: 2, name: highlight.awayTeam.substring(0, 3).toUpperCase() + " 4", base: { x: 220, y: 130 } },
            { id: 3, name: highlight.awayTeam.substring(0, 3).toUpperCase() + " 5", base: { x: 280, y: 160 } },
        ];

        // Pulse coordinate movements
        const homeNodes = leftPlayers.map(p => {
            const pulseX = Math.sin(currentTimeSec * 0.8 + p.id) * 20;
            const pulseY = Math.cos(currentTimeSec * 0.6 + p.id) * 15;
            return { name: p.name, x: p.base.x + pulseX, y: p.base.y + pulseY };
        });

        const awayNodes = rightPlayers.map(p => {
            const pulseX = Math.cos(currentTimeSec * 0.7 + p.id) * 18;
            const pulseY = Math.sin(currentTimeSec * 0.5 + p.id) * 12;
            return { name: p.name, x: p.base.x + pulseX, y: p.base.y + pulseY };
        });

        // Check if goal scored is dynamically active
        const activeGoal = highlight.goals.find(g => Math.abs(currentMin - g.min) <= 3);
        let ballX = 200;
        let ballY = 120;
        let eventName = "Midfield Possession Play";

        if (activeGoal) {
            const isHomeGoal = activeGoal.team === highlight.homeTeam;
            // Animate kicker blasting toward goal
            const shotFactor = ((currentTimeSec % 3) / 3); // cycles 0 to 1
            const startX = isHomeGoal ? 240 : 160;
            const startY = 120;
            const destX = isHomeGoal ? 390 : 10;
            const destY = 120 + Math.sin(currentTimeSec * 1.5) * 15;

            ballX = startX + (destX - startX) * shotFactor;
            ballY = startY + (destY - startY) * shotFactor;
            eventName = `⚠️ GOAL THREAT! ${activeGoal.player} SHOOTS!`;
        } else {
            // Normal passes between key active nodes
            const cycleIndex = Math.floor(currentTimeSec / 3) % 4;
            const passProgress = (currentTimeSec % 3) / 3;

            const randomPassPoints = [
                { x: homeNodes[1].x, y: homeNodes[1].y },
                { x: awayNodes[1].x, y: awayNodes[1].y },
                { x: homeNodes[0].x, y: homeNodes[0].y },
                { x: awayNodes[2].x, y: awayNodes[2].y },
                { x: homeNodes[1].x, y: homeNodes[1].y }
            ];

            const fromPt = randomPassPoints[cycleIndex];
            const toPt = randomPassPoints[cycleIndex + 1] || randomPassPoints[0];

            ballX = fromPt.x + (toPt.x - fromPt.x) * passProgress;
            ballY = fromPt.y + (toPt.y - fromPt.y) * passProgress;
            eventName = "Tactical Building Phase";
        }

        return { homeNodes, awayNodes, ballX, ballY, eventName };
    }, [currentTimeSec, totalSeconds, highlight]);

    // Format seconds to MM:SS string
    const formatTime = (secs: number) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const handleResetPlay = () => {
        setCurrentTimeSec(0);
        setIsPlaying(true);
        if (audioEngineRef.current) {
            audioEngineRef.current.playWhistle();
        }
    };

    return (
        <div className="fixed inset-0 bg-[#020617]/95 backdrop-blur-md z-50 flex items-center justify-center p-3 sm:p-5 overflow-y-auto">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#0b1220] border border-slate-800 rounded-3xl w-full max-w-4xl overflow-hidden shadow-2xl relative flex flex-col my-auto max-h-[96vh] sm:max-h-[90vh]"
            >
                {/* Border Accent Header */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-650 via-indigo-650 to-emerald-500"></div>

                {/* Modal Header */}
                <div className="p-4 border-b border-slate-800/80 flex items-center justify-between bg-[#111928]/40 pr-3 font-mono">
                    <div className="flex items-center gap-2">
            <span className="bg-red-600/10 border border-red-500/20 text-red-500 text-[9px] font-black tracking-wider px-2 py-0.5 rounded uppercase flex items-center gap-1 animate-pulse">
              <Tv size={10} /> {highlight.narrator}
            </span>
                        <span className="text-emerald-400 text-[10px] hidden md:inline">• REAL-TIME SYNTHESIZED BROADCAST</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="hover:bg-slate-800 p-1.5 rounded-full text-slate-400 hover:text-white transition-all cursor-pointer font-bold w-7 h-7 flex items-center justify-center text-sm"
                    >
                        ✕
                    </button>
                </div>

                {/* Content Body Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 overflow-y-auto scrollbar-none flex-1">
                    {/* Main Media Feed (Left - 7 cols) */}
                    <div className="lg:col-span-7 bg-slate-950 p-4 flex flex-col justify-between items-center gap-3 relative min-h-[300px] sm:min-h-[400px]">
                        {/* Camera feed overlay */}
                        <div className="absolute top-6 left-6 z-10 flex items-center gap-1.5 font-mono text-[9px] font-bold text-white bg-slate-950/80 px-2.5 py-1 rounded border border-white/5 select-none uppercase">
                            <span className={`w-1.5 h-1.5 rounded-full ${isPlaying ? 'bg-red-500 animate-ping' : 'bg-slate-500'} inline-block shrink-0`} />
                            FEED: <span className="text-red-400">{selectedFeed}</span>
                        </div>

                        {/* High Fidelity Screen Interface */}
                        <div className="w-full h-72 sm:h-80 rounded-2xl relative overflow-hidden border border-slate-800 bg-slate-950 flex flex-col justify-between p-4 shadow-inner">

                            {/* FEED 1: Main Soccer Field Simulation */}
                            {selectedFeed === 'main' && (
                                <div className="absolute inset-0 p-3 bg-gradient-to-br from-emerald-950/30 to-slate-950 flex flex-col justify-between select-none">
                                    {/* Soccer Pitch Canvas */}
                                    <div className="w-full h-44 relative bg-emerald-900/15 rounded-xl border border-emerald-500/10 overflow-hidden mt-6">
                                        {/* White line field markings */}
                                        <div className="absolute inset-0 border-r-2 border-dashed border-white/10 w-1/2"></div>
                                        <div className="absolute inset-0 m-auto w-20 h-20 rounded-full border border-white/10"></div>
                                        <div className="absolute left-0 top-12 w-6 h-20 border-r border-t border-b border-white/10"></div>
                                        <div className="absolute right-0 top-12 w-6 h-20 border-l border-t border-b border-white/10"></div>

                                        {/* Home Team nodes */}
                                        {pitchSimulation.homeNodes.map((h, i) => (
                                            <motion.div
                                                key={i}
                                                animate={{ x: h.x, y: h.y }}
                                                transition={{ type: "spring", stiffness: 60 }}
                                                className="absolute -ml-2 -mt-2 flex flex-col items-center"
                                            >
                                                <div className="w-4 h-4 rounded-full bg-emerald-500 border border-white flex items-center justify-center text-[7px] text-white font-black">
                                                    {h.name.split(' ')[1]}
                                                </div>
                                                <span className="text-[6px] text-emerald-300 font-mono scale-90 whitespace-nowrap bg-slate-950/70-rounded px-0.5 mt-0.5">{h.name}</span>
                                            </motion.div>
                                        ))}

                                        {/* Away Team nodes */}
                                        {pitchSimulation.awayNodes.map((a, i) => (
                                            <motion.div
                                                key={i}
                                                animate={{ x: a.x, y: a.y }}
                                                transition={{ type: "spring", stiffness: 60 }}
                                                className="absolute -ml-2 -mt-2 flex flex-col items-center"
                                            >
                                                <div className="w-4 h-4 rounded-full bg-indigo-600 border border-white flex items-center justify-center text-[7px] text-white font-black">
                                                    {a.name.split(' ')[1]}
                                                </div>
                                                <span className="text-[6px] text-indigo-300 font-mono scale-90 whitespace-nowrap bg-slate-950/70 rounded px-0.5 mt-0.5">{a.name}</span>
                                            </motion.div>
                                        ))}

                                        {/* Dynamic Moving Ball */}
                                        <motion.div
                                            animate={{ x: pitchSimulation.ballX, y: pitchSimulation.ballY }}
                                            transition={{ type: "spring", stiffness: 95, damping: 15 }}
                                            className="absolute -ml-1.5 -mt-1.5 w-3 h-3 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg shadow-yellow-500/50"
                                        >
                                            <div className="w-1 h-1 bg-black rounded-full" />
                                        </motion.div>
                                    </div>

                                    {/* Broadcast Tactical Caption */}
                                    <div className="flex justify-between items-center bg-slate-950/80 border border-slate-800 p-2 rounded-xl backdrop-blur-sm mt-2">
                                        <span className="text-[8px] font-mono text-slate-400 uppercase tracking-widest">TRANSLATION STATUS:</span>
                                        <span className="text-[9px] font-mono font-extrabold text-yellow-400 uppercase animate-pulse">{pitchSimulation.eventName}</span>
                                    </div>
                                </div>
                            )}

                            {/* FEED 2: Tactical Diagnostics Possession Heatmap */}
                            {selectedFeed === 'tactical' && (
                                <div className="absolute inset-0 p-4 pt-12 flex flex-col justify-between font-mono text-[10px]">
                                    <div className="grid grid-cols-2 gap-4 flex-1 items-center mt-2">
                                        {/* Simulated Match Stats */}
                                        <div className="space-y-2 bg-[#131d31]/40 border border-slate-800/60 p-3 rounded-xl">
                                            <div className="space-y-1">
                                                <div className="flex justify-between text-[8px] text-slate-400 uppercase">
                                                    <span>{highlight.homeTeam} Possession</span>
                                                    <span className="font-bold text-emerald-400">{52 + Math.round(Math.sin(currentTimeSec * 0.1) * 6)}%</span>
                                                </div>
                                                <div className="h-1.5 bg-slate-900 rounded-full overflow-hidden">
                                                    <div className="bg-emerald-500 h-full transition-all duration-500" style={{ width: `${52 + Math.round(Math.sin(currentTimeSec * 0.1) * 6)}%` }} />
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <div className="flex justify-between text-[8px] text-slate-400 uppercase">
                                                    <span>Attack Pressure Index</span>
                                                    <span className="font-bold text-red-400">{70 + Math.round(Math.cos(currentTimeSec * 0.2) * 15)}</span>
                                                </div>
                                                <div className="h-1.5 bg-slate-900 rounded-full overflow-hidden">
                                                    <div className="bg-red-500 h-full transition-all duration-500" style={{ width: `${70 + Math.round(Math.cos(currentTimeSec * 0.2) * 15)}%` }} />
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <div className="flex justify-between text-[8px] text-slate-400 uppercase">
                                                    <span>Field Coverage (Km)</span>
                                                    <span className="font-bold text-indigo-400">{(88 + (currentTimeSec / totalSeconds) * 20).toFixed(1)} km</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Scouting Heatmap Grid */}
                                        <div className="bg-slate-900/60 border border-emerald-500/10 rounded-xl h-full p-2 relative flex flex-col justify-center items-center overflow-hidden">
                                            <span className="text-[7px] text-slate-500 absolute top-1.5 left-2 uppercase">Heatmap Activity</span>
                                            <div className="grid grid-cols-4 grid-rows-3 gap-1 w-full h-24 mt-2">
                                                {Array.from({ length: 12 }).map((_, idx) => {
                                                    const heatLevel = Math.abs(Math.sin(currentTimeSec * 0.15 + idx));
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="rounded transition-colors duration-1000"
                                                            style={{
                                                                backgroundColor: heatLevel > 0.7
                                                                    ? 'rgba(239, 68, 68, 0.45)'
                                                                    : heatLevel > 0.4
                                                                        ? 'rgba(245, 158, 11, 0.25)'
                                                                        : 'rgba(16, 185, 129, 0.08)'
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between text-[8px] bg-slate-900/80 p-2 rounded-lg border border-slate-800">
                                        <span className="text-indigo-400 font-bold uppercase">🚨 TACTICAL COMPILER RUNNING</span>
                                        <span className="text-slate-500">REFRESH_RATE: 2.4 HZ</span>
                                    </div>
                                </div>
                            )}

                            {/* FEED 3: Stadium Equaliser Atmosphere Fan Wave */}
                            {selectedFeed === 'stadium' && (
                                <div className="absolute inset-0 p-4 pt-12 flex flex-col justify-center items-center gap-4">
                                    <div className="text-center space-y-1 mt-3">
                                        <span className="text-3xl animate-bounce inline-block">🏟️</span>
                                        <h6 className="text-[10px] font-mono font-bold text-white uppercase tracking-wider">Crowd Noise Intensity Controller</h6>
                                        <p className="text-[8px] font-mono text-slate-500 max-w-xs mx-auto">
                                            Real-time synthesized Web Audio atmosphere mimicking 85,000 shouting fans in host arena.
                                        </p>
                                    </div>

                                    {/* Audio Volume Bar Visualization */}
                                    <div className="flex items-end justify-center gap-1.5 h-16 w-4/5 select-none font-mono">
                                        {Array.from({ length: 24 }).map((_, i) => {
                                            const throbFactor = isPlaying ? (0.2 + (Math.sin(currentTimeSec * 1.2 + i) * 0.8)) : 0.05;
                                            const activeHeight = throbFactor * 48;
                                            return (
                                                <div
                                                    key={i}
                                                    className="w-2.5 bg-gradient-to-t from-red-650 via-amber-500 to-yellow-400 rounded-sm transition-all duration-300"
                                                    style={{ height: `${Math.max(4, activeHeight)}px` }}
                                                />
                                            );
                                        })}
                                    </div>

                                    <span className="text-[8px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-2.5 py-0.5 tracking-tight uppercase animate-pulse">
                    Ambient Gain Status: Connected
                  </span>
                                </div>
                            )}

                            {/* Score header Overlay */}
                            <div className="flex items-center justify-between font-mono bg-slate-950/80 border border-slate-800 p-2 rounded-xl backdrop-blur-sm self-center max-w-sm select-none z-10">
                                <div className="flex items-center gap-2 text-[10px] text-white">
                                    <span className="font-extrabold uppercase truncate max-w-[80px]">{highlight.homeTeam}</span>
                                    <TeamFlag name={highlight.homeTeam} className="w-5 h-3.5 shrink-0" />
                                    <span className="bg-slate-900 border border-slate-800 px-2 py-0.5 rounded text-red-100 font-bold tracking-widest text-[9px]">{highlight.score}</span>
                                    <TeamFlag name={highlight.awayTeam} className="w-5 h-3.5 shrink-0" />
                                    <span className="font-extrabold uppercase truncate max-w-[80px]">{highlight.awayTeam}</span>
                                </div>
                            </div>

                            {/* Watermark / Play Sign overlay */}
                            {!isPlaying && currentTimeSec >= totalSeconds && (
                                <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm flex flex-col justify-center items-center text-center p-4 z-20">
                                    <span className="text-3xl">🏁</span>
                                    <h6 className="font-mono text-xs text-white font-bold uppercase mt-1">Simulated Broadcast Concluded</h6>
                                    <button
                                        onClick={handleResetPlay}
                                        className="mt-3 px-3.5 py-1.5 bg-indigo-650 hover:bg-indigo-600 text-white text-[10px] font-mono font-bold uppercase tracking-wide rounded-lg transition-all cursor-pointer border border-indigo-500/25"
                                    >
                                        Replay Broadcast
                                    </button>
                                </div>
                            )}

                            {/* Goal Celebrations Ticker Overlay */}
                            {isPlaying && (() => {
                                const currentRatioMin = Math.round((currentTimeSec / totalSeconds) * 90);
                                const activeGoal = highlight.goals.find(g => Math.abs(currentRatioMin - g.min) <= 1);
                                if (activeGoal) {
                                    return (
                                        <motion.div
                                            initial={{ y: 20, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            exit={{ y: 20, opacity: 0 }}
                                            className="bg-yellow-500 text-slate-950 p-2.5 rounded-xl text-center font-mono font-black text-[9px] uppercase tracking-wide absolute bottom-14 inset-x-4 max-w-xs mx-auto shadow-xl z-20 animate-bounce select-none border border-black/10"
                                        >
                                            ⚡ GOOALLL! {activeGoal.player} ({activeGoal.min}') scores for {activeGoal.team}!
                                        </motion.div>
                                    );
                                }
                                return null;
                            })()}

                            {/* Multifeed Selection controls */}
                            <div className="flex justify-center gap-1.5 font-mono text-[8.5px] tracking-tight font-black select-none z-10">
                                {(['main', 'tactical', 'stadium'] as const).map((feed) => (
                                    <button
                                        key={feed}
                                        onClick={() => setSelectedFeed(feed)}
                                        className={`px-3 py-1 rounded-lg transition-all uppercase cursor-pointer border ${
                                            selectedFeed === feed
                                                ? 'bg-red-650 text-white border-red-500 shadow-md shadow-red-500/10'
                                                : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white hover:border-slate-700'
                                        }`}
                                    >
                                        Feed: {feed}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Custom Interactive Player Controls Dashboard */}
                        <div className="w-full bg-[#111928] p-3 rounded-2xl border border-slate-800 flex flex-col gap-2.5 font-mono">
                            {/* Scrub timeline bar */}
                            <div className="flex items-center gap-3 text-[10px] text-slate-400">
                                <span className="tabular-nums font-bold">{formatTime(currentTimeSec)}</span>
                                <div
                                    className="flex-1 h-1.5 bg-slate-950 border border-slate-850 rounded-full relative cursor-pointer group"
                                    onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                                        setCurrentTimeSec(Math.round(percent * totalSeconds));
                                    }}
                                >
                                    <div
                                        className="bg-gradient-to-r from-red-600 to-amber-500 h-full rounded-full transition-all relative"
                                        style={{ width: `${(currentTimeSec / totalSeconds) * 100}%` }}
                                    >
                                        <span className="absolute -right-1.5 -top-1 w-3.5 h-3.5 bg-white rounded-full shadow border-2 border-red-600 scale-0 group-hover:scale-100 transition-transform" />
                                    </div>
                                </div>
                                <span className="tabular-nums font-bold">{highlight.duration}</span>
                            </div>

                            {/* Controls bar */}
                            <div className="flex justify-between items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setIsPlaying(!isPlaying)}
                                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                                            isPlaying ? 'bg-red-650 text-white hover:bg-red-600' : 'bg-emerald-600 text-white hover:bg-emerald-550'
                                        }`}
                                        title={isPlaying ? "Pause Simulated Broadcast" : "Start Broadcast & Audio Simulation"}
                                    >
                                        {isPlaying ? <span className="font-bold text-xs select-none">⏸</span> : <Play size={11} fill="currentColor" className="ml-0.5" />}
                                    </button>
                                    <button
                                        onClick={handleResetPlay}
                                        className="hover:bg-slate-800 border border-slate-800 text-slate-300 hover:text-white rounded-lg px-2.5 py-1.5 text-[9px] uppercase font-bold cursor-pointer"
                                    >
                                        Restart
                                    </button>
                                </div>

                                {/* Simulated Volume bar */}
                                <div className="flex items-center gap-2 text-slate-400 text-[10px] shrink-0 pb-0.5">
                                    <button
                                        onClick={() => setIsMuted(!isMuted)}
                                        className="hover:text-white transition-colors cursor-pointer font-bold shrink-0 text-xs text-center w-5"
                                    >
                                        {isMuted || audioVolume === 0 ? '🔇' : '🔊'}
                                    </button>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={isMuted ? 0 : audioVolume}
                                        onChange={(e) => {
                                            setAudioVolume(parseInt(e.target.value, 10));
                                            setIsMuted(false);
                                        }}
                                        className="w-16 h-1 bg-slate-950 rounded-lg appearance-none cursor-pointer accent-red-500"
                                    />
                                    <span className="text-[8px] font-mono text-slate-500">STADIUM COUPLER</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timeline and Commentary stats (Right - 5 cols) */}
                    <div className="lg:col-span-5 p-4 sm:p-5 flex flex-col justify-between gap-5 bg-[#0e1726]/40 border-t lg:border-t-0 lg:border-l border-slate-800">
                        <div className="space-y-4">
                            {/* Match Header */}
                            <div>
                <span className="text-[9px] font-mono font-black text-indigo-400 uppercase tracking-widest block font-bold">
                  Broadcast Diagnostics
                </span>
                                <h4 className="text-sm font-black text-white uppercase tracking-tight mt-0.5 leading-tight">
                                    {highlight.title}
                                </h4>
                                <p className="text-[10px] text-slate-450 font-mono mt-1">
                                    🏟 Stadium: <strong className="text-slate-200 uppercase">{highlight.stadium}</strong>
                                </p>
                            </div>

                            {/* Goals Timeline */}
                            <div className="space-y-2.5">
                <span className="text-[9px] font-mono font-bold text-slate-500 uppercase tracking-wider block">
                  Match Timeline Events
                </span>
                                <div className="space-y-2 font-mono scrollbar-none max-h-36 overflow-y-auto">
                                    {highlight.goals.map((g, idx) => {
                                        const eventTimeRatio = g.min / 90;
                                        const isPassed = (currentTimeSec / totalSeconds) >= eventTimeRatio;

                                        return (
                                            <div
                                                key={idx}
                                                className={`flex items-center justify-between border rounded-xl p-2.5 transition-all text-[10px] font-mono lg:text-[11px] ${
                                                    isPassed
                                                        ? 'bg-emerald-500/10 border-emerald-500/20 text-slate-200'
                                                        : 'bg-slate-950/20 border-slate-800 text-slate-500'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2 truncate">
                                                    <span className={`text-[12px] shrink-0 ${isPassed ? 'opacity-100 scale-100' : 'opacity-40 scale-90'} transition-all`}>⚽</span>
                                                    <span className="font-extrabold truncate">{g.player}</span>
                                                    <span className="text-[8px] bg-indigo-500/10 text-indigo-300 font-black px-1.5 py-0.5 rounded tracking-wide uppercase font-mono shrink-0">{g.team}</span>
                                                </div>
                                                <span className="font-bold shrink-0">{g.min}' Minute</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Commentary Feed Box */}
                            <div className="bg-[#090d16] p-3.5 rounded-2xl border border-slate-800/80 font-mono text-[10px] text-slate-355 space-y-1.5 leading-normal">
                                <span className="text-[8px] font-bold text-slate-550 uppercase tracking-wider block">LIVE COMMENTARY SIMULATOR</span>
                                <div className="h-20 overflow-y-auto pr-1">
                                    {currentTimeSec === 0 ? (
                                        <p className="text-slate-400 italic font-mono text-[9px]">Pre-match analysis is underway. Click play to start commentators live broadcast translation stream.</p>
                                    ) : (() => {
                                        if (currentTimeSec / totalSeconds < 0.2) {
                                            return <p className="text-emerald-450 animate-pulse font-mono text-[9px]">"[Commentary] A fantastic start from both managers! High high-press. Absolute stadium chaos!"</p>;
                                        } else if (currentTimeSec / totalSeconds < 0.5) {
                                            return <p className="text-slate-300 font-mono text-[9px]">"[Commentary] Midway through this intense block. Possession stats are incredibly close. Defenders are stepping up big!"</p>;
                                        } else if (currentTimeSec / totalSeconds < 0.8) {
                                            return <p className="text-slate-350 font-bold font-mono text-[9px]">"[Commentary] We are in the final chapters of this matchup. Brilliant soccer, incredible stadium roar. Total dedication!"</p>;
                                        } else {
                                            return <p className="text-red-400 font-black font-mono text-[9px]">"[Commentary] Final whistle blows! An epic battle of historical magnitude is secured. Outstanding sportsmanship."</p>;
                                        }
                                    })()}
                                </div>
                            </div>
                        </div>

                        {/* Platform launch actions */}
                        <div className="space-y-2 select-none">
                            <span className="text-[8px] font-mono font-bold text-slate-500 uppercase tracking-wider block">LAUNCH STREAM ON TARGET PLATFORM</span>
                            <div className="flex flex-col gap-2 font-mono">
                                <a
                                    href={highlight.videoUrl}
                                    target="_blank"
                                    referrerPolicy="no-referrer"
                                    rel="noopener noreferrer"
                                    className="w-full text-center py-2 bg-gradient-to-r from-red-650 to-orange-650 hover:from-red-600 hover:to-orange-600 text-white text-[10px] font-black rounded-lg cursor-pointer flex items-center justify-center gap-1.5 uppercase transition-all shadow-md shadow-red-550/10 border border-red-500/20"
                                >
                                    <ExternalLink size={12} /> Launch DailyGoal.tv Highlights Page
                                </a>
                                <a
                                    href="https://dailygoal.tv/world-cup-2026-highlights"
                                    target="_blank"
                                    referrerPolicy="no-referrer"
                                    rel="noopener noreferrer"
                                    className="w-full text-center py-2 bg-[#131d31] hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white text-[10px] font-bold rounded-lg cursor-pointer flex items-center justify-center gap-1.5 uppercase transition-all"
                                >
                                    ⚽ Broadcast Hub on DailyGoal.tv
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
